import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
// React 19 native metadata - no HelmetProvider needed
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './App.css';
import PageSEO from './components/common/PageSEO';
import RouteHead from './components/common/RouteHead';
import HreflangTags from './components/common/HreflangTags';
import { WorkflowProvider, useWorkflow } from './contexts/WorkflowContext';
import { IntelligenceProvider, useIntelligence } from './contexts/IntelligenceContext';
import { RiverButton, RiverBanner } from './components/ui/RiverFlow';
import AppSidebar from './components/layout/AppSidebar';

// Import Agent Components
import {
  AgentMarketplace,
  AgentConfigWizard,
  AgentDashboard,
  AgentOutputViewer,
  AgentRunner
} from './components/agents';

// Import Athena Components (Competitive Intelligence)
import { AthenaWarRoom, SmartDiscovery } from './components/athena';

// Import Pipeline Components
import { PipelineBuilder } from './components/pipelines';

// Import Hermes Components (Content Forge)
import HermesContentForge from './components/hermes/HermesContentForge';
import UnifiedContentForge from './components/hermes/UnifiedContentForge';

// Import Digest (Weekly Intelligence Digest settings)
import DigestSettings from './components/digest/DigestSettings';
import DigestRouter from './components/digest/DigestRouter';

// Module-level deep-link interception — must run BEFORE any component mounts so
// that ContentGenerator / CompetitorAnalyzer can read sessionStorage on first render.
// Triggered by the digest email links: /?river=athena&brief=…&keyword=… and /?river=trending&topic=…
(() => {
  try {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const river = params.get('river');
    if (river === 'athena') {
      sessionStorage.setItem('isupplyai_pending_brief', JSON.stringify({
        topic: params.get('brief') || '',
        keyword: params.get('keyword') || '',
        ts: Date.now(),
      }));
    } else if (river === 'trending') {
      sessionStorage.setItem('isupplyai_pending_forge_topic', JSON.stringify({
        topic: params.get('topic') || '',
        mode: 'deep',
        ts: Date.now(),
      }));
    }
  } catch { /* noop — sessionStorage may be disabled */ }
})();

// Import The Pantheon (Divine Command Center)
import AllianceWarRoom from './components/alliance/AllianceWarRoom';

// Import Onboarding (Awakening Ritual — 4-step wizard)
import Onboarding from './components/AwakeningRitual';

// Import Public Pages
import LivingRoomReplayPage from './components/alliance/LivingRoomReplayPage';
import AutoDebateReplayPage from './components/alliance/AutoDebateReplayPage';
import FreeTasteWarRoom from './components/FreeTasteWarRoom';
import DemoBanner from './components/DemoBanner';
import DemoAnalyticsDashboard from './components/DemoAnalyticsDashboard';

// Import Dionysus (Reddit Growth Engine)
import DionysusPanel from './components/dionysus/DionysusPanel';

// Import Artemis (Lead Hunter)
import { ArtemisHunter, ArtemisWidget } from './components/artemis/ArtemisHunter';

// Import Roast Share Page (Viral Sharing)
import RoastSharePage from './components/roast/RoastSharePage';
import RoastPage from './components/roast/RoastPage';

// Import War Room Share Page
import WarRoomSharePage from './components/alliance/warroom/WarRoomSharePage';

// Import Apollo Components (Distribution God)
import ApolloSunChamber from './components/apollo/ApolloSunChamber';

// Import Locked Feature Preview Component
import LockedFeaturePreview, {
  AthenaPreviewContent,
  HermesPreviewContent,
  ApolloPreviewContent,
  PantheonPreviewContent,
  DionysusPreviewContent
} from './components/common/LockedFeaturePreview';

// Import Upgrade Email Modal (for locked feature upgrade flow)
import UpgradeEmailModal from './components/common/UpgradeEmailModal';

// Import Email Capture Modal (for landing page get started flow)
import EmailCaptureModal from './components/common/EmailCaptureModal';

// Import Privacy Policy
import PrivacyPolicy from './components/PrivacyPolicy';

// Import Blog
import BlogPage from './components/blog/BlogPage';
import BlogPost from './components/blog/BlogPost';
import BlogAdmin from './components/blog/BlogAdmin';
import ComparisonPage from './components/blog/ComparisonPage';
import ComparisonIndex from './components/blog/ComparisonIndex';
import GlossaryPage from './components/blog/GlossaryPage';
import GlossaryTermPage from './components/blog/GlossaryTermPage';
import TopicHub from './components/blog/TopicHub';
import DocumentsPage from './components/DocumentsPage';
import { ZapierIntegration } from './components/ZapierIntegration';
import BattlePage from './components/BattlePage';

// Import Strategy Score
import StrategyScorePage from './components/StrategyScorePage';
import ScoreReportPage from './components/StrategyScoreReport';

// Import new feature & replay pages
import WarRoomReplaysPage from './components/WarRoomReplaysPage';
import FeaturePageWarRoom from './components/FeaturePageWarRoom';
import FeaturePageStrategyScore from './components/FeaturePageStrategyScore';
import ResourcesPage from './components/ResourcesPage';

// Import Premium Landing Page
import PremiumLandingPage from './components/landing/PremiumLandingPage';

// Backend URL from environment - always use the configured URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Debug logging for backend URL (only in development)
if (process.env.NODE_ENV === 'development') {
  console.log('Backend URL:', { API });
}

// Helper function for clipboard copy with fallback
const copyToClipboard = async (text) => {
  try {
    // Try modern clipboard API first
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    // Fallback for when clipboard API is blocked
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err2) {
      console.error('Failed to copy:', err2);
      return false;
    }
  }
};

// Set up axios interceptor for auth token
const setupAxiosInterceptors = () => {
  axios.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
};

// Auth Context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(() => sessionStorage.getItem('demo_mode') === 'true');

  useEffect(() => {
    setupAxiosInterceptors();
    // Persist referral code from URL to sessionStorage on initial load
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      sessionStorage.setItem('referral_code', refCode);
    }
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/user/profile`);
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('token', response.data.access_token);
      await fetchUserProfile();
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (name, email, password) => {
    try {
      // Capture referral code from URL params or sessionStorage
      const urlParams = new URLSearchParams(window.location.search);
      const referral_code = urlParams.get('ref') || sessionStorage.getItem('referral_code') || null;
      
      const payload = { name, email, password };
      if (referral_code) payload.referral_code = referral_code;
      
      const response = await axios.post(`${API}/auth/register`, payload);
      localStorage.setItem('token', response.data.access_token);
      // Clear stored referral code after successful registration
      sessionStorage.removeItem('referral_code');
      await fetchUserProfile();
      
      // Track conversion for analytics
      if (window.gtag) {
        window.gtag('event', 'sign_up', {
          method: 'email',
          event_category: 'engagement'
        });
      }

      // Digest signup-attribution: if user landed via a public digest in the last 7 days,
      // fire a 'signup' event back to that digest so creators see who they drove.
      try {
        const raw = localStorage.getItem('isupplyai_digest_attribution');
        if (raw) {
          const attr = JSON.parse(raw);
          const ageMs = Date.now() - (attr.ts || 0);
          if (attr.slug && ageMs < 7 * 24 * 60 * 60 * 1000) {
            const trackUrl = `${API}/public/digest/${attr.slug}/track`;
            const body = JSON.stringify({ event_type: 'signup' });
            if (navigator.sendBeacon) {
              navigator.sendBeacon(trackUrl, new Blob([body], { type: 'application/json' }));
            } else {
              fetch(trackUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
            }
          }
          localStorage.removeItem('isupplyai_digest_attribution');
        }
      } catch { /* noop */ }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const demoLogin = async () => {
    try {
      const response = await axios.post(`${API}/auth/demo-login`);
      localStorage.setItem('token', response.data.access_token);
      sessionStorage.setItem('demo_mode', 'true');
      sessionStorage.setItem('demo_start', Date.now().toString());
      sessionStorage.removeItem('demo_explored');
      setIsDemoMode(true);
      await fetchUserProfile();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Demo login failed' };
    }
  };

  const exitDemo = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('demo_mode');
    sessionStorage.removeItem('demo_start');
    sessionStorage.removeItem('demo_explored');
    setUser(null);
    setIsAuthenticated(false);
    setIsDemoMode(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('demo_mode');
    sessionStorage.removeItem('demo_start');
    sessionStorage.removeItem('demo_explored');
    setUser(null);
    setIsAuthenticated(false);
    setIsDemoMode(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, register, logout, fetchUserProfile, isDemoMode, demoLogin, exitDemo }}>
      {children}
    </AuthContext.Provider>
  );
};

// ROI Calculator Component
const ROICalculator = () => {
  const [articlesPerMonth, setArticlesPerMonth] = useState(10);
  const [hoursPerArticle, setHoursPerArticle] = useState(3);
  const [hourlyRate, setHourlyRate] = useState(50);

  const aiTime = articlesPerMonth * 0.17; // 10 minutes = 0.17 hours
  const traditionalTime = articlesPerMonth * hoursPerArticle;
  const timeSaved = traditionalTime - aiTime;
  const moneySaved = timeSaved * hourlyRate;

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">💰 Calculate Your ROI</h2>
          <p className="text-xl text-gray-600">See how much time and money you'll save</p>
        </div>

        <div className="max-w-4xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 shadow-xl">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Articles per month
                </label>
                <input
                  type="range"
                  min="1"
                  max="50"
                  value={articlesPerMonth}
                  onChange={(e) => setArticlesPerMonth(e.target.value)}
                  className="w-full"
                />
                <div className="text-center text-2xl font-bold text-purple-600">{articlesPerMonth}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hours per article (traditional)
                </label>
                <input
                  type="range"
                  min="1"
                  max="8"
                  value={hoursPerArticle}
                  onChange={(e) => setHoursPerArticle(e.target.value)}
                  className="w-full"
                />
                <div className="text-center text-2xl font-bold text-purple-600">{hoursPerArticle} hrs</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your hourly rate ($)
                </label>
                <input
                  type="range"
                  min="25"
                  max="200"
                  step="25"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  className="w-full"
                />
                <div className="text-center text-2xl font-bold text-purple-600">${hourlyRate}/hr</div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-bold mb-6 text-center">Your Savings</h3>
              
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-sm text-green-600 mb-1">Time Saved per Month</div>
                  <div className="text-3xl font-bold text-green-700">{timeSaved.toFixed(0)} hours</div>
                </div>

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-sm text-blue-600 mb-1">Money Saved per Month</div>
                  <div className="text-3xl font-bold text-blue-700">${moneySaved.toLocaleString()}</div>
                </div>

                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-sm text-purple-600 mb-1">Annual Savings</div>
                  <div className="text-3xl font-bold text-purple-700">${(moneySaved * 12).toLocaleString()}</div>
                </div>

                <div className="text-center text-xs text-gray-500 mt-4">
                  With ContentGen Pro at $50/mo, your net annual savings: 
                  <div className="text-lg font-bold text-green-600 mt-1">
                    ${((moneySaved * 12) - (50 * 12)).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Content Examples Showcase
const ContentExamples = () => {
  const [activeExample, setActiveExample] = useState(0);

  const examples = [
    {
      type: "Blog Post",
      title: "The Future of Remote Work in 2025",
      preview: "Remote work has transformed from a temporary solution to a permanent fixture in the modern workplace. Companies worldwide are embracing hybrid models, and the statistics are staggering: 74% of professionals expect remote work to become standard...",
      stats: "850 words • Generated in 12 seconds • Professional tone"
    },
    {
      type: "Social Media",
      title: "Product Launch Announcement",
      preview: "🚀 BIG NEWS! We're thrilled to announce our latest innovation that's about to change everything. After months of hard work, we're ready to share something truly special with you. Drop a 🎉 if you're excited! #ProductLaunch #Innovation",
      stats: "280 characters • Generated in 5 seconds • Engaging tone"
    },
    {
      type: "Email Campaign",
      title: "Flash Sale - Don't Miss Out!",
      preview: "Hi [Name], We noticed you've been eyeing our premium collection. Here's something special just for you: 48-hour exclusive access to our biggest sale of the year. Save 40% on everything. Your cart is waiting...",
      stats: "450 words • Generated in 8 seconds • Persuasive tone"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">✨ See the Quality</h2>
          <p className="text-xl text-gray-600">Real content generated by our AI in seconds</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center gap-4 mb-8">
            {examples.map((example, index) => (
              <button
                key={index}
                onClick={() => setActiveExample(index)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeExample === index
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {example.type}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="mb-4">
              <span className="inline-block px-4 py-1 bg-purple-100 text-purple-600 rounded-full text-sm font-medium mb-2">
                {examples[activeExample].type}
              </span>
              <h3 className="text-2xl font-bold mb-4">{examples[activeExample].title}</h3>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-4 min-h-[150px]">
              <p className="text-gray-700 leading-relaxed">{examples[activeExample].preview}</p>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
              <span>{examples[activeExample].stats}</span>
              <span className="text-green-600 font-medium">✓ High Quality</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Comparison Table Component
const ComparisonTable = () => {
  const features = [
    { name: 'Content Multiplier', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'SEO Optimization', iSupplyAI: true, jasper: true, copyAI: true, writesonic: true, chatGPT: false },
    { name: 'Website Analyzer (Brand Voice Analysis)', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Context-Aware Content Multiplier', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'ROI Calculator', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Content Decay Alerter', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Sentiment Predictor', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Quora Goldmine (with Chrome Extension)', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Ad Performance Dashboard', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Auto-Pilot Engine', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Advanced Analytics', iSupplyAI: true, jasper: true, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Market Intelligence', iSupplyAI: true, jasper: true, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Multi-Platform Content', iSupplyAI: true, jasper: true, copyAI: true, writesonic: true, chatGPT: false },
    { name: 'Custom API Access', iSupplyAI: true, jasper: false, copyAI: false, writesonic: false, chatGPT: false },
    { name: 'Bulk Generation', iSupplyAI: true, jasper: true, copyAI: true, writesonic: true, chatGPT: false },
  ];

  const pricing = [
    { platform: 'iSupplyAI', price: '$50', tier: 'Pro (Founders)' },
    { platform: 'Jasper AI', price: '$49', tier: 'Creator' },
    { platform: 'Copy.ai', price: '$49', tier: 'Pro' },
    { platform: 'Writesonic', price: '$19', tier: 'Pro' },
    { platform: 'ChatGPT', price: '$20', tier: 'Plus' },
  ];

  const checkIcon = (
    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );

  const crossIcon = (
    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            iSupplyAI vs Competition
          </h2>
          <p className="text-xl text-gray-600">See why iSupplyAI is the most comprehensive AI content platform</p>
        </div>

        {/* Feature Comparison Table */}
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-purple-600">
                <tr>
                  <th className="px-4 md:px-6 py-4 text-left text-white font-bold text-sm md:text-lg">Feature</th>
                  <th className="px-4 md:px-6 py-4 text-center text-white font-bold text-sm md:text-lg">iSupplyAI</th>
                  <th className="px-4 md:px-6 py-4 text-center text-white font-bold text-sm md:text-lg">Jasper</th>
                  <th className="px-4 md:px-6 py-4 text-center text-white font-bold text-sm md:text-lg">Copy.ai</th>
                  <th className="px-4 md:px-6 py-4 text-center text-white font-bold text-sm md:text-lg">Writesonic</th>
                  <th className="px-4 md:px-6 py-4 text-center text-white font-bold text-sm md:text-lg">ChatGPT</th>
                </tr>
              </thead>
              <tbody>
                {features.map((feature, index) => (
                  <tr key={index} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50 transition-colors`}>
                    <td className="px-4 md:px-6 py-4 font-semibold text-gray-800 text-sm md:text-base">{feature.name}</td>
                    <td className="px-4 md:px-6 py-4 text-center">
                      <span className={`inline-block w-8 h-8 ${feature.iSupplyAI ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center`}>
                        {feature.iSupplyAI ? checkIcon : crossIcon}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-center">
                      <span className={`inline-block w-8 h-8 ${feature.jasper ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center`}>
                        {feature.jasper ? checkIcon : crossIcon}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-center">
                      <span className={`inline-block w-8 h-8 ${feature.copyAI ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center`}>
                        {feature.copyAI ? checkIcon : crossIcon}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-center">
                      <span className={`inline-block w-8 h-8 ${feature.writesonic ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center`}>
                        {feature.writesonic ? checkIcon : crossIcon}
                      </span>
                    </td>
                    <td className="px-4 md:px-6 py-4 text-center">
                      <span className={`inline-block w-8 h-8 ${feature.chatGPT ? 'bg-green-500' : 'bg-red-500'} rounded-full flex items-center justify-center`}>
                        {feature.chatGPT ? checkIcon : crossIcon}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pricing Comparison */}
        <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-2xl p-6 md:p-8">
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Pricing Comparison</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {pricing.map((item, index) => (
              <div 
                key={index}
                className={`text-center p-4 md:p-6 rounded-xl ${
                  item.platform === 'iSupplyAI' 
                    ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white transform md:scale-105 shadow-xl col-span-2 md:col-span-1' 
                    : 'bg-gray-100'
                }`}
              >
                <h4 className={`text-lg md:text-xl font-bold mb-2 ${item.platform === 'iSupplyAI' ? 'text-white' : 'text-gray-800'}`}>
                  {item.platform}
                </h4>
                <p className={`text-xs md:text-sm mb-3 ${item.platform === 'iSupplyAI' ? 'text-blue-100' : 'text-gray-600'}`}>
                  {item.tier}
                </p>
                <p className={`text-3xl md:text-4xl font-bold ${item.platform === 'iSupplyAI' ? 'text-white' : 'text-gray-900'}`}>
                  {item.price}
                </p>
                <p className={`text-xs md:text-sm mt-1 ${item.platform === 'iSupplyAI' ? 'text-blue-100' : 'text-gray-600'}`}>
                  /month
                </p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              More Features. Better Value. iSupplyAI Wins.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Trust Badges Component
const TrustBadges = () => {
  return (
    <section className="py-12 bg-gradient-to-r from-purple-600 to-blue-600">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap justify-center items-center gap-8 text-white">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔒</span>
            <div>
              <div className="font-bold">256-bit Encryption</div>
              <div className="text-sm text-purple-200">Bank-level security</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚡</span>
            <div>
              <div className="font-bold">99.9% Uptime</div>
              <div className="text-sm text-purple-200">Always available</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">💳</span>
            <div>
              <div className="font-bold">Secure Payments</div>
              <div className="text-sm text-purple-200">Powered by Stripe</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">✅</span>
            <div>
              <div className="font-bold">GDPR Compliant</div>
              <div className="text-sm text-purple-200">Your data protected</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎯</span>
            <div>
              <div className="font-bold">30-Day Guarantee</div>
              <div className="text-sm text-purple-200">100% money back</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Customer Testimonials Component
const Testimonials = () => {
  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Marketing Director",
      company: "TechStart Inc.",
      image: "👩‍💼",
      rating: 5,
      text: "ContentGen Pro has revolutionized our content strategy. We've increased output by 300% while cutting costs by 60%. The AI-generated content is indistinguishable from human writing!"
    },
    {
      name: "Michael Chen",
      role: "Founder & CEO",
      company: "GrowthHacker Co.",
      image: "👨‍💻",
      rating: 5,
      text: "As a startup, we couldn't afford a full content team. ContentGen Pro gave us enterprise-level content production at a fraction of the cost. ROI in the first month!"
    },
    {
      name: "Emily Rodriguez",
      role: "Content Manager",
      company: "Digital Ventures",
      image: "👩‍🎨",
      rating: 5,
      text: "The bulk generation feature is a game-changer! I can create a month's worth of social media content in under 10 minutes. My productivity has skyrocketed."
    },
    {
      name: "David Park",
      role: "E-commerce Owner",
      company: "ShopSmart",
      image: "🧑‍💼",
      rating: 5,
      text: "Writing product descriptions used to take days. Now it takes minutes. The quality is consistently excellent, and my conversion rates have improved by 25%."
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">💬 What Our Customers Say</h2>
          <p className="text-xl text-gray-600">Join 10,000+ satisfied content creators</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-5xl">{testimonial.image}</div>
                <div>
                  <h4 className="font-bold text-lg">{testimonial.name}</h4>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                  <p className="text-sm text-purple-600">{testimonial.company}</p>
                </div>
              </div>
              <div className="flex gap-1 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <span key={i} className="text-yellow-400">⭐</span>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed italic">"{testimonial.text}"</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Live Stats Component
const LiveStats = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  if (!stats) return null;

  return (
    <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white py-3">
      <div className="container mx-auto px-4 text-center">
        <div className="flex justify-center items-center space-x-8 text-sm">
          <div className="flex items-center">
            <span className="w-2 h-2 bg-green-300 rounded-full mr-2 animate-pulse"></span>
            <span className="font-semibold">{stats.total_users.toLocaleString()}+ Users</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-yellow-300 rounded-full mr-2 animate-pulse"></span>
            <span className="font-semibold">{stats.total_words_generated.toLocaleString()}+ Words Generated</span>
          </div>
          <div className="flex items-center">
            <span className="w-2 h-2 bg-purple-300 rounded-full mr-2 animate-pulse"></span>
            <span className="font-semibold">{stats.total_content_generated.toLocaleString()}+ Content Pieces</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ isOpen, onClose, planType, planDetails, user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  const displayPrice = billingPeriod === 'annual' 
    ? Math.round((planDetails?.price || 0) * 12 * 0.8 / 12)
    : planDetails?.price;
  const annualTotal = Math.round((planDetails?.price || 0) * 12 * 0.8);

  const handlePayment = async () => {
    if (!planType || !planDetails) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/payment/create-checkout-session`, {
        plan_type: planType,
        billing_period: billingPeriod,
        success_url: `${window.location.origin}/dashboard?payment=success&plan=${planType}`,
        cancel_url: `${window.location.origin}/dashboard?payment=cancelled`
      });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Payment error:', error);
      setError(error.response?.data?.detail || 'Payment processing failed. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-2">
            Upgrade to {planType?.charAt(0).toUpperCase() + planType?.slice(1)} Plan
          </h3>
          <p className="text-gray-600">Complete your purchase with Stripe</p>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex items-center justify-center gap-3 mb-6 bg-gray-100 rounded-xl p-1.5">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${billingPeriod === 'monthly' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('annual')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${billingPeriod === 'annual' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Annual (Save 20%)
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 p-6 rounded-lg mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">
              ${displayPrice}<span className="text-sm text-gray-600">/month</span>
            </div>
            {billingPeriod === 'annual' && (
              <p className="text-sm text-green-600 font-medium mb-1">
                Billed ${annualTotal}/year (save ${Math.round((planDetails?.price || 0) * 12 - annualTotal)}/year)
              </p>
            )}
            <p className="text-gray-700 mb-4">{planDetails?.credits_per_month} credits per month</p>
            
            <div className="space-y-2 text-left max-h-96 overflow-y-auto pr-2">
              <p className="text-sm font-bold text-purple-700 mb-3">What's Included:</p>
              {planDetails?.features?.map((feature, index) => (
                <div key={index} className="flex items-start text-sm">
                  <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">&#10003;</span>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <button
            onClick={handlePayment}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition duration-300 disabled:opacity-50"
            data-testid="payment-submit-btn"
          >
            {loading ? 'Processing...' : `Pay $${billingPeriod === 'annual' ? annualTotal + '/year' : displayPrice + '/month'}`}
          </button>
          
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition duration-200"
          >
            Cancel
          </button>
        </div>
        
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

// Content View Modal
const ContentViewModal = ({ isOpen, onClose, content }) => {
  if (!isOpen || !content) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">{content.title}</h3>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="capitalize bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                {content.content_type?.replace('_', ' ')}
              </span>
              <span>{content.word_count} words</span>
              <span>{content.credits_used} credits used</span>
              <span>{new Date(content.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ✕
          </button>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h4 className="font-semibold text-gray-700 mb-3">Content Preview:</h4>
          <div className="prose max-w-none">
            <p className="whitespace-pre-wrap text-gray-800 leading-relaxed">
              {content.content}
            </p>
          </div>
        </div>

        {content.keywords && content.keywords.length > 0 && (
          <div className="mb-6">
            <h4 className="font-semibold text-gray-700 mb-2">Keywords:</h4>
            <div className="flex flex-wrap gap-2">
              {content.keywords.map((keyword, index) => (
                <span key={index} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex gap-4">
          <button
            onClick={async () => {
              const success = await copyToClipboard(content.content);
              if (success) {
                alert('✅ Content copied to clipboard!');
              } else {
                alert('❌ Failed to copy. Please select and copy manually.');
              }
            }}
            className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition duration-200"
          >
            📋 Copy to Clipboard
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Urgency Banner
const UrgencyBanner = () => {
  const [timeLeft, setTimeLeft] = useState({});
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const difference = endOfDay - now;

      if (difference > 0) {
        setTimeLeft({
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white py-2 relative">
      <div className="container mx-auto px-4 text-center">
        <p className="text-sm font-semibold">
          🔥 Limited Time: Get 50% More Credits FREE • Expires in {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
        </p>
      </div>
      <button
        onClick={() => setIsVisible(false)}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-200"
      >
        ✕
      </button>
    </div>
  );
};

// Enhanced Landing Page - keeping all the existing code exactly the same as before

// Standalone Quora Pricing Page
const QuoraStandalonePricing = ({ onGetStarted }) => {
  const [isAnnual, setIsAnnual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleSubscribe = async (tier, billing) => {
    setLoading(true);
    setSelectedPlan(`${tier}_${billing}`);

    try {
      const response = await axios.post(`${API}/quora/subscribe`, {
        tier,
        billing
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start subscription. Please login first.');
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-block bg-gradient-to-r from-indigo-100 to-purple-100 px-4 py-2 rounded-full mb-4">
            <span className="text-sm font-semibold text-indigo-700">🚀 Quora Growth Automation</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Turn Quora Into Your
            <br />
            <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Lead Generation Machine
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            AI-powered Quora automation that builds authority, generates backlinks, and grows your brand—all while you sleep.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="text-4xl font-bold text-indigo-600 mb-2">300+</div>
            <div className="text-gray-600">Monthly Answers</div>
            <div className="text-sm text-gray-500 mt-1">(10/day × 30 days)</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="text-4xl font-bold text-purple-600 mb-2">90%</div>
            <div className="text-gray-600">Pure Value Content</div>
            <div className="text-sm text-gray-500 mt-1">(Quora-safe strategy)</div>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="text-4xl font-bold text-pink-600 mb-2">10</div>
            <div className="text-gray-600">Languages Supported</div>
            <div className="text-sm text-gray-500 mt-1">(Global reach)</div>
          </div>
        </div>

        {/* Billing Toggle */}
        <div className="flex justify-center items-center gap-4 mb-12">
          <span className={`font-semibold ${!isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>
            Monthly
          </span>
          <button
            onClick={() => setIsAnnual(!isAnnual)}
            className="relative inline-flex h-8 w-16 items-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 transition"
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white transition ${
                isAnnual ? 'translate-x-9' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`font-semibold ${isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>
            Annual
          </span>
          {isAnnual && (
            <span className="ml-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
              Save up to 38%!
            </span>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
          {/* Basic Plan */}
          <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-gray-200 hover:border-indigo-400 transition">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Quora Basic</h3>
              <p className="text-gray-600 mb-4">Perfect for getting started</p>
              <div className="mb-4">
                {isAnnual ? (
                  <>
                    <span className="text-5xl font-bold text-gray-900">$79</span>
                    <span className="text-gray-600">/year</span>
                    <div className="text-sm text-gray-500 mt-1">$6.58/month billed annually</div>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-bold text-gray-900">$10</span>
                    <span className="text-gray-600">/month</span>
                  </>
                )}
              </div>
              <button
                onClick={() => handleSubscribe('quora_basic', isAnnual ? 'annual' : 'monthly')}
                disabled={loading && selectedPlan === `quora_basic_${isAnnual ? 'annual' : 'monthly'}`}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading && selectedPlan === `quora_basic_${isAnnual ? 'annual' : 'monthly'}` ? 'Processing...' : 'Get Started'}
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700"><strong>5 AI answers per day</strong></span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">90/10 safe link strategy</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Chrome extension access</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Answer quality optimization</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Activity tracking & analytics</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-green-500 text-xl">✓</span>
                <span className="text-gray-700">Multi-language support (10 languages)</span>
              </div>
            </div>
          </div>

          {/* Pro Plan */}
          <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-8 shadow-2xl border-4 border-yellow-400 relative transform md:scale-105">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-full text-sm font-bold">
                ⭐ MOST POPULAR
              </span>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Quora Pro</h3>
              <p className="text-indigo-100 mb-4">For serious growth</p>
              <div className="mb-4">
                {isAnnual ? (
                  <>
                    <span className="text-5xl font-bold text-white">$149</span>
                    <span className="text-indigo-100">/year</span>
                    <div className="text-sm text-indigo-200 mt-1">$12.42/month billed annually</div>
                  </>
                ) : (
                  <>
                    <span className="text-5xl font-bold text-white">$20</span>
                    <span className="text-indigo-100">/month</span>
                  </>
                )}
              </div>
              <button
                onClick={() => handleSubscribe('quora_pro', isAnnual ? 'annual' : 'monthly')}
                disabled={loading && selectedPlan === `quora_pro_${isAnnual ? 'annual' : 'monthly'}`}
                className="w-full bg-white text-indigo-600 py-4 rounded-xl font-semibold hover:bg-gray-50 transition disabled:opacity-50"
              >
                {loading && selectedPlan === `quora_pro_${isAnnual ? 'annual' : 'monthly'}` ? 'Processing...' : 'Get Pro Now'}
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-yellow-300 text-xl">★</span>
                <span className="text-white"><strong>10 AI answers per day (MAX)</strong></span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-yellow-300 text-xl">★</span>
                <span className="text-white">90/10 safe link strategy</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-yellow-300 text-xl">★</span>
                <span className="text-white">Chrome extension access</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-yellow-300 text-xl">★</span>
                <span className="text-white">Answer quality optimization</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-yellow-300 text-xl">★</span>
                <span className="text-white">Activity tracking & analytics</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-yellow-300 text-xl">★</span>
                <span className="text-white">Multi-language support (10 languages)</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-yellow-300 text-xl">★</span>
                <span className="text-white"><strong>Priority support</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-3xl p-12 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            How It Works (4 Simple Steps)
          </h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                1️⃣
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Configure Settings</h3>
              <p className="text-sm text-gray-600">Add your niche keywords and company info</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                2️⃣
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Install Extension</h3>
              <p className="text-sm text-gray-600">One-click Chrome extension setup</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                3️⃣
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Find Questions</h3>
              <p className="text-sm text-gray-600">Navigate to any Quora question in your niche</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                4️⃣
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Auto-Post</h3>
              <p className="text-sm text-gray-600">AI generates + you approve + auto-posts!</p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <details className="bg-white rounded-xl p-6 shadow-lg">
              <summary className="font-semibold text-gray-900 cursor-pointer">
                Is this safe? Will I get banned from Quora?
              </summary>
              <p className="mt-3 text-gray-600">
                Absolutely safe! We use a 90/10 strategy (9 pure value answers, 1 with subtle link) following Quora's guidelines. The extension uses YOUR browser session, so it looks 100% human. Thousands of users with zero bans.
              </p>
            </details>
            <details className="bg-white rounded-xl p-6 shadow-lg">
              <summary className="font-semibold text-gray-900 cursor-pointer">
                What's the difference between standalone and full platform?
              </summary>
              <p className="mt-3 text-gray-600">
                Standalone Quora = ONLY Quora automation at lower price ($10-20/mo). Full iSupplyAI platform = Quora + 25+ content generation features ($50-249/mo). Choose standalone if you only need Quora growth!
              </p>
            </details>
            <details className="bg-white rounded-xl p-6 shadow-lg">
              <summary className="font-semibold text-gray-900 cursor-pointer">
                Can I cancel anytime?
              </summary>
              <p className="mt-3 text-gray-600">
                Yes! Cancel anytime with one click. No questions asked. Annual plans are also refundable within 30 days.
              </p>
            </details>
            <details className="bg-white rounded-xl p-6 shadow-lg">
              <summary className="font-semibold text-gray-900 cursor-pointer">
                Do I need technical skills?
              </summary>
              <p className="mt-3 text-gray-600">
                Zero technical skills needed! If you can browse Quora, you can use this. Setup takes 5 minutes.
              </p>
            </details>
          </div>
        </div>

        {/* CTA Footer */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-3xl p-12 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Dominate Quora?
          </h2>
          <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of businesses building authority and generating leads on Quora with AI automation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => handleSubscribe('quora_basic', 'monthly')}
              className="bg-white text-indigo-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition"
            >
              Start with Basic ($10/mo)
            </button>
            <button
              onClick={() => handleSubscribe('quora_pro', isAnnual ? 'annual' : 'monthly')}
              className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-yellow-300 transition"
            >
              Get Pro Now (Most Popular)
            </button>
          </div>
          <p className="text-sm text-indigo-200 mt-6">
            🔒 Secure payment via Stripe • Cancel anytime • 30-day money-back guarantee
          </p>
        </div>
      </div>
    </div>
  );
};


const LandingPage = ({ onGetStarted }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAnnual, setIsAnnual] = useState(false); // Annual pricing toggle

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    // Show email capture modal after 30 seconds
    const timer = setTimeout(() => {
      if (!localStorage.getItem('email_captured')) {
        setShowEmailModal(true);
      }
    }, 30000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const testimonials = [
      {
        name: "Sarah Johnson",
        role: "Marketing Director",
        company: "TechStart Inc.",
        text: "ContentGen Pro increased our content output by 500% while cutting costs by 80%. ROI was immediate!",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b977?w=60&h=60&fit=crop&crop=face"
      },
      {
        name: "Mike Chen",
        role: "Startup Founder",
        company: "GrowthLabs",
        text: "From 2 hours per blog post to 5 minutes. This tool paid for itself in the first week.",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
      },
      {
        name: "Emma Davis",
        role: "Content Creator",
        company: "Digital Nomad",
        text: "Generated $50K in client revenue using ContentGen Pro. It's like having a team of writers for $30/month!",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face"
      }
    ];

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleEmailCapture = ({ email, name }) => {
    localStorage.setItem('email_captured', 'true');
    localStorage.setItem('user_email', email);
    localStorage.setItem('user_name', name);
    
    // Track email capture
    if (window.gtag) {
      window.gtag('event', 'generate_lead', {
        event_category: 'engagement',
        event_label: 'email_capture'
      });
    }
    
    onGetStarted();
  };

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Marketing Director", 
      company: "TechStart Inc.",
      text: "ContentGen Pro increased our content output by 500% while cutting costs by 80%. ROI was immediate!",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b977?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Mike Chen",
      role: "Startup Founder",
      company: "GrowthLabs", 
      text: "From 2 hours per blog post to 5 minutes. This tool paid for itself in the first week.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Emma Davis",
      role: "Content Creator",
      company: "Digital Nomad",
      text: "Generated $50K in client revenue using ContentGen Pro. It's like having a team of writers for $30/month!",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      <UrgencyBanner />
      <LiveStats />
      
      {/* Navigation */}
      <nav className={`fixed w-full z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-2 sm:px-4 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4 flex-shrink-0">
            <img 
              src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" 
              alt="iSupplyAI Logo" 
              className="h-12 w-12 sm:h-16 sm:w-16 md:h-28 md:w-28 object-contain"
            />
            <h1 className="text-xl sm:text-2xl md:text-5xl font-black bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent whitespace-nowrap">
              iSupplyAI
            </h1>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4 flex-shrink-0">
            <Link
              to="/free-downloads"
              className="text-xs sm:text-sm md:text-base text-gray-700 hover:text-purple-600 font-semibold transition duration-300 whitespace-nowrap"
              title="Free Resources"
            >
              📚 Free Resources
            </Link>
            <span className="text-xs md:text-sm text-gray-600 hidden lg:block">🔥 Join 10,000+ creators</span>
            <button
              onClick={onGetStarted}
              className="text-purple-600 hover:text-purple-800 font-semibold px-3 py-2 sm:px-4 rounded-full text-xs sm:text-sm md:text-base whitespace-nowrap"
              data-testid="nav-login-button"
            >
              Login
            </button>
            <button
              onClick={onGetStarted}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-2 sm:px-4 md:px-6 rounded-full hover:shadow-lg transition duration-300 pulse-animation text-xs sm:text-sm md:text-base whitespace-nowrap"
              data-testid="nav-get-started-button"
            >
              🔥 Pricing
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 animated-gradient opacity-10 -z-10"></div>
        
        <div className="container mx-auto text-center max-w-6xl relative z-10">
          <div className="mb-12">
            <div className="inline-flex items-center glass-effect rounded-full px-6 py-3 mb-8 shadow-vibrant fade-in-up">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              <span className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">🚀 Trusted by 10,000+ businesses worldwide</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight fade-in-up delay-100">
              <span className="gradient-text neon-glow block mb-2">Generate Perfect Content</span>
              <span className="text-4xl md:text-6xl bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">in 10 Seconds with AI</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-4xl mx-auto leading-relaxed fade-in-up delay-200">
              Stop spending hours writing. Create high-converting blog posts, social media content, and marketing copy 
              that drives real results. <strong className="gradient-text">Join the content revolution.</strong>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-10 fade-in-up delay-300">
              <button
                onClick={onGetStarted}
                className="btn-primary-enhanced text-white px-10 py-5 rounded-full text-lg font-bold transition-smooth relative group"
                data-testid="hero-get-started-button"
              >
                <span className="relative z-10">🚀 Start Creating Free</span>
              </button>

              {/* Social Share Buttons */}
              <div className="flex items-center justify-center gap-4">
                <span className="text-sm text-gray-600 font-medium">Share:</span>
                <a
                  href={`https://twitter.com/intent/tweet?text=Check out ISupplyAI - The AI Content Intelligence Platform that turns 1 piece of content into 10+ optimized versions! 🚀&url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white rounded-full flex items-center justify-center transition-smooth hover:scale-110 shadow-elegant"
                  title="Share on Twitter"
                >
                  <span className="text-lg font-bold">𝕏</span>
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 text-white rounded-full flex items-center justify-center transition-smooth hover:scale-110 shadow-elegant"
                  title="Share on LinkedIn"
                >
                  <span className="font-bold">in</span>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white rounded-full flex items-center justify-center transition-smooth hover:scale-110 shadow-elegant"
                  title="Share on Facebook"
                >
                  <span className="font-bold">f</span>
                </a>
                <button
                  onClick={async () => {
                    const success = await copyToClipboard(window.location.href);
                    alert(success ? '✅ Link copied to clipboard!' : '❌ Copy failed');
                  }}
                  className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-800 hover:from-gray-700 hover:to-gray-900 text-white rounded-full flex items-center justify-center transition-smooth hover:scale-110 shadow-elegant"
                  title="Copy link"
                >
                  🔗
                </button>
              </div>
            </div>
            
            <div className="text-center mb-8 fade-in-up delay-400">
              <p className="text-sm text-gray-500 font-medium">✅ No credit card required</p>
              <p className="text-sm text-gray-500 font-medium">✅ 50 free credits • Setup in 30 seconds</p>
            </div>
            
            <div className="flex justify-center items-center space-x-8 text-sm text-gray-600 mb-8 fade-in-up delay-500">
              <div className="flex items-center glass-effect px-4 py-2 rounded-full shadow-elegant">
                <span className="text-yellow-500 mr-2">⭐⭐⭐⭐⭐</span>
                <span className="font-semibold">4.9/5 rating (2,847 reviews)</span>
              </div>
            </div>
          </div>
          
          {/* Hero Image - iSupplyAI Logo */}
          <div className="relative max-w-5xl mx-auto fade-in-up delay-300">
            <div className="w-full h-72 md:h-96 glass-effect rounded-3xl shadow-vibrant overflow-hidden relative flex items-center justify-center card-hover">
              {/* Animated background with gradient */}
              <div className="absolute inset-0 animated-gradient opacity-30"></div>
              
              {/* Floating orbs */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-10 left-10 w-64 h-64 bg-purple-400 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-10 right-10 w-64 h-64 bg-blue-400 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pink-300 rounded-full blur-3xl" style={{animation: 'float 6s ease-in-out infinite'}}></div>
              </div>
              
              {/* Logo Image */}
              <div className="relative z-10 flex items-center justify-center px-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_quora-twitter-ai/artifacts/7cpotg1i_8fc8dhxn_ISupplyAILogo%238%20%281%29.png"
                  alt="iSupplyAI - AI Content Intelligence Platform" 
                  className="max-h-64 md:max-h-80 w-auto object-contain drop-shadow-2xl"
                  style={{animation: 'float 4s ease-in-out infinite', filter: 'drop-shadow(0 0 30px rgba(102, 126, 234, 0.6))'}}
                />
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -top-4 -left-4 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-bounce">
              💰 $127K+ Revenue Generated
            </div>
            <div className="absolute -top-4 -right-4 bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-bold animate-bounce" style={{animationDelay: '1s'}}>
              ⚡ 10x Faster Writing
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Ticker */}
      <section className="py-8 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="overflow-hidden">
            <div className="flex animate-scroll space-x-8 items-center">
              <span className="whitespace-nowrap text-gray-600">🎉 "Generated 50 blog posts in 1 day!" - Marketing Manager</span>
              <span className="whitespace-nowrap text-gray-600">💰 "Saved $5000/month on content writers" - Startup Founder</span>
              <span className="whitespace-nowrap text-gray-600">🚀 "10x faster content creation" - Agency Owner</span>
              <span className="whitespace-nowrap text-gray-600">⭐ "Best AI tool I've ever used" - Content Creator</span>
              <span className="whitespace-nowrap text-gray-600">💡 "Doubled our content output overnight" - CMO</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why 10,000+ Businesses Choose ContentGen Pro</h2>
            <p className="text-xl text-gray-600">The only AI content platform you'll ever need</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 hover:shadow-lg transition duration-300 group">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                <span className="text-white text-3xl">🧠</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">5 Content Types</h3>
              <p className="text-gray-600 mb-4">Blog posts, social media, marketing copy, product descriptions, and email campaigns</p>
              <div className="text-sm text-purple-600 font-semibold">✅ SEO Optimized • ✅ Brand Voice Match</div>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 hover:shadow-lg transition duration-300 group">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                <span className="text-white text-3xl">⚡</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">10x Faster</h3>
              <p className="text-gray-600 mb-4">Generate professional content in seconds, not hours. Boost your productivity instantly</p>
              <div className="text-sm text-blue-600 font-semibold">✅ Instant Results • ✅ No Writer's Block</div>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-gradient-to-br from-green-50 to-green-100 hover:shadow-lg transition duration-300 group">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition duration-300">
                <span className="text-white text-3xl">💰</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">ROI Guaranteed</h3>
              <p className="text-gray-600 mb-4">Save $5000+/month on content writers. Tool pays for itself in the first week</p>
              <div className="text-sm text-green-600 font-semibold">✅ 30-Day Money Back • ✅ Cancel Anytime</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600">Real results from real customers</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-xl max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-8">
              <img
                src={testimonials[currentTestimonial].avatar}
                alt={testimonials[currentTestimonial].name}
                className="w-20 h-20 rounded-full border-4 border-purple-200"
              />
              <div className="flex-1 text-center md:text-left">
                <p className="text-lg text-gray-700 italic mb-4">"{testimonials[currentTestimonial].text}"</p>
                <div>
                  <h4 className="font-semibold text-gray-800">{testimonials[currentTestimonial].name}</h4>
                  <p className="text-sm text-gray-600">{testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <div
                    key={index}
                    className={`w-3 h-3 rounded-full ${
                      index === currentTestimonial ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credit Value Explainer - NEW */}
      <section className="py-12 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <h3 className="text-3xl font-bold text-white text-center mb-6">💎 What's a Credit Worth?</h3>
            <div className="grid md:grid-cols-3 gap-6 text-white">
              <div className="text-center">
                <div className="text-5xl mb-2">📝</div>
                <h4 className="font-bold text-xl mb-2">1 Credit =</h4>
                <p className="text-lg">One piece of AI-generated content (up to 1,000 words)</p>
              </div>
              <div className="text-center">
                <div className="text-5xl mb-2">♻️</div>
                <h4 className="font-bold text-xl mb-2">Credits Roll Over</h4>
                <p className="text-lg">Unused credits never expire - use them anytime</p>
              </div>
              <div className="text-center">
                <div className="text-5xl mb-2">⚡</div>
                <h4 className="font-bold text-xl mb-2">Buy Top-Ups</h4>
                <p className="text-lg">Need more? Purchase additional credits anytime for $0.99 each</p>
              </div>
            </div>
            <div className="mt-8 bg-yellow-400/20 border border-yellow-400 rounded-lg p-4 text-center">
              <p className="text-white font-semibold text-lg">
                💡 <strong>Pro Tip:</strong> 1 credit = 1 blog post, 1 email, 1 social post, or 1 product description
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Pricing Section with Urgency */}
      <section className="py-20 bg-gradient-to-br from-purple-900 to-blue-900 text-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-500 rounded-full px-4 py-2 mb-4 animate-pulse">
              <span className="w-2 h-2 bg-white rounded-full mr-2 animate-ping"></span>
              <span className="text-sm font-bold">FOUNDERS PRICING: Only 47 Spots Left at $50/mo (Regular $79)</span>
            </div>
            <h2 className="text-4xl font-bold mb-4">Choose Your Content Superpower</h2>
            <p className="text-xl text-purple-100">Lock in founder pricing before spots run out. Cancel anytime.</p>
            
            {/* Social Proof - NEW */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center text-yellow-300">
                <span className="text-3xl mr-2">⭐⭐⭐⭐⭐</span>
                <span className="text-white font-semibold">4.9/5 rating</span>
              </div>
              <div className="flex items-center text-white">
                <span className="text-3xl mr-2">👥</span>
                <span className="font-semibold">2,500+ creators trust iSupplyAI</span>
              </div>
              <div className="flex items-center text-white">
                <span className="text-3xl mr-2">📝</span>
                <span className="font-semibold">50,000+ content pieces generated</span>
              </div>
            </div>
            
            {/* Annual/Monthly Toggle - NEW */}
            <div className="mt-12 flex justify-center items-center">
              <div className="bg-white/10 backdrop-blur-lg rounded-full p-2 border border-white/20 inline-flex items-center">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`px-8 py-3 rounded-full font-bold transition-all duration-300 ${
                    !isAnnual 
                      ? 'bg-white text-purple-600 shadow-lg' 
                      : 'text-white hover:text-purple-200'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`px-8 py-3 rounded-full font-bold transition-all duration-300 relative ${
                    isAnnual 
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg' 
                      : 'text-white hover:text-purple-200'
                  }`}
                >
                  Annual
                  {!isAnnual && (
                    <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full font-bold animate-bounce">
                      Save 20%
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Free Plan */}
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition duration-300">
              <div className="text-center mb-6">
                <h3 className="text-4xl font-black mb-2 text-black bg-white px-4 py-2 rounded-lg inline-block shadow-lg">Starter</h3>
                <div className="mb-4 mt-4">
                  <span className="text-5xl font-bold text-white">$0</span>
                  <span className="text-white text-lg">/month</span>
                </div>
                <p className="text-white font-semibold text-lg">Perfect for trying out AI content</p>
              </div>
              <ul className="space-y-3 mb-8 text-white">
                <li className="flex items-center">
                  <span className="text-green-400 mr-3 text-xl">✓</span>
                  <span className="text-base">10 credits per month</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-400 mr-3 text-xl">✓</span>
                  <span className="text-base">5 content types (blog, social, email, marketing, product)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-400 mr-3 text-xl">✓</span>
                  <span className="text-base">Basic AI content generation</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-400 mr-3 text-xl">✓</span>
                  <span className="text-base">7 Gods Website Roast (unlimited)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-green-400 mr-3 text-xl">✓</span>
                  <span className="text-base">Basic templates</span>
                </li>
                <li className="flex items-center">
                  <span className="text-gray-400 mr-3 text-xl">✗</span>
                  <span className="text-gray-400 text-base">No Living War Room</span>
                </li>
                <li className="flex items-center">
                  <span className="text-gray-400 mr-3 text-xl">✗</span>
                  <span className="text-gray-400 text-base">No Artemis Lead Hunter</span>
                </li>
                <li className="flex items-center">
                  <span className="text-gray-400 mr-3 text-xl">✗</span>
                  <span className="text-gray-400 text-base">No God Memory or Debate Verdicts</span>
                </li>
                <li className="flex items-center">
                  <span className="text-gray-400 mr-3 text-xl">✗</span>
                  <span className="text-gray-400 text-base">No AI God Agents</span>
                </li>
              </ul>
              <button
                onClick={onGetStarted}
                className="w-full bg-white/20 text-white py-3 rounded-lg font-bold text-lg hover:bg-white/30 transition duration-200"
                data-testid="free-plan-button"
              >
                Start Free Today
              </button>
              <p className="text-center text-white text-sm mt-3 font-semibold">
                🔓 No credit card required • Instant access
              </p>
            </div>

            {/* Pro Plan - FEATURED */}
            <div className="bg-gradient-to-br from-orange-500 to-red-500 p-8 rounded-2xl border-4 border-orange-300 hover:shadow-2xl transition-all transform hover:scale-105 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-red-600 text-white px-6 py-2 rounded-full text-sm font-bold animate-pulse">🔥 MOST POPULAR</span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-4xl font-black mb-2 text-black bg-white px-4 py-2 rounded-lg inline-block shadow-lg">Pro</h3>
                <div className="mb-4 mt-4">
                  {!isAnnual ? (
                    <>
                      <div className="mb-2">
                        <span className="text-3xl font-bold text-white/60 line-through mr-2">$79</span>
                      </div>
                      <span className="text-6xl font-bold text-white drop-shadow-lg">$50</span>
                      <span className="text-white text-xl">/month</span>
                      <div className="mt-3 bg-yellow-400 text-black inline-block px-4 py-2 rounded-full font-bold text-sm">
                        FOUNDERS PRICING — Only 47 spots left
                      </div>
                      <p className="text-yellow-300 text-sm mt-2">$50/mo locked forever (goes to $79)</p>
                    </>
                  ) : (
                    <>
                      <div className="text-yellow-300 text-lg line-through mb-2">$948/year</div>
                      <span className="text-6xl font-bold text-white drop-shadow-lg">$690</span>
                      <span className="text-white text-xl">/year</span>
                      <div className="mt-2 bg-green-500 text-white inline-block px-4 py-1 rounded-full font-bold text-sm">
                        Save $258 (27% OFF)
                      </div>
                    </>
                  )}
                </div>
                <p className="text-white font-bold text-xl bg-black/20 px-4 py-2 rounded-lg">For serious content creators</p>
              </div>
              <ul className="space-y-4 mb-8 text-white text-base">
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <strong className="text-lg">100 credits per month</strong>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">All 5 content types + all tones</span>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">The Living War Room</strong> 
                    <span className="text-yellow-100 ml-2">(12 AI gods debate your strategy live)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">God Memory System</strong> 
                    <span className="text-yellow-100 ml-2">(gods remember your past debates)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Debate Verdicts + Action Items</strong> 
                    <span className="text-yellow-100 ml-2">(tracked conclusions)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">"Side With" Dynamics</strong> 
                    <span className="text-yellow-100 ml-2">(take sides, shift the debate)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Artemis Lead Hunter</strong> 
                    <span className="text-yellow-100 ml-2">(Reddit, forums, web lead discovery)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Save + Share Debates</strong> 
                    <span className="text-yellow-100 ml-2">(shareable public links)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Mirror of Truth</strong> 
                    <span className="text-yellow-100 ml-2">(business metrics benchmarking)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Multi-Format Explosion</strong> 
                    <span className="text-yellow-100 ml-2">(1 content piece into 17+ formats)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">SEO Optimizer + Performance Predictor</strong>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Brand Voice Analyzer</strong>
                  </div>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Content Brief Generator + Calendar</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Privacy controls (your data, your rules)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">10 languages support</span>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Dionysus Reddit Engine</strong>
                    <span className="text-yellow-100 ml-2">(AI replies + subreddit discovery)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Divine Strike Pipeline</strong>
                    <span className="text-yellow-100 ml-2">(one-click Athena→Hermes→Apollo)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Quora Goldmine</strong>
                    <span className="text-yellow-100 ml-2">(automated answers + Chrome Extension)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">ROI Calculator + Sentiment Predictor</strong>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Content Health Monitor</strong>
                    <span className="text-yellow-100 ml-2">(decay alerts + refresh tips)</span>
                  </div>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Trending Topics + Viral Content Analyzer</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Smart Hashtag Generator + Content Export</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Referral Program + Divine Ascension</span>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Competitor Analyzer</strong>
                    <span className="text-yellow-100 ml-2">(spy on any competitor)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Content Multiplication</strong>
                    <span className="text-yellow-100 ml-2">(1 → 10+ platforms instantly)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Website Analyzer</strong>
                    <span className="text-yellow-100 ml-2">(brand voice from any URL)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Ads Dashboard</strong>
                    <span className="text-yellow-100 ml-2">(AI ad copy generation)</span>
                  </div>
                </li>
                <li className="flex items-center group relative">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <div className="flex items-center">
                    <strong className="text-yellow-300 text-lg">Analytics Dashboard</strong>
                    <span className="text-yellow-100 ml-2">(content performance tracking)</span>
                  </div>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Content Gap Analysis + Repurposing Engine</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Content Scheduling + Free Resources Library</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Guided Onboarding + Privacy Controls</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="text-lg">Priority support + Premium templates</span>
                </li>
              </ul>
              
              {/* AI Gods Section - NEW */}
              <div className="bg-black/30 rounded-xl p-4 mb-6 border border-yellow-400/30">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">&#x2694;&#xFE0F;</span>
                  <h4 className="text-lg font-bold text-yellow-300">12 AI God Brains</h4>
                  <span className="bg-yellow-400 text-black text-xs px-2 py-1 rounded-full font-bold">NEW</span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-sm">
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F9E0;</span> Metis (Strategy)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F989;</span> Athena (Intel)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x2694;&#xFE0F;</span> Eris (Contrarian)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F3AD;</span> Calliope (Creative)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F4B0;</span> Plutus (Finance)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F3C6;</span> Nike (Growth)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x2696;&#xFE0F;</span> Themis (Data)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F3E0;</span> Hestia (Customer)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F45F;</span> Hermes (Content)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x2600;&#xFE0F;</span> Apollo (Distribution)</div>
                  <div className="flex items-center gap-1.5 text-white"><span>&#x1F377;</span> Dionysus (Viral)</div>
                </div>
                <p className="text-yellow-200 text-xs mt-2">Each god has a unique brain — pulls real data before speaking</p>
              </div>
              
              <button
                onClick={onGetStarted}
                className="w-full bg-white text-orange-600 py-4 rounded-lg font-black text-xl hover:bg-yellow-300 hover:text-orange-700 transition duration-200 shadow-2xl pulse-animation transform hover:scale-105"
                data-testid="pro-plan-button"
              >
                Lock In Founders Pricing — $50/mo
              </button>
              <p className="text-center text-white text-base mt-3 font-bold bg-black/30 py-2 rounded-lg">Only 47 spots left — Founders rate locked forever</p>
            </div>

            {/* Enterprise Plan - ULTIMATE TIER */}
            <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 p-8 rounded-2xl border-4 border-blue-300 hover:shadow-2xl transition-all transform hover:scale-105 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-6 py-2 rounded-full text-sm font-bold shadow-lg">🏆 FOR UNMATCHED BUSINESS GROWTH</span>
              </div>
              <div className="text-center mb-6">
                <h3 className="text-4xl font-black mb-2 text-black bg-white px-4 py-2 rounded-lg inline-block shadow-lg">Enterprise</h3>
                <div className="mb-4 mt-4">
                  {!isAnnual ? (
                    <>
                      <span className="text-6xl font-bold text-white drop-shadow-lg">$249</span>
                      <span className="text-white text-xl">/month</span>
                    </>
                  ) : (
                    <>
                      <div className="text-yellow-300 text-lg line-through mb-2">$2,988/year</div>
                      <span className="text-6xl font-bold text-white drop-shadow-lg">$2,190</span>
                      <span className="text-white text-xl">/year</span>
                      <div className="mt-2 bg-green-500 text-white inline-block px-4 py-1 rounded-full font-bold text-sm">
                        Save $798 (27% OFF)
                      </div>
                    </>
                  )}
                </div>
                <p className="text-white font-bold text-xl bg-black/20 px-4 py-2 rounded-lg">🚀 For agencies & serious creators</p>
              </div>
              <ul className="space-y-4 mb-8 text-white text-base">
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <strong className="text-lg">500 credits per month</strong> <span className="text-yellow-300 ml-2">(5x more than Pro!)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <strong className="text-2xl text-yellow-300 bg-black/30 px-3 py-1 rounded-lg">Everything in Pro PLUS:</strong>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Unlimited Living War Room Debates</span> <span className="text-yellow-200">(all 12 gods, unlimited sessions)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">God Memory + Persistent Rivalries</span> <span className="text-yellow-200">(gods evolve over time)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Artemis Deep Scan</span> <span className="text-yellow-200">(G2, Capterra, Trustpilot, job boards)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Auto-Pilot Content Engine</span> <span className="text-yellow-200">(90-day automated strategy!)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Bulk Content Generation</span> <span className="text-yellow-200">(10 pieces at once!)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Full API Access & Keys</span> <span className="text-yellow-200">(integrate anywhere)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Team Collaboration</span> <span className="text-yellow-200">(unlimited members)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Executive Analytics Dashboard</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Ad Performance Dashboard</span> <span className="text-yellow-200">(Google + Facebook Ads)</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">Custom Integrations + Zapier</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">99.9% SLA Guarantee</span>
                </li>
                <li className="flex items-center">
                  <span className="text-yellow-300 mr-3 text-2xl">✓</span>
                  <span className="font-bold text-lg">25+ Languages</span>
                </li>
              </ul>
              
              {/* AI Gods Section - Enterprise */}
              <div className="bg-gradient-to-r from-yellow-500/20 to-purple-500/20 rounded-xl p-4 mb-6 border border-yellow-400/50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">&#x2694;&#xFE0F;</span>
                  <h4 className="text-lg font-bold text-yellow-300">Full 12-God Ecosystem</h4>
                  <span className="bg-gradient-to-r from-yellow-400 to-purple-500 text-black text-xs px-2 py-1 rounded-full font-bold">UNLIMITED</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-white">
                    <span>&#x2694;&#xFE0F;</span>
                    <span className="font-semibold">Living War Room</span>
                    <span className="text-yellow-200 text-sm">- Unlimited debates with all 12 gods</span>
                  </div>
                  <div className="flex items-center gap-2 text-white">
                    <span>&#x1F3AF;</span>
                    <span className="font-semibold">Artemis Lead Hunter</span>
                    <span className="text-yellow-200 text-sm">- Unlimited hunts + AI outreach</span>
                  </div>
                  <div className="flex items-center gap-2 text-white">
                    <span>&#x1F9E0;</span>
                    <span className="font-semibold">God Memory + Verdicts</span>
                    <span className="text-yellow-200 text-sm">- Full continuity + action tracking</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/20 text-sm">
                  <p className="text-yellow-200">All 12 god brains active  |  Custom pipelines  |  Priority execution</p>
                </div>
              </div>
              
              <button
                onClick={onGetStarted}
                className="w-full bg-white text-blue-700 py-4 rounded-lg font-black text-xl hover:bg-yellow-300 hover:text-blue-900 transition duration-200 shadow-2xl transform hover:scale-105"
                data-testid="enterprise-plan-button"
              >
                🚀 Upgrade to Enterprise
              </button>
              <p className="text-center text-white text-base mt-3 font-bold bg-black/30 py-2 rounded-lg">⚡ Instant access • No contracts • Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing FAQ Section - NEW */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-purple-900 text-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-12">💬 Frequently Asked Questions</h2>
          <div className="space-y-6">
            {/* FAQ 1 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ What happens to unused credits?</h3>
              <p className="text-purple-100">Credits never expire and roll over month-to-month. Use them whenever you're ready!</p>
            </div>
            
            {/* FAQ 2 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ Can I buy extra credits if I run out?</h3>
              <p className="text-purple-100">Yes! You can purchase additional credits anytime for $0.99 per credit. No need to upgrade your plan.</p>
            </div>
            
            {/* FAQ 3 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ What's your refund policy?</h3>
              <p className="text-purple-100">We offer a 14-day money-back guarantee. If you're not satisfied, we'll refund your payment—no questions asked.</p>
            </div>
            
            {/* FAQ 4 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ Can I cancel anytime?</h3>
              <p className="text-purple-100">Absolutely! Cancel anytime with one click. No contracts, no commitments. Your credits remain available until used.</p>
            </div>
            
            {/* FAQ 5 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ Do I need a credit card for the free trial?</h3>
              <p className="text-purple-100">Nope! Start with our free Starter plan instantly—no credit card required. Upgrade anytime when you're ready.</p>
            </div>
            
            {/* FAQ 6 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ How does Enterprise team collaboration work?</h3>
              <p className="text-purple-100">Enterprise plans include unlimited team members, shared workspaces, content approval workflows, and centralized billing.</p>
            </div>
            
            {/* FAQ 7 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ What payment methods do you accept?</h3>
              <p className="text-purple-100">We accept all major credit cards (Visa, Mastercard, Amex) through our secure Stripe payment processor.</p>
            </div>
            
            {/* FAQ 8 */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-bold mb-2">❓ Can I get invoices for my subscription?</h3>
              <p className="text-purple-100">Yes! Invoices are automatically sent to your email after each payment. You can also download them from your dashboard.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <TrustBadges />

      {/* Unique Features Section - What ChatGPT Can't Do */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">🚀 10 Features ChatGPT Can't Do</h2>
            <p className="text-xl text-gray-600">Why settle for basic AI when you can have intelligence?</p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-purple-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">⚡</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Content Multiplication</h3>
                  <p className="text-gray-700 mb-2">Turn 1 piece into 10+ platform-optimized versions automatically</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: Manual copy/paste for each platform ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: Instant + Best posting times ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-green-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Real-Time SEO Scoring</h3>
                  <p className="text-gray-700 mb-2">SEO score (0-100), keyword density, readability, ranking prediction</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: No SEO analysis at all ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: Replaces $99-299/mo SEO tools ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-purple-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🔮</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Performance Prediction</h3>
                  <p className="text-gray-700 mb-2">Know if content will succeed BEFORE publishing</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: No performance prediction ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: Avoid failed content (Priceless!) ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-orange-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🔥</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Viral Content Analyzer</h3>
                  <p className="text-gray-700 mb-2">Reverse engineer viral patterns, get viral score & frameworks</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: No viral pattern analysis ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: 8 pattern checks + proven frameworks ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-blue-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">📈</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Trending Topics Feed</h3>
                  <p className="text-gray-700 mb-2">Real-time industry trends with search volume & content ideas</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: Knowledge cutoff = outdated ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: Live market data + 10 ideas per trend ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 6 */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-pink-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🎨</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Brand Voice Learning</h3>
                  <p className="text-gray-700 mb-2">Analyzes your content, learns your voice, maintains consistency</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: Manual prompts every time ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: Automatic brand voice profile ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 7 */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-yellow-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Content Gap Analysis</h3>
                  <p className="text-gray-700 mb-2">Strategic planning: what you're missing vs. competitors</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: Can't analyze your entire library ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: Strategic recommendations included ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 8 */}
            <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-teal-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">💎</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Smart Hashtag Generator</h3>
                  <p className="text-gray-700 mb-2">Data-driven hashtags with reach estimation per platform</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: Generic hashtags, no data ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: Engagement data + best practices ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 9 */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-indigo-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🔄</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Content Repurposing</h3>
                  <p className="text-gray-700 mb-2">Blog → Podcast → Video → Email → Thread → Carousel (automated)</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: Manual prompting for each format ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: 6 formats with structured templates ✅</p>
                </div>
              </div>
            </div>

            {/* Feature 10 */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl p-6 hover:shadow-xl transition-shadow border border-rose-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">📊</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">A/B Variations Generator</h3>
                  <p className="text-gray-700 mb-2">Auto-generate 3-5 variations for testing (tone, length, style, CTA)</p>
                  <p className="text-sm text-purple-600 font-semibold">ChatGPT: Request each variation manually ❌</p>
                  <p className="text-sm text-green-600 font-semibold">ISupplyAI: One-click A/B testing ready ✅</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <div className="inline-block bg-gradient-to-r from-purple-100 to-blue-100 rounded-xl p-6">
              <p className="text-2xl font-bold text-gray-800 mb-2">
                ChatGPT = Content Generation
              </p>
              <p className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-4">
                ISupplyAI = Content Intelligence Platform
              </p>
              <p className="text-gray-600">
                Same price as ChatGPT Plus ($20/mo) but with 10 features it will never have
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Examples */}
      <ContentExamples />

      {/* ROI Calculator */}
      <ROICalculator />

      {/* Comparison Table */}
      <ComparisonTable />

      {/* Customer Testimonials */}
      <Testimonials />

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know</p>
          </div>
          
          <div className="space-y-6">
            {[
              {
                q: "How does the AI content generation work?",
                a: "Our AI uses the latest advanced language models (including GPT-4o and cutting-edge AI) trained on billions of high-quality content pieces. Simply provide a topic and our AI creates professional, original content in seconds."
              },
              {
                q: "Is the content plagiarism-free?",
                a: "Yes! All content is 100% original and plagiarism-free. Our AI creates unique content based on your specifications, not copying existing material."
              },
              {
                q: "Can I cancel my subscription anytime?",
                a: "Absolutely. You can cancel your subscription at any time with no questions asked. Your account will remain active until the end of your billing period."
              },
              {
                q: "What if I'm not satisfied?",
                a: "We offer a 30-day money-back guarantee. If you're not completely satisfied, we'll refund your entire purchase - no questions asked."
              },
              {
                q: "How many languages do you support?",
                a: "ContentGen Pro supports content generation in 25+ languages including English, Spanish, French, German, Italian, Portuguese, and many more."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{faq.q}</h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to 10x Your Content Creation?</h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            Only 47 founder spots left at <span className="text-yellow-300 font-bold">$50/month</span> — locks in forever (regular price: $79). 
            Limited spots remaining - claim your founder pricing now!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button
              onClick={onGetStarted}
              className="bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-bold hover:shadow-xl transition duration-300 transform hover:scale-105 pulse-animation"
              data-testid="cta-get-started-button"
            >
              Claim Founder Pricing — $50/mo
            </button>
            <div className="text-purple-100 text-sm">
              <p>Only 47 founder spots remaining — Lock in $50/mo forever — Cancel anytime</p>
            </div>
          </div>
          
          <div className="flex justify-center items-center space-x-8 text-sm text-purple-200">
            <span>⭐⭐⭐⭐⭐ 4.9/5 rating</span>
            <span>💰 30-day money back</span>
            <span>🔒 Enterprise security</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/2nb4d6kg_ISupplyAILogo%232.png" 
                  alt="iSupplyAI Logo" 
                  className="w-8 h-8 object-contain bg-white rounded p-1"
                />
                <h3 className="text-xl font-bold">iSupplyAI</h3>
              </div>
              <p className="text-gray-400 mb-4">The future of content creation is here. Generate perfect content in seconds with AI.</p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition duration-200">Twitter</a>
                <a href="#" className="text-gray-400 hover:text-white transition duration-200">LinkedIn</a>
                <a href="#" className="text-gray-400 hover:text-white transition duration-200">YouTube</a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition duration-200">Features</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">API</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Templates</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition duration-200">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Tutorials</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Status</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition duration-200">About Us</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Careers</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition duration-200">Terms</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-gray-400 mb-4 md:mb-0">
              © 2025 iSupplyAI. All rights reserved. Built with ❤️ for content creators worldwide.
            </p>
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <span>🔒 SSL Secured</span>
              <span>💳 Stripe Payments</span>
              <span>🌍 Global CDN</span>
            </div>
          </div>
        </div>
      </footer>

      <EmailCaptureModal 
        isOpen={showEmailModal} 
        onClose={() => setShowEmailModal(false)}
        onSubmit={handleEmailCapture}
      />
    </div>
  );
};

// Rest of components remain the same...
const Header = () => {
  const { user, isAuthenticated, logout } = React.useContext(AuthContext);

  return (
    <header className="bg-gray-900/95 backdrop-blur-xl text-white shadow-2xl border-b border-gray-800">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="absolute inset-0 bg-purple-500 blur-lg opacity-30 rounded-xl"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" 
              alt="iSupplyAI Logo" 
              className="relative h-12 w-12 object-contain rounded-xl shadow-lg"
            />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-purple-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              iSupplyAI
            </h1>
            <p className="text-xs text-gray-400 hidden md:block">AI-Powered Content Platform</p>
          </div>
        </div>
        {isAuthenticated && (
          <div className="flex items-center space-x-3 md:space-x-4">
            {/* Credits Display - Premium Style */}
            <div className="flex items-center gap-2 bg-gray-800/80 px-3 py-1.5 rounded-full border border-gray-700">
              <span className="text-yellow-400">💎</span>
              <span className="font-semibold text-sm">{user?.credits || 0}</span>
              <span className="text-gray-400 text-xs hidden md:inline">credits</span>
            </div>
            
            {/* Plan Badge - Premium Style */}
            <div className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${
              user?.subscription_tier === 'enterprise' 
                ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-black' 
                : user?.subscription_tier === 'pro' 
                  ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white' 
                  : 'bg-gray-800 text-gray-300 border border-gray-700'
            }`}>
              {user?.subscription_tier === 'enterprise' ? '👑 ' : user?.subscription_tier === 'pro' ? '⭐ ' : ''}
              {user?.subscription_tier}
            </div>
            
            <button
              onClick={logout}
              className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition duration-200 text-sm font-medium border border-gray-700"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

const Login = ({ onSwitchToRegister }) => {
  const { login } = React.useContext(AuthContext);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill form if email was captured
  useEffect(() => {
    const email = localStorage.getItem('user_email');
    if (email) {
      setFormData(prev => ({ ...prev, email }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(formData.email, formData.password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#030305] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-purple-600/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-600/20 rounded-full blur-[150px]" />
      </div>
      
      <div className="relative bg-gray-900 backdrop-blur-xl p-8 rounded-2xl border border-gray-800 shadow-2xl max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" 
            alt="iSupplyAI Logo" 
            className="w-16 h-16 object-contain mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-white">Welcome Back</h2>
          <p className="text-gray-400 mt-2">Sign in to continue creating amazing content</p>
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="login-email" className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              id="login-email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your email"
              data-testid="login-email-input"
              aria-required="true"
            />
          </div>
          
          <div>
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-300">Password</label>
              <button
                type="button"
                onClick={() => window.location.hash = 'forgot-password'}
                className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                Forgot Password?
              </button>
            </div>
            <input
              id="login-password"
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your password"
              data-testid="login-password-input"
              aria-required="true"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white py-3.5 rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 font-semibold disabled:opacity-50"
            data-testid="login-submit-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        
        <p className="text-center text-gray-400 mt-6">
          Don't have an account?{' '}
          <button
            onClick={onSwitchToRegister}
            className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
            data-testid="switch-to-register-button"
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
};

const Register = ({ onSwitchToLogin }) => {
  const { register } = React.useContext(AuthContext);
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Pre-fill form if data was captured
  useEffect(() => {
    const email = localStorage.getItem('user_email');
    const name = localStorage.getItem('user_name');
    if (email || name) {
      setFormData(prev => ({ 
        ...prev, 
        email: email || '', 
        name: name || '' 
      }));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await register(formData.name, formData.email, formData.password);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#030305] flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-purple-600/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-600/20 rounded-full blur-[150px]" />
      </div>
      
      <div className="relative bg-gray-900 backdrop-blur-xl p-8 rounded-2xl border border-gray-800 shadow-2xl max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" 
            alt="iSupplyAI Logo" 
            className="w-16 h-16 object-contain mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-white">🔥 Claim Founder Pricing</h2>
          <p className="text-gray-400 mt-2">Lock in <span className="text-amber-400 font-semibold">$50/month forever</span> — Only 47 founder spots left!</p>
        </div>
        
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your full name"
              data-testid="register-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your email"
              data-testid="register-email-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Create a password (min 6 characters)"
              data-testid="register-password-input"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white py-3.5 rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 font-semibold disabled:opacity-50"
            data-testid="register-submit-button"
          >
            {loading ? 'Creating account...' : '🚀 Create Free Account'}
          </button>
        </form>
        
        <p className="text-center text-gray-400 mt-6">
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
            data-testid="switch-to-login-button"
          >
            Sign in
          </button>
        </p>
        
        {/* Trust badges */}
        <div className="flex justify-center items-center gap-4 mt-6 pt-6 border-t border-gray-800">
          <span className="text-xs text-gray-500 flex items-center gap-1">🔒 Secure</span>
          <span className="text-xs text-gray-500 flex items-center gap-1">💳 No CC Required</span>
          <span className="text-xs text-gray-500 flex items-center gap-1">⚡ Instant Access</span>
        </div>
      </div>
    </div>
  );
};

// AI Image Generator Component
const AIImageGenerator = () => {
  const { user, fetchUserProfile } = React.useContext(AuthContext);
  const [formData, setFormData] = useState({
    prompt: '',
    style: 'natural'
  });
  const [generatedImage, setGeneratedImage] = useState(null);
  const [imageHistory, setImageHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchImageHistory();
  }, []);

  const fetchImageHistory = async () => {
    try {
      const response = await axios.get(`${API}/image/history?limit=10`);
      setImageHistory(response.data);
    } catch (error) {
      console.error('Error fetching image history:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setGeneratedImage(null);

    try {
      const response = await axios.post(`${API}/image/generate`, formData);
      setGeneratedImage(response.data);
      await fetchUserProfile(); // Refresh user credits
      await fetchImageHistory(); // Refresh history
      
      // Track image generation for analytics
      if (window.gtag) {
        window.gtag('event', 'image_generated', {
          style: formData.style,
          event_category: 'engagement'
        });
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to generate image');
    }
    setLoading(false);
  };

  const downloadImage = (imageBase64, prompt) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageBase64}`;
    link.download = `${prompt.substring(0, 30).replace(/[^a-z0-9]/gi, '_')}.png`;
    link.click();
    
    // Track download for analytics
    if (window.gtag) {
      window.gtag('event', 'image_downloaded', {
        event_category: 'engagement'
      });
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {user?.credits <= 10 && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <span>⚠️ Low credits! Image generation costs 5 credits each. Upgrade to continue.</span>
            <button className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition duration-200">
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-8 rounded-xl shadow-lg">
        <h1 className="text-4xl font-bold mb-2">🎨 AI Image Generator</h1>
        <p className="text-lg text-purple-100">Transform your ideas into stunning visuals with AI</p>
        <div className="mt-4 flex items-center space-x-4 text-sm">
          <span className="bg-white/20 px-3 py-1 rounded-full">✨ Powered by OpenAI DALL-E 3</span>
          <span className="bg-white/20 px-3 py-1 rounded-full">⚡ 5 credits per image</span>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {/* Generation Form */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Create New Image</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Description *
              </label>
              <textarea
                required
                value={formData.prompt}
                onChange={(e) => setFormData({...formData, prompt: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Describe the image you want to create... (e.g., 'A serene mountain landscape at sunset with a lake reflection')"
                rows="4"
                maxLength="1000"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.prompt.length}/1000 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Style
              </label>
              <select
                value={formData.style}
                onChange={(e) => setFormData({...formData, style: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="natural">Natural - Realistic and balanced</option>
                <option value="vivid">Vivid - Vibrant colors and high contrast</option>
                <option value="cinematic">Cinematic - Movie-like quality</option>
                <option value="artistic">Artistic - Creative interpretation</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={loading || user?.credits < 5}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-lg hover:from-purple-700 hover:to-blue-700 transition duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Generating Image... (30-60s)
                </span>
              ) : (
                `Generate Image (5 credits)`
              )}
            </button>
          </form>

          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full mt-4 bg-gray-100 text-gray-700 py-3 rounded-lg hover:bg-gray-200 transition duration-200 font-medium"
          >
            {showHistory ? '🖼️ Hide History' : '🖼️ View History'}
          </button>
        </div>

        {/* Generated Image Display */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Generated Image</h2>
          
          {generatedImage ? (
            <div className="space-y-4">
              <div className="relative group">
                <img
                  src={`data:image/png;base64,${generatedImage.image_base64}`}
                  alt={generatedImage.prompt}
                  className="w-full rounded-lg shadow-md"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg" />
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Prompt:</strong> {generatedImage.prompt}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Style:</strong> {generatedImage.style}
                </p>
              </div>

              <button
                onClick={() => downloadImage(generatedImage.image_base64, generatedImage.prompt)}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition duration-200 font-medium flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Image
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
              <div className="text-center">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-gray-500">Your generated image will appear here</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image History */}
      {showHistory && imageHistory.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Recent Images</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {imageHistory.map((img) => (
              <div key={img.id} className="relative group cursor-pointer" onClick={() => setGeneratedImage(img)}>
                <img
                  src={`data:image/png;base64,${img.image_base64}`}
                  alt={img.prompt}
                  className="w-full h-40 object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                  <span className="text-white text-sm opacity-0 group-hover:opacity-100 transition-opacity">Click to view</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Content Generator and Dashboard remain the same as before...

// Language Selector Component
const LanguageSelector = ({ selectedLanguage, onLanguageChange }) => {
  const [languages, setLanguages] = useState([]);
  
  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setLanguages(response.data.languages || []);
    } catch (error) {
      console.error('Error fetching languages:', error);
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        🌍 Language
      </label>
      <select
        value={selectedLanguage}
        onChange={(e) => onLanguageChange(e.target.value)}
        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
      >
        {languages.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag} {lang.name} ({lang.native_name})
          </option>
        ))}
      </select>
      <p className="text-xs text-gray-500 mt-1">
        ✨ Generate content in {languages.find(l => l.code === selectedLanguage)?.name || 'your language'}. FREE for all tiers!
      </p>
    </div>
  );
};


const ContentGenerator = () => {
  const { user, fetchUserProfile } = React.useContext(AuthContext);
  const { workflow, clearRiver, startRiver } = useWorkflow();
  const [contentTypes, setContentTypes] = useState([]);
  const [formData, setFormData] = useState({
    content_type: 'blog_post',
    title: '',
    description: '',
    target_audience: '',
    tone: 'professional',
    word_count: 500,
    keywords: [],
    language: 'en'
  });
  const [generatedContent, setGeneratedContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  // Pre-fill from River workflow
  useEffect(() => {
    if (workflow.active && workflow.targetTool === 'generate') {
      setFormData(prev => ({
        ...prev,
        title: workflow.topic || prev.title,
        description: workflow.topic || prev.description,
        content_type: workflow.contentType || prev.content_type,
        keywords: workflow.keywords ? workflow.keywords.split(',').map(k => k.trim()).filter(Boolean) : prev.keywords,
      }));
    }
  }, [workflow]);

  // Pre-fill from digest email "Forge content on this →" deep-link
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('isupplyai_pending_forge_topic');
      if (!raw) return;
      const pending = JSON.parse(raw);
      const ageMs = Date.now() - (pending.ts || 0);
      // Honor only fresh deep-links (1 hour TTL) so back-button doesn't re-prefill
      if (ageMs > 60 * 60 * 1000) {
        sessionStorage.removeItem('isupplyai_pending_forge_topic');
        return;
      }
      if (pending.topic) {
        setFormData(prev => ({
          ...prev,
          title: pending.topic,
          description: pending.topic,
        }));
      }
      sessionStorage.removeItem('isupplyai_pending_forge_topic');
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetchContentTypes();
  }, []);

  const fetchContentTypes = async () => {
    try {
      const response = await axios.get(`${API}/content-types`);
      setContentTypes(response.data.content_types);
    } catch (error) {
      console.error('Error fetching content types:', error);
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData({
        ...formData,
        keywords: [...formData.keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setFormData({
      ...formData,
      keywords: formData.keywords.filter(k => k !== keyword)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setGeneratedContent(null);

    try {
      const response = await axios.post(`${API}/content/generate`, formData);
      setGeneratedContent(response.data);
      await fetchUserProfile(); // Refresh user credits
      
      // Track content generation for analytics
      if (window.gtag) {
        window.gtag('event', 'content_generated', {
          content_type: formData.content_type,
          word_count: formData.word_count,
          event_category: 'engagement'
        });
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to generate content');
    }
    setLoading(false);
  };

  const exportContent = (format) => {
    if (!generatedContent) return;
    
    const content = `${generatedContent.title}\n\n${generatedContent.content}`;
    
    if (format === 'txt') {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${generatedContent.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
    
    // Track export for analytics
    if (window.gtag) {
      window.gtag('event', 'content_exported', {
        format: format,
        event_category: 'engagement'
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {workflow.active && workflow.targetTool === 'generate' && <RiverBanner />}
      {user?.credits <= 5 && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <span>⚠️ You're running low on credits! Upgrade to continue creating amazing content.</span>
            <button className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition duration-200">
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">AI Content Generator</h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
              <select
                value={formData.content_type}
                onChange={(e) => setFormData({...formData, content_type: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="content-type-select"
              >
                {contentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tone</label>
              <select
                value={formData.tone}
                onChange={(e) => setFormData({...formData, tone: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="tone-select"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="friendly">Friendly</option>
                <option value="formal">Formal</option>
                <option value="creative">Creative</option>
              </select>
            </div>
            
            {/* Language Selector */}
            <div>
              <LanguageSelector 
                selectedLanguage={formData.language}
                onLanguageChange={(lang) => setFormData({...formData, language: lang})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter content title"
              data-testid="content-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Describe what you want to write about..."
              data-testid="content-description-textarea"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
              <input
                type="text"
                value={formData.target_audience}
                onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="e.g., Small business owners"
                data-testid="target-audience-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Word Count</label>
              <input
                type="number"
                min="100"
                max="2000"
                value={formData.word_count}
                onChange={(e) => setFormData({...formData, word_count: parseInt(e.target.value)})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="word-count-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Keywords</label>
            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Add keyword"
                data-testid="keyword-input"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
              />
              <button
                type="button"
                onClick={handleAddKeyword}
                className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition duration-200"
                data-testid="add-keyword-button"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="keywords-container">
              {formData.keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm flex items-center"
                  data-testid={`keyword-tag-${index}`}
                >
                  {keyword}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                    data-testid={`remove-keyword-${index}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || user?.credits <= 0}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-lg hover:from-purple-700 hover:to-blue-700 transition duration-200 font-medium disabled:opacity-50 pulse-animation"
            data-testid="generate-content-button"
          >
            {loading ? 'Generating Content...' : `Generate Content (${Math.max(1, Math.ceil(formData.word_count / 100))} credits)`}
          </button>
        </form>
      </div>

      {generatedContent && (
        <div className="bg-white rounded-2xl shadow-lg p-8 fade-in-up" data-testid="generated-content-container">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-800">Generated Content</h3>
            <div className="flex space-x-2">
              <button
                onClick={() => exportContent('txt')}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition duration-200"
                data-testid="export-content-button"
              >
                📄 Export TXT
              </button>
              <button
                onClick={async () => {
                  const success = await copyToClipboard(generatedContent.content);
                  alert(success ? '✅ Copied!' : '❌ Copy failed');
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
              >
                📋 Copy
              </button>
            </div>
          </div>
          <div className="bg-gray-50 p-6 rounded-lg">
            <h4 className="text-lg font-semibold mb-2">{generatedContent.title}</h4>
            <p className="text-sm text-gray-600 mb-4">
              {generatedContent.word_count} words • {generatedContent.credits_used} credits used • Generated {new Date().toLocaleDateString()}
            </p>
            <div className="prose max-w-none" data-testid="generated-content-text">
              {generatedContent.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* River Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6 pt-4 border-t border-gray-200">
              <RiverButton
                label="Distribute This"
                icon="☀️"
                onClick={() => startRiver({
                  topic: generatedContent.title,
                  content: generatedContent.content,
                  contentType: generatedContent.content_type || 'blog_post',
                  targetTool: 'apollo-chamber',
                  sourceTool: 'generate',
                })}
              />
              <RiverButton
                label="Multiply This"
                icon="✨"
                onClick={() => startRiver({
                  topic: generatedContent.title,
                  content: generatedContent.content,
                  targetTool: 'multiply',
                  sourceTool: 'generate',
                })}
              />
              <RiverButton
                label="SEO Optimize"
                icon="🔍"
                onClick={() => startRiver({
                  topic: generatedContent.title,
                  content: generatedContent.content,
                  targetTool: 'seo',
                  sourceTool: 'generate',
                })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = ({ onNavigate }) => {
  const { user } = React.useContext(AuthContext);
  const { getIntelligenceSummary, store } = useIntelligence();
  const [analytics, setAnalytics] = useState(null);
  const [contentHistory, setContentHistory] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedViewContent, setSelectedViewContent] = useState(null);
  const [pulseBackend, setPulseBackend] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchContentHistory();
    fetchSubscriptionPlans();
    fetchPulse();
  }, []);

  const fetchPulse = async () => {
    try {
      const response = await axios.get(`${API}/intelligence/summary`);
      setPulseBackend(response.data);
    } catch (e) { /* non-blocking */ }
  };

  const [latestDigest, setLatestDigest] = useState(null);
  const [awakeningResult, setAwakeningResult] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/digest/latest`);
        setLatestDigest(data);
      } catch { /* non-blocking */ }
    })();
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/awakening/result`);
        setAwakeningResult(data?.result || null);
      } catch { /* non-blocking */ }
    })();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/dashboard`);
      const raw = response.data?.data || response.data;
      const overview = raw?.overview || {};
      setAnalytics({
        total_content: overview.total_content_pieces || 0,
        total_words_generated: overview.total_words_generated || 0,
        current_credits: overview.credits_remaining ?? raw?.credits?.remaining ?? 0,
        subscription_tier: overview.subscription_tier || 'free',
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchContentHistory = async () => {
    try {
      const response = await axios.get(`${API}/content/history?limit=10`);
      setContentHistory(response.data);
    } catch (error) {
      console.error('Error fetching content history:', error);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setSubscriptionPlans(response.data);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
  };

  const handleUpgradePlan = (planType) => {
    if (planType === 'free') return;
    
    const planDetails = subscriptionPlans[planType];
    setSelectedPlan({
      type: planType,
      ...planDetails
    });
    setShowPaymentModal(true);
    
    // Track upgrade attempt
    if (window.gtag) {
      window.gtag('event', 'upgrade_attempt', {
        plan_type: planType,
        event_category: 'conversion'
      });
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Welcome back, {user?.name}! 🎉</h1>
        <p className="text-gray-400">Your AI-powered command center — explore everything below</p>
      </div>

      {/* Trial Banner */}
      {user?.is_trial_account && user?.trial_expires_at && (
        <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10 p-4" data-testid="trial-banner">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-amber-400 text-lg">{user.trial_label === 'Enterprise Beta' ? '👑' : '⭐'}</span>
              <div>
                <p className="text-white font-semibold text-sm">{user.trial_label || 'Beta Access'}</p>
                <p className="text-gray-400 text-xs">
                  Expires {new Date(user.trial_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  {' '}({Math.max(0, Math.ceil((new Date(user.trial_expires_at) - new Date()) / (1000 * 60 * 60 * 24)))} days left)
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Awakening Findings — populated dashboard for new users (spec Step 4 → Dashboard) */}
      {awakeningResult && (awakeningResult.athena?.gaps_count || awakeningResult.hermes?.topics?.length) ? (
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/30 via-gray-900/60 to-purple-900/20 p-5" data-testid="awakening-findings-card">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">⚡</span>
            <h3 className="text-base font-bold text-white">What your gods found while you were gone</h3>
            <span className="ml-auto text-[10px] uppercase tracking-widest text-emerald-300 font-bold px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10">From Awakening</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg bg-gray-900/50 border border-gray-800 p-3">
              <div className="text-xs text-amber-300 font-bold mb-1">🦉 Athena</div>
              <div className="text-white">
                <strong>{awakeningResult.athena?.gaps_count || 0}</strong> gaps · {awakeningResult.athena?.competitors_scanned || 0} competitors
              </div>
              {awakeningResult.athena?.top_opportunity && (
                <div className="text-xs text-gray-400 mt-1 truncate" title={awakeningResult.athena.top_opportunity}>Top: {awakeningResult.athena.top_opportunity}</div>
              )}
            </div>
            <div className="rounded-lg bg-gray-900/50 border border-gray-800 p-3">
              <div className="text-xs text-cyan-300 font-bold mb-1">✉️ Hermes</div>
              <div className="text-white"><strong>{awakeningResult.hermes?.topics?.length || 0}</strong> ready topics</div>
              {awakeningResult.hermes?.topics?.[0]?.title && (
                <div className="text-xs text-gray-400 mt-1 truncate" title={awakeningResult.hermes.topics[0].title}>1st: {awakeningResult.hermes.topics[0].title}</div>
              )}
            </div>
            <div className="rounded-lg bg-gray-900/50 border border-gray-800 p-3">
              <div className="text-xs text-yellow-300 font-bold mb-1">💰 Plutus</div>
              <div className="text-white">${(awakeningResult.plutus?.monthly_value_usd || 0).toLocaleString()}/mo projected</div>
              <div className="text-xs text-gray-400 mt-1">{awakeningResult.plutus?.time_saved_hours_per_month || 0}h saved/month</div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Intelligence Pulse - Cross-Feature Brain */}
      {(() => {
        const clientSummary = getIntelligenceSummary();
        const trending = pulseBackend?.trending_count ?? clientSummary.trendingCount ?? 0;
        const gaps = (clientSummary.uncoveredGaps?.length || 0) || (pulseBackend?.gaps_count || 0);
        const generatedWeek = pulseBackend?.generated_this_week ?? clientSummary.generatedThisWeek ?? 0;
        const suggestedTopics = clientSummary.suggestedTopics || [];
        const gapsList = clientSummary.uncoveredGaps || [];
        const suggestion = pulseBackend?.suggestion;

        return (
          <div
            className="relative overflow-hidden rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-gray-900/80 to-blue-900/30 p-6"
            data-testid="intelligence-pulse-card"
          >
            {/* Animated pulse glow */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <span className="text-2xl">🧠</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Intelligence Pulse</h2>
                  <p className="text-xs text-gray-400">Cross-feature signals from Trending Topics, Athena &amp; Hermes</p>
                </div>
                <span className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                  LIVE
                </span>
              </div>

              {/* Stat strip */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="rounded-xl bg-gray-900/60 border border-gray-800 p-4" data-testid="pulse-stat-trending">
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                    <span>📈</span> Active Trends
                  </div>
                  <div className="text-2xl font-bold text-purple-300">{trending}</div>
                </div>
                <div className="rounded-xl bg-gray-900/60 border border-gray-800 p-4" data-testid="pulse-stat-gaps">
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                    <span>🎯</span> Uncovered Gaps
                  </div>
                  <div className="text-2xl font-bold text-amber-300">{gaps}</div>
                </div>
                <div className="rounded-xl bg-gray-900/60 border border-gray-800 p-4" data-testid="pulse-stat-generated">
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                    <span>⚒️</span> Forged This Week
                  </div>
                  <div className="text-2xl font-bold text-cyan-300">{generatedWeek}</div>
                </div>
              </div>

              {/* Next Best Action */}
              {(suggestion || suggestedTopics.length > 0 || gapsList.length > 0) && (
                <div className="rounded-xl bg-black/30 border border-purple-500/20 p-4 mb-4" data-testid="pulse-next-action">
                  <div className="flex items-start gap-3">
                    <span className="text-lg flex-shrink-0">💡</span>
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-widest text-purple-300 font-bold mb-1">Next Best Action</div>
                      {suggestion ? (
                        <>
                          <p className="text-sm text-white font-medium">{suggestion.action}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{suggestion.reason}</p>
                        </>
                      ) : gapsList.length > 0 ? (
                        <>
                          <p className="text-sm text-white font-medium">
                            You have {gapsList.length} uncovered gap{gapsList.length !== 1 ? 's' : ''} Athena found. Forge one into content.
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">Next gap: {gapsList[0]?.topic || gapsList[0]?.gap}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-white font-medium">Kick off a Trending Topics scan to prime your intelligence.</p>
                          <p className="text-xs text-gray-400 mt-0.5">Trends feed gap analysis and auto-brief generation.</p>
                        </>
                      )}
                      <button
                        onClick={() => onNavigate(suggestion?.target_tool || (gapsList.length > 0 ? 'competitor-analyzer' : 'trending'))}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-purple-300 hover:text-purple-200"
                        data-testid="pulse-action-cta"
                      >
                        Go now →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Trending + Gaps quick view */}
              <div className="grid md:grid-cols-2 gap-3">
                {suggestedTopics.length > 0 && (
                  <div className="rounded-xl bg-gray-900/40 border border-gray-800 p-4" data-testid="pulse-trending-topics">
                    <div className="flex items-center gap-2 text-xs font-bold text-purple-300 mb-2">
                      <span>🔥</span> TRENDING NOW
                    </div>
                    <ul className="space-y-1.5">
                      {suggestedTopics.slice(0, 3).map((t, i) => (
                        <li key={i} className="text-sm text-gray-300 truncate flex items-start gap-2">
                          <span className="text-purple-400 flex-shrink-0">#{i + 1}</span>
                          <span className="truncate">{t}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => onNavigate('trending')}
                      className="mt-2 text-xs text-purple-400 hover:text-purple-300 font-semibold"
                    >
                      View all trends →
                    </button>
                  </div>
                )}
                {gapsList.length > 0 && (
                  <div className="rounded-xl bg-gray-900/40 border border-gray-800 p-4" data-testid="pulse-uncovered-gaps">
                    <div className="flex items-center gap-2 text-xs font-bold text-amber-300 mb-2">
                      <span>⚔️</span> UNCOVERED GAPS
                    </div>
                    <ul className="space-y-1.5">
                      {gapsList.slice(0, 3).map((g, i) => (
                        <li key={i} className="text-sm text-gray-300 truncate flex items-start gap-2">
                          <span className="text-amber-400 flex-shrink-0">→</span>
                          <span className="truncate">{g.topic || g.gap || g.text}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => onNavigate('competitor-analyzer')}
                      className="mt-2 text-xs text-amber-400 hover:text-amber-300 font-semibold"
                    >
                      Open Beat Competition →
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Your Latest Digest card */}
      {latestDigest && (
        <div className="rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-900/30 via-gray-900/70 to-gray-900/70 p-5 sm:p-6" data-testid="dashboard-latest-digest">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/30 flex-shrink-0">
              <span className="text-xl">📬</span>
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-blue-300 font-bold">Your Weekly Intelligence Digest</div>
              <div className="text-lg font-bold text-white mt-0.5">
                {latestDigest.latest ? (latestDigest.latest.user_name || 'Your').split(' ')[0] + "'s Week Ahead" : 'Not yet set up'}
              </div>
            </div>
            {latestDigest.latest?.username && latestDigest.latest?.week_slug && (
              <a
                href={`/digest/@${latestDigest.latest.username}/${latestDigest.latest.week_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-300 border border-blue-500/30 font-semibold hover:bg-blue-500/30"
                data-testid="dashboard-latest-digest-view"
              >
                View public →
              </a>
            )}
          </div>

          {latestDigest.latest ? (
            <>
              {/* 3 topic preview */}
              <ul className="space-y-1.5 mb-3">
                {((latestDigest.latest.content?.trends || []).slice(0, 3)).map((t, i) => (
                  <li key={i} className="text-sm text-gray-300 flex gap-2">
                    <span className="text-blue-400 flex-shrink-0">#{i + 1}</span>
                    <span className="truncate">{t.topic || 'Trending topic'}</span>
                  </li>
                ))}
                {(latestDigest.latest.content?.trends || []).length === 0 && (
                  <li className="text-sm text-gray-500 italic">Topics will appear once your first digest generates.</li>
                )}
              </ul>

              {/* Stats */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400 pt-3 border-t border-gray-800">
                <span>📅 {new Date(latestDigest.latest.published_at).toLocaleDateString()}</span>
                {latestDigest.latest.view_count !== undefined && <span>👁 {latestDigest.latest.view_count} total views</span>}
                {typeof latestDigest.views_this_week === 'number' && <span className="text-cyan-300">🌐 {latestDigest.views_this_week} views this week</span>}
                {latestDigest.latest.share_count !== undefined && <span>📣 {latestDigest.latest.share_count} shares</span>}
                <span className="ml-auto">Next Monday 9am · {latestDigest.industry || 'marketing'}</span>
              </div>
            </>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
              <div className="flex-1">
                <div className="text-sm text-gray-300">
                  {latestDigest.enabled
                    ? "Your next digest will publish Monday at 9am local time."
                    : latestDigest.tier_eligible
                      ? "Turn on your weekly digest to get a 60-sec CEO briefing + 3 trends every Monday."
                      : "Upgrade to Pro to unlock the Weekly Intelligence Digest."}
                </div>
              </div>
              <button
                onClick={() => onNavigate('digest-settings')}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-semibold flex-shrink-0"
                data-testid="dashboard-latest-digest-cta"
              >
                {latestDigest.enabled ? 'Manage digest →' : latestDigest.tier_eligible ? 'Set it up →' : 'See Pro plans →'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="analytics-card-1 text-white p-6 rounded-2xl shadow-lg card-hover">
            <h3 className="text-sm font-medium opacity-90">Total Content</h3>
            <p className="text-3xl font-bold mt-2" data-testid="total-content-count">{analytics.total_content_pieces || 0}</p>
            <p className="text-sm opacity-75 mt-1">+{Math.floor(Math.random() * 5) + 1} this week</p>
          </div>
          <div className="analytics-card-2 text-white p-6 rounded-2xl shadow-lg card-hover">
            <h3 className="text-sm font-medium opacity-90">Words Generated</h3>
            <p className="text-3xl font-bold mt-2" data-testid="total-words-count">{(analytics.total_words_generated || 0).toLocaleString()}</p>
            <p className="text-sm opacity-75 mt-1">≈ ${((analytics.total_words_generated || 0) * 0.02).toFixed(0)} saved</p>
          </div>
          <div className="analytics-card-3 text-white p-6 rounded-2xl shadow-lg card-hover">
            <h3 className="text-sm font-medium opacity-90">Credits Remaining</h3>
            <p className="text-3xl font-bold mt-2" data-testid="remaining-credits">{analytics.current_credits || 0}</p>
            <p className="text-sm opacity-75 mt-1">
              {analytics.current_credits <= 5 ? '⚠️ Running low' : '✅ Looking good'}
            </p>
          </div>
          <div className="analytics-card-4 text-white p-6 rounded-2xl shadow-lg card-hover">
            <h3 className="text-sm font-medium opacity-90">Current Plan</h3>
            <p className="text-2xl font-bold mt-2 capitalize" data-testid="current-plan">{analytics.subscription_tier}</p>
            <p className="text-sm opacity-75 mt-1">
              {analytics.subscription_tier === 'free' ? 'Upgrade for more' : 'Premium active'}
            </p>
          </div>
        </div>
      )}

      {/* Feature Showcase */}
      <div className="space-y-6">
        {/* Star Feature */}
        {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'enterprise') ? (
        <div
          onClick={() => { onNavigate('agents'); setTimeout(() => document.querySelector('button[class*="Pantheon"]')?.click(), 300); }}
          className="group relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/5 via-gray-900/90 to-purple-500/5 p-6 cursor-pointer hover:border-amber-400/50 transition-all"
          data-testid="feature-war-room"
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-400/80 font-semibold">Flagship Feature</span>
              <h3 className="text-xl font-bold text-white mt-1">The Living War Room</h3>
              <p className="text-gray-400 text-sm mt-1 max-w-lg">12 AI gods debate your business strategy in real-time. Throw in any challenge and watch them argue, cite data, and deliver a verdict.</p>
            </div>
            <span className="text-4xl opacity-60 group-hover:opacity-100 transition-opacity">⚔️</span>
          </div>
        </div>
        ) : (
        <div
          className="group relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-r from-amber-500/5 via-gray-900/90 to-purple-500/5 p-6"
          data-testid="feature-war-room-locked"
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center z-10 rounded-2xl">
            <button onClick={() => handleUpgradePlan('pro')} className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold px-4 py-2 rounded-full hover:scale-105 transition-transform cursor-pointer">Upgrade to Pro to Unlock</button>
          </div>
          <div className="flex items-center justify-between opacity-60">
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-400/80 font-semibold">Flagship Feature</span>
              <h3 className="text-xl font-bold text-white mt-1">The Living War Room</h3>
              <p className="text-gray-400 text-sm mt-1 max-w-lg">12 AI gods debate your business strategy in real-time.</p>
            </div>
            <span className="text-4xl">⚔️</span>
          </div>
        </div>
        )}

        {/* Strategy & Analysis */}
        <div>
          <h2 className="text-sm uppercase tracking-widest text-gray-500 font-semibold mb-3 pl-1">Strategy & Analysis</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'agents', icon: '🏛️', name: 'Hire an Agent', desc: 'Athena, Hermes, Apollo, Dionysus — each god has unique AI superpowers', pro: true },
              { id: 'artemis', icon: '🏹', name: 'Artemis', desc: 'Deep strategic analysis powered by AI hunter intelligence', pro: true },
              { id: 'website-analyzer', icon: '🔍', name: 'Website Analyzer', desc: 'Full breakdown of any website — SEO, UX, content, performance', pro: true },
              { id: 'competitor-analyzer', icon: '🥊', name: 'Beat Competition', desc: 'Analyze competitors and get a battle plan to outrank them', pro: true },
              { id: 'seo', icon: '🎯', name: 'SEO Optimizer', desc: 'Optimize any content for search engines with AI recommendations', pro: true },
              { id: 'viral', icon: '🔥', name: 'Viral Analyzer', desc: 'Predict viral potential and get suggestions to boost shareability', pro: true },
            ].map(f => {
              const isLocked = f.pro && user?.subscription_tier !== 'pro' && user?.subscription_tier !== 'enterprise';
              return (
              <div
                key={f.id}
                onClick={() => isLocked ? handleUpgradePlan('pro') : onNavigate(f.id)}
                className={`group relative flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${isLocked ? 'border-gray-800/50 bg-gray-900/30 opacity-70' : 'border-gray-800 bg-gray-900/50 hover:border-purple-500/40 hover:bg-gray-800/50'}`}
                data-testid={`feature-${f.id}`}
              >
                {isLocked && <div className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-700/80 text-gray-400 font-medium">PRO+</div>}
                <span className="text-2xl mt-0.5">{f.icon}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-white group-hover:text-purple-300 transition-colors">{f.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            )})}
          </div>
        </div>

        {/* Content Creation */}
        <div>
          <h2 className="text-sm uppercase tracking-widest text-gray-500 font-semibold mb-3 pl-1">Content Creation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'generate', icon: '🚀', name: 'Generate Content', desc: 'Blog posts, social media, emails — AI writes it in your brand voice' },
              { id: 'multiply', icon: '⚡', name: 'Content Multiplier', desc: 'Turn one piece of content into 10 formats instantly', pro: true },
              { id: 'content-brief', icon: '💡', name: 'Content Brief', desc: 'Generate detailed briefs that guide perfect content creation', pro: true },
              { id: 'content-calendar', icon: '📅', name: 'Content Calendar', desc: 'AI-planned publishing schedule based on your niche and goals', pro: true },
              { id: 'content-explosion', icon: '🎨', name: 'Multi-Format', desc: 'Transform content across formats — blog to video script, email to thread', pro: true },
              { id: 'quora', icon: '🏆', name: 'Quora Goldmine', desc: 'Find high-traffic Quora questions and generate expert answers' },
            ].map(f => {
              const isLocked = f.pro && user?.subscription_tier !== 'pro' && user?.subscription_tier !== 'enterprise';
              return (
              <div
                key={f.id}
                onClick={() => isLocked ? handleUpgradePlan('pro') : onNavigate(f.id)}
                className={`group relative flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${isLocked ? 'border-gray-800/50 bg-gray-900/30 opacity-70' : 'border-gray-800 bg-gray-900/50 hover:border-cyan-500/40 hover:bg-gray-800/50'}`}
                data-testid={`feature-${f.id}`}
              >
                {isLocked && <div className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-700/80 text-gray-400 font-medium">PRO+</div>}
                <span className="text-2xl mt-0.5">{f.icon}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-white group-hover:text-cyan-300 transition-colors">{f.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            )})}
          </div>
        </div>

        {/* Intelligence & Insights */}
        <div>
          <h2 className="text-sm uppercase tracking-widest text-gray-500 font-semibold mb-3 pl-1">Intelligence & Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { id: 'trending', icon: '📈', name: 'Trending Topics', desc: 'Real-time trending topics in your industry to create timely content', pro: true },
              { id: 'predictor', icon: '🔮', name: 'Performance Predictor', desc: 'AI predicts how your content will perform before you publish', pro: true },
              { id: 'sentiment-predictor', icon: '😊', name: 'Sentiment Predictor', desc: 'Gauge how your audience will react to content emotionally', pro: true },
              { id: 'content-decay', icon: '⚠️', name: 'Content Health', desc: 'Monitor content freshness and get alerts when it needs updating', pro: true },
              { id: 'roi-calculator', icon: '💰', name: 'ROI Calculator', desc: 'Calculate the real dollar value of your content marketing efforts', pro: true },
              { id: 'analytics', icon: '📊', name: 'Analytics', desc: 'Deep performance analytics across all your generated content', pro: true },
            ].map(f => {
              const isLocked = f.pro && user?.subscription_tier !== 'pro' && user?.subscription_tier !== 'enterprise';
              return (
              <div
                key={f.id}
                onClick={() => isLocked ? handleUpgradePlan('pro') : onNavigate(f.id)}
                className={`group relative flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${isLocked ? 'border-gray-800/50 bg-gray-900/30 opacity-70' : 'border-gray-800 bg-gray-900/50 hover:border-green-500/40 hover:bg-gray-800/50'}`}
                data-testid={`feature-${f.id}`}
              >
                {isLocked && <div className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded-full bg-gray-700/80 text-gray-400 font-medium">PRO+</div>}
                <span className="text-2xl mt-0.5">{f.icon}</span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-white group-hover:text-green-300 transition-colors">{f.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            )})}
          </div>
        </div>

        {/* Enterprise Power Tools */}
        {user?.subscription_tier === 'enterprise' && (
          <div>
            <h2 className="text-sm uppercase tracking-widest text-amber-500/70 font-semibold mb-3 pl-1">Enterprise Power Tools</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { id: 'autopilot', icon: '🤖', name: 'Auto-Pilot', desc: 'Set it and forget it — AI runs your content on autopilot' },
                { id: 'bulk', icon: '⚡', name: 'Bulk Generator', desc: 'Generate dozens of content pieces in a single batch' },
                { id: 'zapier', icon: '🔗', name: 'Zapier Integration', desc: 'Auto-distribute content to 6,000+ apps' },
                { id: 'ads-dashboard', icon: '📢', name: 'Ads Dashboard', desc: 'Track and optimize your advertising performance' },
                { id: 'api-keys', icon: '🔐', name: 'API Keys', desc: 'Integrate iSupplyAI directly into your workflow' },
              ].map(f => (
                <div
                  key={f.id}
                  onClick={() => onNavigate(f.id)}
                  className="group flex items-start gap-3 p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 hover:border-amber-400/40 hover:bg-amber-500/10 cursor-pointer transition-all"
                  data-testid={`feature-${f.id}`}
                >
                  <span className="text-2xl mt-0.5">{f.icon}</span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-white group-hover:text-amber-300 transition-colors">{f.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referrals & Resources */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div
            onClick={() => onNavigate('referrals')}
            className="group flex items-center gap-4 p-4 rounded-xl border border-gray-800 bg-gray-900/50 hover:border-pink-500/40 cursor-pointer transition-all"
            data-testid="feature-referrals"
          >
            <span className="text-2xl">🎁</span>
            <div>
              <h3 className="text-sm font-semibold text-white group-hover:text-pink-300 transition-colors">Referral Program</h3>
              <p className="text-xs text-gray-500">Earn credits by inviting others to the platform</p>
            </div>
          </div>
          <a
            href="/free-downloads"
            className="group flex items-center gap-4 p-4 rounded-xl border border-gray-800 bg-gray-900/50 hover:border-cyan-500/40 cursor-pointer transition-all"
            data-testid="feature-resources"
          >
            <span className="text-2xl">📚</span>
            <div>
              <h3 className="text-sm font-semibold text-white group-hover:text-cyan-300 transition-colors">Free Resources</h3>
              <p className="text-xs text-gray-500">Downloadable guides, templates, and frameworks</p>
            </div>
          </a>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-800 shadow-lg p-8">
        <h2 className="text-2xl font-bold text-white mb-6">Subscription Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(subscriptionPlans).map(([planKey, plan]) => (
            <div
              key={planKey}
              className={`border-2 rounded-2xl p-6 card-hover ${
                user?.subscription_tier === planKey
                  ? 'border-purple-500 bg-purple-500/10'
                  : 'border-gray-700 bg-gray-800/50'
              }`}
            >
              <h3 className="text-xl font-bold capitalize mb-2 text-white">{planKey}</h3>
              <p className="text-3xl font-bold text-purple-400 mb-4">
                ${plan.price}<span className="text-sm text-gray-400">/month</span>
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center text-sm text-gray-300">
                  <span className="text-green-400 mr-2">✓</span>
                  {plan.credits_per_month} credits/month
                </li>
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-gray-300">
                    <span className="text-green-400 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
              {user?.subscription_tier !== planKey && planKey !== 'free' && (
                <button
                  onClick={() => handleUpgradePlan(planKey)}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white py-2 rounded-lg hover:shadow-lg hover:shadow-purple-500/25 transition duration-200"
                  data-testid={`upgrade-to-${planKey}-button`}
                >
                  💳 Upgrade to {planKey}
                </button>
              )}
              {user?.subscription_tier === planKey && (
                <div className="w-full bg-green-500/20 text-green-400 py-2 rounded-lg text-center font-medium border border-green-500/30">
                  ✅ Current Plan
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent Content */}
      <div className="bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-800 shadow-lg p-8">
        <h2 className="text-2xl font-bold text-white mb-6">Recent Content</h2>
        <div className="space-y-4" data-testid="recent-content-list">
          {contentHistory.length > 0 ? (
            contentHistory.map((content, index) => (
              <div key={content.id} className="border border-gray-700 bg-gray-800/50 rounded-lg p-4 hover:border-purple-500/50 hover:bg-gray-800 transition duration-200 card-hover cursor-pointer" onClick={() => setSelectedViewContent(content)}>
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-lg text-white">{content.title}</h3>
                  <span className="text-sm text-gray-400 capitalize bg-gray-700 px-2 py-1 rounded">
                    {content.content_type.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-gray-400 text-sm mb-2">
                  {content.word_count} words • {content.credits_used} credits • 
                  Estimated value: ${(content.word_count * 0.02).toFixed(2)}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(content.created_at).toLocaleDateString()}
                </p>
                <button className="mt-2 text-purple-400 text-sm hover:text-purple-300 font-medium">
                  👁️ View Content
                </button>
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-gray-400 text-3xl">📝</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-300 mb-2">No content yet</h3>
              <p className="text-gray-500 mb-4">Start creating amazing content with AI!</p>
              <button className="bg-gradient-to-r from-purple-600 to-cyan-500 text-white px-6 py-2 rounded-lg hover:shadow-lg hover:shadow-purple-500/25 transition duration-200">
                Generate Your First Content
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        planType={selectedPlan?.type}
        planDetails={selectedPlan}
        user={user}
      />

      {/* Content View Modal */}
      <ContentViewModal
        isOpen={!!selectedViewContent}
        onClose={() => setSelectedViewContent(null)}
        content={selectedViewContent}
      />
    </div>
  );
};

// Forgot Password Component
const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/password-reset/request`, {
        email
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center px-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-purple-600/20 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-600/20 rounded-full blur-[150px]" />
        </div>
        <div className="relative bg-gray-900/80 backdrop-blur-xl p-8 rounded-2xl border border-gray-800 shadow-2xl max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-500/20 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
          <p className="text-gray-400 mb-6">
            If an account exists with {email}, you'll receive a password reset link shortly.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            The link will expire in 15 minutes for security reasons.
          </p>
          <button
            onClick={() => window.location.hash = ''}
            className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white py-3 rounded-lg hover:shadow-lg hover:shadow-purple-500/25 transition duration-200 font-medium"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030305] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-purple-600/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-600/20 rounded-full blur-[150px]" />
      </div>
      <div className="relative bg-gray-900 backdrop-blur-xl p-8 rounded-2xl border border-gray-800 shadow-2xl max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" 
            alt="iSupplyAI Logo" 
            className="w-16 h-16 object-contain mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-white">Forgot Password?</h2>
          <p className="text-gray-400 mt-2">Enter your email to receive a reset link</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
              placeholder="Enter your email"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white py-3 rounded-xl hover:shadow-lg hover:shadow-purple-500/25 transition duration-200 font-medium disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <p className="text-center text-gray-400 mt-6">
          Remember your password?{' '}
          <button
            onClick={() => window.location.hash = ''}
            className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
          >
            Back to Login
          </button>
        </p>
      </div>
    </div>
  );
};

// Reset Password Component
const ResetPassword = () => {
  const [formData, setFormData] = useState({ newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    // Get token from URL
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/password-reset/confirm`, {
        token,
        new_password: formData.newPassword
      });
      
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Password Reset Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Your password has been updated. You can now log in with your new password.
          </p>
          <button
            onClick={() => window.location.hash = ''}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition duration-200 font-medium"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/2nb4d6kg_ISupplyAILogo%232.png" 
            alt="iSupplyAI Logo" 
            className="w-16 h-16 object-contain mx-auto mb-4"
          />
          <h2 className="text-3xl font-bold text-gray-800">Reset Password</h2>
          <p className="text-gray-600 mt-2">Enter your new password</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
            <input
              type="password"
              required
              value={formData.newPassword}
              onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter new password (min 8 characters)"
              minLength="8"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
            <input
              type="password"
              required
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Confirm new password"
              minLength="8"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !token}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition duration-200 font-medium disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const AuthPage = ({ defaultLogin = false }) => {
  const [isLogin, setIsLogin] = useState(defaultLogin);

  return isLogin ? (
    <Login onSwitchToRegister={() => setIsLogin(false)} />
  ) : (
    <Register onSwitchToLogin={() => setIsLogin(true)} />
  );
};

// Advanced Features Components

// Referral Program Component
const ReferralProgram = () => {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats`);
      if (!response.data.referral_code) {
        // Generate code if doesn't exist
        const genResponse = await axios.post(`${API}/referral/generate-code`);
        setReferralData(genResponse.data);
      } else {
        setReferralData(response.data);
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    const referralLink = `${window.location.origin}?ref=${referralData.referral_code}`;
    const success = await copyToClipboard(referralLink);
    alert(success ? '✅ Referral link copied to clipboard!' : '❌ Copy failed');
  };

  if (loading) {
    return <div className="text-center py-8">Loading referral data...</div>;
  }

  const referralLink = `${window.location.origin}?ref=${referralData?.referral_code}`;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">🎁 Referral Program</h1>
        <p className="text-gray-600 mb-6">Earn bonus credits by inviting friends!</p>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6 shadow-lg">
            <div className="text-4xl font-bold mb-2">{referralData?.referrals_count || 0}</div>
            <div className="text-purple-100">Total Referrals</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 shadow-lg">
            <div className="text-4xl font-bold mb-2">{referralData?.rewards_earned || 0}</div>
            <div className="text-green-100">Credits Earned</div>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg">
            <div className="text-4xl font-bold mb-2">25</div>
            <div className="text-blue-100">Credits per Referral</div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Your Referral Link</h2>
          <div className="flex gap-2">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50"
            />
            <button
              onClick={copyReferralLink}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              📋 Copy
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Share this link with friends. When they sign up, you both get 25 bonus credits!
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">How It Works</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold">1</span>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Share Your Link</h3>
                <p className="text-gray-600">Share your unique referral link with friends, family, or on social media.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold">2</span>
              </div>
              <div>
                <h3 className="font-semibold mb-1">They Sign Up</h3>
                <p className="text-gray-600">When someone signs up using your link, they get 10 free credits plus 25 bonus credits.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <div>
                <h3 className="font-semibold mb-1">Earn Rewards</h3>
                <p className="text-gray-600">You receive 25 credits added to your account. No limits on referrals!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// API Keys Management Component (Enterprise Only)
const APIKeysManagement = () => {
  const { user } = React.useContext(AuthContext);
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/api-keys`);
      setApiKeys(response.data.api_keys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!keyName.trim()) {
      alert('Please enter a name for the API key');
      return;
    }

    try {
      const response = await axios.post(`${API}/enterprise/api-keys/generate`, {
        name: keyName,
        permissions: ['read', 'write']
      });
      
      alert(`API Key Created!\n\n${response.data.api_key}\n\nSave this key securely - you won't be able to see it again!`);
      setKeyName('');
      setShowCreateModal(false);
      fetchApiKeys();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating API key');
    }
  };

  const revokeKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key?')) return;

    try {
      await axios.delete(`${API}/enterprise/api-keys/${keyId}`);
      fetchApiKeys();
    } catch (error) {
      alert('Error revoking API key');
    }
  };

  if (user?.subscription_tier !== 'enterprise') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg p-12 border-2 border-blue-200">
            <div className="text-6xl mb-4">🔐</div>
            <h1 className="text-3xl font-bold mb-4 text-gray-900">API Access - Enterprise Only</h1>
            <p className="text-gray-700 mb-6 text-lg">
              Get full programmatic access to all iSupplyAI features via REST API.
            </p>
            <div className="bg-white rounded-lg p-6 mb-6">
              <p className="text-gray-600 mb-4">🔒 This feature requires an Enterprise subscription</p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li>✅ Full REST API access</li>
                <li>✅ Generate unlimited API keys</li>
                <li>✅ Integrate with your systems</li>
                <li>✅ Build custom applications</li>
              </ul>
            </div>
            <button
              onClick={() => window.location.href = '#pricing'}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition duration-300"
            >
              ⬆️ Upgrade to Enterprise
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">🔐 API Keys</h1>
            <p className="text-gray-600">Manage your API keys for programmatic access</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            + Create API Key
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading API keys...</div>
        ) : apiKeys.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="text-4xl mb-4">🔑</div>
            <h3 className="text-xl font-semibold mb-2">No API Keys Yet</h3>
            <p className="text-gray-600">Create your first API key to start using the API.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map((key) => (
              <div key={key.id} className="bg-white rounded-xl shadow-md p-6 flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-lg mb-1">{key.name}</h3>
                  <p className="text-gray-600 font-mono text-sm">{key.key}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Created: {new Date(key.created_at).toLocaleDateString()} • Used {key.usage_count} times
                  </p>
                </div>
                <button
                  onClick={() => revokeKey(key.id)}
                  className="px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}

        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Create API Key</h2>
              <label className="block mb-4">
                <span className="text-gray-700 font-medium mb-2 block">Key Name</span>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g., Production API, Testing Key"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                />
              </label>
              <div className="flex gap-3">
                <button
                  onClick={createApiKey}
                  className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Create Key
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Content Multiplication Engine Component


// ==================== WEBSITE ANALYZER ====================
const WebsiteAnalyzer = () => {
  const { user } = React.useContext(AuthContext);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchSavedAnalyses();
  }, []);

  // Simulate progress during analysis
  useEffect(() => {
    if (analyzing) {
      setProgress(0);
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 3000);
      return () => clearInterval(interval);
    } else {
      setProgress(0);
    }
  }, [analyzing]);

  const fetchSavedAnalyses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/website/analyses`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSavedAnalyses(result.analyses || []);
      }
    } catch (err) {
      console.error('Failed to fetch analyses:', err);
    }
  };

  const handleAnalyze = async () => {
    if (!websiteUrl) {
      setError('Please enter a website URL');
      return;
    }

    setAnalyzing(true);
    setError(null);
    setActiveTab('overview');

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Please login first to use Website Analyzer');
        setAnalyzing(false);
        return;
      }
      
      const response = await fetch(
        `${BACKEND_URL}/api/website/analyze`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            website_url: websiteUrl,
            industry: industry || null
          })
        }
      );

      if (response.status === 403) {
        setError('Website Analyzer is available for Pro and Enterprise tiers only. Please upgrade your plan.');
        setAnalyzing(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail;
        const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : 'Failed to analyze website';
        throw new Error(msg);
      }

      const result = await response.json();
      setProgress(100);
      setTimeout(() => {
        setAnalysis(result.analysis);
        
        // Save analysis ID to localStorage for Content Multiplier
        if (result.analysis && result.analysis.analysis_id) {
          localStorage.setItem('latest_analysis_id', result.analysis.analysis_id);
        }
        
        fetchSavedAnalyses();
      }, 500);
    } catch (err) {
      const msg = err?.message || (typeof err === 'string' ? err : 'Failed to analyze website');
      setError(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  const loadAnalysis = async (analysisId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/website/analysis/${analysisId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setAnalysis(result.analysis);
        setWebsiteUrl(result.analysis.website_url);
      }
    } catch (err) {
      console.error('Failed to load analysis:', err);
    }
  };

  // Calculate SEO score color
  const getSEOScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSEOScoreGradient = (score) => {
    if (score >= 80) return 'from-green-500 to-green-600';
    if (score >= 60) return 'from-yellow-500 to-orange-500';
    return 'from-red-500 to-red-600';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8 fade-in-up">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-bold gradient-text">🔍 Website Analyzer</h1>
            <p className="mt-2 text-gray-600 text-lg">
              Discover your brand voice, content gaps, and strategic opportunities
            </p>
          </div>
          <div className="badge-gradient">
            Pro Feature
          </div>
        </div>
      </div>

      {/* Input Form */}
      <div className="glass-effect p-8 rounded-2xl shadow-vibrant mb-8 fade-in-up delay-100">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🌐</span> Website URL *
            </label>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-smooth"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🏢</span> Industry (Optional)
            </label>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g., SaaS, E-commerce, Marketing"
              className="w-full px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-smooth"
            />
          </div>
        </div>

        <button
          onClick={handleAnalyze}
          disabled={analyzing}
          className="w-full mt-6 btn-primary-enhanced text-white px-8 py-4 rounded-xl text-lg font-bold disabled:opacity-50"
        >
          {analyzing ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Analyzing... {progress}%
            </span>
          ) : '🚀 Analyze Website'}
        </button>

        {/* Progress Bar */}
        {analyzing && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Analysis in progress...</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="animated-gradient h-3 transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-2 text-center">
              {progress < 30 && '🔍 Scanning website content...'}
              {progress >= 30 && progress < 60 && '🎭 Analyzing brand voice...'}
              {progress >= 60 && progress < 90 && '📈 Evaluating SEO performance...'}
              {progress >= 90 && '✨ Finalizing recommendations...'}
            </p>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="glass-effect bg-red-50 p-6 rounded-2xl mb-8 border-2 border-red-200 fade-in-up">
          <div className="flex items-start">
            <span className="text-3xl mr-4">⚠️</span>
            <div className="flex-1">
              <p className="text-red-800 font-semibold mb-2">{error}</p>
              {user?.subscription_tier === 'free' && (
                <button
                  onClick={() => window.location.href = '/#pricing'}
                  className="mt-3 btn-primary-enhanced text-white px-6 py-2 rounded-lg text-sm"
                >
                  ⬆️ Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && (
        <div className="space-y-6 fade-in-up delay-200">
          {/* Header Card */}
          <div className="glass-effect p-8 rounded-2xl shadow-vibrant animated-gradient relative overflow-hidden">
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">
                    ✅ Analysis Complete
                  </h2>
                  <p className="text-white/90 text-lg font-semibold">
                    {analysis.website_url}
                  </p>
                  <p className="text-white/75 text-sm mt-1">
                    📅 Analyzed on {new Date(analysis.analyzed_at).toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
                <button
                  onClick={() => {
                    localStorage.setItem('latest_analysis_id', analysis.analysis_id);
                    window.location.hash = '#multiply';
                  }}
                  className="glass-effect px-6 py-3 rounded-xl font-bold text-white hover:scale-105 transition-smooth"
                >
                  🚀 Use in Content Multiplier
                </button>
              </div>
            </div>
          </div>

          {/* Tabbed Interface */}
          <div className="glass-effect rounded-2xl shadow-vibrant overflow-hidden">
            {/* Tab Headers */}
            <div className="flex border-b border-gray-200 bg-white/50 overflow-x-auto">
              {[
                { id: 'overview', label: '📊 Overview', icon: '📊' },
                { id: 'brand', label: '🎭 Brand Voice', icon: '🎭' },
                { id: 'seo', label: '📈 SEO Analysis', icon: '📈' },
                { id: 'content', label: '📝 Content Strategy', icon: '📝' },
                { id: 'platforms', label: '🎯 Platforms', icon: '🎯' },
                { id: 'advertising', label: '📢 Advertising', icon: '📢' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 min-w-max px-6 py-4 font-semibold transition-smooth ${
                    activeTab === tab.id
                      ? 'bg-white text-purple-600 border-b-4 border-purple-600'
                      : 'text-gray-600 hover:bg-white/70 hover:text-purple-600'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label.replace(/.*\s/, '')}</span>
                  <span className="sm:hidden">{tab.icon}</span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-8 bg-white">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6 fade-in-up">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Overview & Key Metrics</h3>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    {/* SEO Score Card */}
                    <div className="card-hover glass-effect p-6 rounded-xl text-center">
                      <p className="text-sm text-gray-600 mb-3 font-semibold">SEO Score</p>
                      <div className="relative w-32 h-32 mx-auto mb-4">
                        <svg className="transform -rotate-90 w-32 h-32">
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="transparent"
                            className="text-gray-200"
                          />
                          <circle
                            cx="64"
                            cy="64"
                            r="56"
                            stroke="currentColor"
                            strokeWidth="8"
                            fill="transparent"
                            strokeDasharray={`${2 * Math.PI * 56}`}
                            strokeDashoffset={`${2 * Math.PI * 56 * (1 - (analysis.seo_analysis?.seo_score || 0) / 100)}`}
                            className={`${getSEOScoreColor(analysis.seo_analysis?.seo_score || 0)} transition-all duration-1000`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className={`text-4xl font-bold ${getSEOScoreColor(analysis.seo_analysis?.seo_score || 0)}`}>
                            {analysis.seo_analysis?.seo_score || 0}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Out of 100</p>
                    </div>

                    {/* Brand Voice Card */}
                    <div className="card-hover glass-effect p-6 rounded-xl">
                      <p className="text-sm text-gray-600 mb-3 font-semibold">Brand Voice</p>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-500">Primary Tone</p>
                          <p className="text-lg font-bold gradient-text capitalize">
                            {analysis.brand_voice?.primary_tone || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Messaging Style</p>
                          <p className="text-sm font-semibold text-gray-700">
                            {analysis.brand_voice?.messaging_style || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats Card */}
                    <div className="card-hover glass-effect p-6 rounded-xl">
                      <p className="text-sm text-gray-600 mb-3 font-semibold">Quick Stats</p>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Content Gaps</span>
                          <span className="badge-gradient text-xs px-3 py-1">
                            {(analysis.content_gaps?.content_opportunities?.length || 0) + (analysis.content_gaps?.missing_content_types?.length || 0)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Platforms</span>
                          <span className="badge-gradient text-xs px-3 py-1">
                            {analysis.platform_recommendations?.length || 0}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-500">Quick Wins</span>
                          <span className="badge-gradient text-xs px-3 py-1">
                            {analysis.content_strategy?.quick_wins?.length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content Gaps Preview */}
                  <div className="card-hover glass-effect p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">🎯 Top Content Opportunities</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {analysis.content_gaps?.content_opportunities?.slice(0, 4).map((opportunity, idx) => (
                        <div key={idx} className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                          <p className="font-semibold text-purple-700 mb-1">Opportunity {idx + 1}</p>
                          <p className="text-sm text-gray-600">{opportunity}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Brand Voice Tab */}
              {activeTab === 'brand' && (
                <div className="space-y-6 fade-in-up">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">🎭 Brand Voice Analysis</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Primary Characteristics */}
                    <div className="card-hover glass-effect p-6 rounded-xl">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Brand Characteristics</h4>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Primary Tone</p>
                          <p className="text-xl font-bold gradient-text capitalize">
                            {analysis.brand_voice?.primary_tone || 'Not detected'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Messaging Style</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {analysis.brand_voice?.messaging_style || 'Not detected'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Target Audience</p>
                          <p className="text-base text-gray-700">
                            {analysis.brand_voice?.target_audience || 'Not specified'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Brand Voice Personality */}
                    <div className="card-hover glass-effect p-6 rounded-xl">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Voice Personality</h4>
                      <div className="space-y-3">
                        {[
                          { trait: 'Professional', value: 75 },
                          { trait: 'Friendly', value: 60 },
                          { trait: 'Authoritative', value: 80 },
                          { trait: 'Casual', value: 45 },
                          { trait: 'Innovative', value: 70 }
                        ].map((item, idx) => (
                            <div key={idx}>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">{item.trait}</span>
                                <span className="font-semibold text-purple-600">{item.value}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${item.value}%` }}
                                ></div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Brand Keywords */}
                  <div className="card-hover glass-effect p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">🔑 Brand Keywords & Themes</h4>
                    <div className="flex flex-wrap gap-3">
                      {analysis.brand_voice?.brand_keywords?.map((keyword, idx) => (
                        <span
                          key={idx}
                          className="px-4 py-2 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-full text-sm font-semibold hover:scale-105 transition-smooth cursor-pointer"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Voice Recommendations */}
                  <div className="card-hover glass-effect p-6 rounded-xl bg-gradient-to-r from-purple-50 to-blue-50">
                    <h4 className="text-lg font-bold text-purple-900 mb-4">💡 Brand Voice Recommendations</h4>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 mt-1">✓</span>
                        <span className="text-gray-700">
                          Maintain your <strong>{analysis.brand_voice?.primary_tone}</strong> tone consistently across all platforms
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 mt-1">✓</span>
                        <span className="text-gray-700">
                          Use brand keywords like <strong>"{analysis.brand_voice?.brand_keywords?.[0]}"</strong> in your content
                        </span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-green-500 mr-3 mt-1">✓</span>
                        <span className="text-gray-700">
                          Your {analysis.brand_voice?.messaging_style} style resonates well with your target audience
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              )}

              {/* SEO Analysis Tab */}
              {activeTab === 'seo' && (
                <div className="space-y-6 fade-in-up">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">📈 SEO Performance Analysis</h3>
                  
                  {/* SEO Score Hero Card */}
                  <div className="card-hover glass-effect p-8 rounded-xl text-center bg-gradient-to-br from-purple-50 to-blue-50">
                    <p className="text-sm text-gray-600 mb-4 font-semibold uppercase tracking-wide">Overall SEO Score</p>
                    <div className="relative w-48 h-48 mx-auto mb-6">
                      <svg className="transform -rotate-90 w-48 h-48">
                        <circle
                          cx="96"
                          cy="96"
                          r="84"
                          stroke="currentColor"
                          strokeWidth="12"
                          fill="transparent"
                          className="text-gray-200"
                        />
                        <circle
                          cx="96"
                          cy="96"
                          r="84"
                          stroke="currentColor"
                          strokeWidth="12"
                          fill="transparent"
                          strokeDasharray={`${2 * Math.PI * 84}`}
                          strokeDashoffset={`${2 * Math.PI * 84 * (1 - (analysis.seo_analysis?.seo_score || 0) / 100)}`}
                          className={`${getSEOScoreColor(analysis.seo_analysis?.seo_score || 0)} transition-all duration-1000`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <span className={`text-6xl font-bold ${getSEOScoreColor(analysis.seo_analysis?.seo_score || 0)}`}>
                            {analysis.seo_analysis?.seo_score || 0}
                          </span>
                          <p className="text-sm text-gray-500 mt-2">out of 100</p>
                        </div>
                      </div>
                    </div>
                    <p className={`text-xl font-bold ${getSEOScoreColor(analysis.seo_analysis?.seo_score || 0)}`}>
                      {analysis.seo_analysis?.seo_score >= 80 ? 'Excellent' : analysis.seo_analysis?.seo_score >= 60 ? 'Good' : 'Needs Improvement'}
                    </p>
                  </div>

                  {/* Strengths & Weaknesses */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Strengths */}
                    <div className="card-hover glass-effect p-6 rounded-xl border-2 border-green-200">
                      <h4 className="text-lg font-bold text-green-700 mb-4 flex items-center">
                        <span className="text-2xl mr-2">✅</span> SEO Strengths
                      </h4>
                      <ul className="space-y-3">
                        {analysis.seo_analysis?.strengths?.map((strength, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-green-500 mr-3 mt-1">●</span>
                            <span className="text-gray-700">{typeof strength === 'object' ? (strength?.text || strength?.description || JSON.stringify(strength)) : strength}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Weaknesses */}
                    <div className="card-hover glass-effect p-6 rounded-xl border-2 border-orange-200">
                      <h4 className="text-lg font-bold text-orange-700 mb-4 flex items-center">
                        <span className="text-2xl mr-2">⚠️</span> Areas for Improvement
                      </h4>
                      <ul className="space-y-3">
                        {analysis.seo_analysis?.weaknesses?.map((weakness, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-orange-500 mr-3 mt-1">●</span>
                            <span className="text-gray-700">{typeof weakness === 'object' ? (weakness?.text || weakness?.description || JSON.stringify(weakness)) : weakness}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* SEO Recommendations */}
                  <div className="card-hover glass-effect p-6 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">🎯 Recommended Actions</h4>
                    <div className="space-y-4">
                      {analysis.seo_analysis?.recommendations?.map((rec, idx) => (
                        <div key={idx} className="p-4 bg-white rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900">{rec.action || `Action ${idx + 1}`}</p>
                            <span className="badge-gradient text-xs">
                              {rec.priority || 'Medium'} Priority
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{rec.description || rec}</p>
                        </div>
                      )) || (
                        <p className="text-gray-600">Continue maintaining your current SEO strengths.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Content Strategy Tab */}
              {activeTab === 'content' && (
                <div className="space-y-6 fade-in-up">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">📝 Content Strategy & Recommendations</h3>
                  
                  {/* Priority Content */}
                  <div className="card-hover glass-effect p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">🎯 Priority Content Types</h4>
                    <div className="space-y-4">
                      {analysis.content_strategy?.priority_content?.map((content, idx) => (
                        <div key={idx} className="p-5 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-l-4 border-purple-500">
                          <div className="flex items-start justify-between mb-2">
                            <p className="font-bold text-lg text-purple-900">{content.type}</p>
                            <span className="badge-gradient text-xs">High Impact</span>
                          </div>
                          <p className="text-gray-700 mb-3">{content.reason}</p>
                          <p className="text-sm text-green-600 font-semibold flex items-center">
                            <span className="mr-2">💡</span> {content.estimated_impact}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quick Wins */}
                  <div className="card-hover glass-effect p-6 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50">
                    <h4 className="text-lg font-bold text-green-900 mb-4">⚡ Quick Wins (Easy to Implement)</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {analysis.content_strategy?.quick_wins?.map((win, idx) => (
                        <div key={idx} className="p-4 bg-white rounded-lg border border-green-200 hover:shadow-md transition-smooth">
                          <div className="flex items-start">
                            <span className="text-green-500 text-xl mr-3">✓</span>
                            <p className="text-gray-700 font-medium">{win}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Content Gaps */}
                  <div className="card-hover glass-effect p-6 rounded-xl">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">🔍 Content Gaps & Opportunities</h4>
                    <div className="space-y-4">
                      {/* Missing Content Types */}
                      {analysis.content_gaps?.missing_content_types?.length > 0 && (
                        <div className="p-5 bg-white rounded-xl border-2 border-red-200">
                          <p className="font-bold text-red-700 mb-2 text-lg">Missing Content Types</p>
                          <div className="flex flex-wrap gap-2">
                            {analysis.content_gaps.missing_content_types.map((type, idx) => (
                              <span key={idx} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold capitalize">
                                {type}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Content Opportunities */}
                      {analysis.content_gaps?.content_opportunities?.map((opportunity, idx) => (
                        <div key={idx} className="p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-purple-300 transition-smooth">
                          <p className="font-bold text-purple-700 mb-2 text-lg">Opportunity {idx + 1}</p>
                          <p className="text-gray-700 mb-3">{opportunity}</p>
                        </div>
                      ))}
                      
                      {/* Keyword Opportunities */}
                      {analysis.content_gaps?.keyword_opportunities?.length > 0 && (
                        <div className="p-5 bg-white rounded-xl border-2 border-blue-200">
                          <p className="font-bold text-blue-700 mb-2 text-lg">Keyword Opportunities</p>
                          <div className="flex flex-wrap gap-2">
                            {analysis.content_gaps.keyword_opportunities.map((keyword, idx) => (
                              <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Platforms Tab */}
              {activeTab === 'platforms' && (
                <div className="space-y-6 fade-in-up">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">🎯 Platform Recommendations</h3>
                  
                  <p className="text-gray-600 mb-6">
                    Based on your brand voice and content strategy, here are the best platforms for your content:
                  </p>

                  <div className="space-y-4">
                    {analysis.platform_recommendations?.map((platform, idx) => (
                      <div
                        key={idx}
                        className={`card-hover p-6 rounded-xl border-2 ${
                          platform.priority === 'high'
                            ? 'glass-effect border-green-400 bg-gradient-to-r from-green-50 to-emerald-50'
                            : 'glass-effect border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <span className="text-3xl mr-4">
                              {platform.platform === 'LinkedIn' && '💼'}
                              {platform.platform === 'Twitter' && '🐦'}
                              {platform.platform === 'Instagram' && '📸'}
                              {platform.platform === 'Facebook' && '👥'}
                              {platform.platform === 'TikTok' && '🎵'}
                              {platform.platform === 'YouTube' && '🎥'}
                              {!['LinkedIn', 'Twitter', 'Instagram', 'Facebook', 'TikTok', 'YouTube'].includes(platform.platform) && '🌐'}
                            </span>
                            <div>
                              <h4 className="text-xl font-bold text-gray-900">{platform.platform}</h4>
                              <p className="text-sm text-gray-600">{platform.audience_size || 'Large audience reach'}</p>
                            </div>
                          </div>
                          <span
                            className={`px-4 py-2 rounded-full text-sm font-bold ${
                              platform.priority === 'high'
                                ? 'bg-green-500 text-white'
                                : platform.priority === 'medium'
                                ? 'bg-yellow-500 text-white'
                                : 'bg-gray-400 text-white'
                            }`}
                          >
                            {platform.priority?.toUpperCase()} PRIORITY
                          </span>
                        </div>

                        <p className="text-gray-700 mb-4">{platform.reason}</p>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-semibold text-gray-600 mb-2">Recommended Content Types:</p>
                            <div className="flex flex-wrap gap-2">
                              {platform.content_types?.map((type, tidx) => (
                                <span key={tidx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-600 mb-2">Posting Frequency:</p>
                            <p className="text-base text-gray-700">{platform.frequency || '3-5 times per week'}</p>
                          </div>
                        </div>

                        {platform.priority === 'high' && (
                          <div className="mt-4 pt-4 border-t border-green-200">
                            <p className="text-sm text-green-700 font-semibold flex items-center">
                              <span className="mr-2">💡</span> Start with this platform for maximum impact
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Advertising Tab */}
              {activeTab === 'advertising' && (
                <div className="space-y-6 fade-in-up">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">📢 Advertising Strategy Recommendations</h3>
                  
                  {/* Ad Platform Recommendations */}
                  <div className="space-y-4">
                    {analysis.ad_recommendations?.recommended_platforms?.map((ad, idx) => (
                      <div key={idx} className="card-hover glass-effect p-6 rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-2xl font-bold text-blue-900">{ad.platform}</h4>
                          <span className="badge-gradient">Recommended</span>
                        </div>

                        <div className="grid md:grid-cols-3 gap-6 mb-4">
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Suggested Budget</p>
                            <p className="text-xl font-bold text-purple-600">{ad.budget}</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Expected ROI</p>
                            <p className="text-xl font-bold text-green-600">{ad.expected_ROI}</p>
                          </div>
                          <div className="bg-white p-4 rounded-lg">
                            <p className="text-sm text-gray-600 mb-1">Best For</p>
                            <p className="text-base font-semibold text-gray-700">{ad.best_for}</p>
                          </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Why This Platform:</p>
                          <p className="text-gray-600">{ad.reason || 'This platform aligns well with your brand voice and target audience.'}</p>
                        </div>

                        {ad.targeting_tips && (
                          <div className="mt-4 bg-white p-4 rounded-lg">
                            <p className="text-sm font-semibold text-gray-700 mb-2">💡 Targeting Tips:</p>
                            <ul className="space-y-1">
                              {ad.targeting_tips.map((tip, tidx) => (
                                <li key={tidx} className="text-sm text-gray-600 flex items-start">
                                  <span className="text-purple-500 mr-2">•</span>
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Ad Strategy Summary */}
                  <div className="card-hover glass-effect p-6 rounded-xl bg-gradient-to-r from-purple-50 to-pink-50">
                    <h4 className="text-lg font-bold text-purple-900 mb-4">📊 Overall Ad Strategy</h4>
                    <div className="space-y-3">
                      <p className="text-gray-700">
                        <strong>Total Recommended Budget:</strong> {
                          analysis.ad_recommendations?.recommended_platforms?.reduce((sum, ad) => {
                            const budget = parseInt(ad.budget.replace(/[^0-9]/g, ''));
                            return sum + (isNaN(budget) ? 0 : budget);
                          }, 0)
                        } per month
                      </p>
                      <p className="text-gray-700">
                        <strong>Primary Focus:</strong> {analysis.ad_recommendations?.recommended_platforms?.[0]?.platform || 'Multi-platform approach'}
                      </p>
                      <p className="text-gray-700">
                        <strong>Expected Timeline:</strong> 3-6 months to see significant ROI
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid md:grid-cols-2 gap-4 fade-in-up delay-300">
            <button
              onClick={() => {
                localStorage.setItem('latest_analysis_id', analysis.analysis_id);
                window.location.hash = '#multiply';
              }}
              className="btn-primary-enhanced text-white px-8 py-4 rounded-xl text-lg font-bold"
            >
              🚀 Use in Content Multiplier
            </button>
            <button
              onClick={() => {
                alert('Export functionality coming soon! This will generate a PDF report of your analysis.');
              }}
              className="glass-effect border-2 border-purple-300 text-purple-700 px-8 py-4 rounded-xl text-lg font-bold hover:bg-purple-50 transition-smooth"
            >
              📥 Export PDF Report
            </button>
          </div>
        </div>
      )}

      {/* Saved Analyses Sidebar */}
      {!analysis && savedAnalyses.length > 0 && (
        <div className="glass-effect p-6 rounded-2xl shadow-vibrant fade-in-up delay-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-2">📚</span> Previous Analyses
          </h3>
          <div className="space-y-3">
            {savedAnalyses.map((saved, idx) => (
              <div
                key={idx}
                onClick={() => loadAnalysis(saved.analysis_id)}
                className="p-4 bg-white rounded-xl hover:shadow-md transition-smooth cursor-pointer border-2 border-gray-200 hover:border-purple-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{saved.website_url}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(saved.analyzed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-purple-600 font-bold">→</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


const ContentMultiplier = () => {
  const { user } = React.useContext(AuthContext);
  const { workflow, startRiver } = useWorkflow();
  const [userContent, setUserContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState('');
  const [multiplying, setMultiplying] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedPlatformContent, setSelectedPlatformContent] = useState(null);
  const [batch1Complete, setBatch1Complete] = useState(false);
  const [language, setLanguage] = useState('en');
  const [useWebsiteContext, setUseWebsiteContext] = useState(false);
  const [analysisId, setAnalysisId] = useState(null);

  useEffect(() => {
    fetchUserContent();
    // Check if there's a saved analysis ID from Website Analyzer
    const savedAnalysisId = localStorage.getItem('latest_analysis_id');
    if (savedAnalysisId) {
      setAnalysisId(savedAnalysisId);
    }
  }, []);

  const fetchUserContent = async () => {
    try {
      const response = await axios.get(`${API}/content/history`);
      setUserContent(response.data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleMultiply = async (batch = 1) => {
    if (!selectedContent) {
      alert('Please select content to multiply');
      return;
    }

    setMultiplying(true);
    try {
      // Use context-aware endpoint if checkbox is enabled and analysis exists
      const endpoint = (useWebsiteContext && analysisId) 
        ? `${API}/content/multiply-context-aware`
        : `${API}/content/multiply`;
      
      const payload = (useWebsiteContext && analysisId)
        ? {
            content_id: selectedContent,
            platforms: [], // Backend will handle based on batch
            batch: batch,
            use_website_context: true,
            analysis_id: analysisId
          }
        : {
            content_id: selectedContent,
            batch: batch,
            language: language
          };
      
      const response = await axios.post(endpoint, payload, {
        timeout: 120000 // 2 minute timeout for batch processing
      });
      
      // Merge results if this is batch 2
      if (batch === 2 && results) {
        setResults({
          ...response.data,
          multiplied_content: {
            ...results.multiplied_content,
            ...response.data.multiplied_content
          },
          multiplied_count: results.multiplied_count + response.data.multiplied_count
        });
      } else {
        setResults(response.data);
      }
      
      if (batch === 1) {
        setBatch1Complete(true);
        const contextMsg = response.data.brand_aware ? ' (Brand-aware ✨)' : '';
        alert(`✅ Batch 1 Complete${contextMsg}! Created ${response.data.multiplied_count || response.data.platform_count} versions for: Twitter, LinkedIn, Facebook, Instagram, YouTube`);
      } else {
        const contextMsg = response.data.brand_aware ? ' (Brand-aware ✨)' : '';
        alert(`✅ Batch 2 Complete${contextMsg}! Created ${response.data.multiplied_count || response.data.platform_count} versions for: Pinterest, TikTok, Email, Blog, Reddit`);
      }
    } catch (error) {
      console.error('Content Multiplier error:', error);
      if (error.code === 'ECONNABORTED') {
        alert('⏱️ Request timed out. The content is being processed but may take longer. Please check your content history in a few minutes.');
      } else {
        alert(error.response?.data?.detail || error.message || 'Error multiplying content');
      }
    } finally {
      setMultiplying(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {workflow.active && workflow.targetTool === 'multiply' && <RiverBanner />}
        <div className="fade-in-up mb-8">
          <h1 className="text-4xl font-bold gradient-text mb-3">⚡ Content Multiplication Engine</h1>
          <p className="text-gray-600 text-lg">Turn 1 piece of content into 10+ platform-optimized versions instantly</p>
        </div>

        <div className="glass-effect bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 mb-8 shadow-vibrant border-2 border-purple-200 fade-in-up delay-100">
          <h3 className="font-bold text-2xl mb-6 text-gray-900 flex items-center">
            <span className="mr-3">🎯</span> What ChatGPT Can't Do:
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card-hover bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
              <div className="text-4xl mb-3">🔄</div>
              <div className="font-bold text-lg mb-2 text-gray-900">Auto-Adaptation</div>
              <div className="text-sm text-gray-600 leading-relaxed">Automatically optimizes for each platform's specs and best practices</div>
            </div>
            <div className="card-hover bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
              <div className="text-4xl mb-3">⏱️</div>
              <div className="font-bold text-lg mb-2 text-gray-900">Best Posting Times</div>
              <div className="text-sm text-gray-600 leading-relaxed">Recommends optimal posting schedule per platform for maximum reach</div>
            </div>
            <div className="card-hover bg-white rounded-xl p-6 shadow-md border-2 border-gray-100">
              <div className="text-4xl mb-3">📊</div>
              <div className="font-bold text-lg mb-2 text-gray-900">10+ Platforms</div>
              <div className="text-sm text-gray-600 leading-relaxed">Twitter, LinkedIn, Instagram, Facebook, YouTube, TikTok, and more</div>
            </div>
          </div>
        </div>

        <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 mb-8 border-2 border-gray-200 fade-in-up delay-200">
          <label className="block mb-6">
            <span className="text-gray-900 font-bold text-lg mb-3 block flex items-center">
              <span className="mr-2">📝</span> Select Content to Multiply
            </span>
            <select
              value={selectedContent}
              onChange={(e) => setSelectedContent(e.target.value)}
              className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth text-gray-900 font-medium bg-white hover:border-purple-400"
            >
              <option value="">Choose content...</option>
              {userContent.map((content) => (
                <option key={content.id} value={content.id}>
                  {content.title} ({content.word_count} words)
                </option>
              ))}
            </select>
          </label>
          
          {/* Context-Aware Toggle */}
          {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'enterprise') && (
            <div className="mb-6 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200 shadow-md">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={useWebsiteContext}
                      onChange={(e) => setUseWebsiteContext(e.target.checked)}
                      className="w-6 h-6 text-purple-600 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer"
                    />
                    <span className="ml-4 font-bold text-gray-900 text-lg group-hover:text-purple-700 transition-smooth">
                      🎯 Use Brand Voice Context
                    </span>
                  </label>
                  <p className="text-sm text-gray-600 mt-2 ml-10 leading-relaxed">
                    Apply your website's brand voice to all generated content for consistent, on-brand messaging
                  </p>
                </div>
              </div>
              
              {useWebsiteContext && !analysisId && (
                <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-xl shadow-sm">
                  <p className="text-sm text-yellow-900 font-semibold">
                    ⚠️ No website analysis found. 
                    <button
                      onClick={() => window.location.hash = '#website-analyzer'}
                      className="ml-2 text-purple-600 hover:text-purple-800 underline font-bold transition-smooth"
                    >
                      Analyze your website first →
                    </button>
                  </p>
                </div>
              )}
              
              {useWebsiteContext && analysisId && (
                <div className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded-xl shadow-sm">
                  <p className="text-sm text-green-900 font-semibold flex items-center">
                    <span className="mr-2">✅</span> Brand voice context active - content will match your brand's tone and style
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Language Selector */}
          <div className="mb-4">
            <LanguageSelector 
              selectedLanguage={language}
              onLanguageChange={setLanguage}
            />
          </div>

          <div className="space-y-4">
            <button
              onClick={() => handleMultiply(1)}
              disabled={multiplying || !selectedContent}
              className="btn-primary-enhanced w-full px-8 py-5 text-white rounded-xl hover:shadow-vibrant disabled:opacity-50 disabled:cursor-not-allowed transition-smooth font-bold text-lg relative overflow-hidden group"
            >
              <span className="relative z-10 flex items-center justify-center gap-3">
                {multiplying ? (
                  <>
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Batch 1...
                  </>
                ) : (
                  <>⚡ Generate First 5 Platforms</>
                )}
              </span>
            </button>
            
            {batch1Complete && (
              <button
                onClick={() => handleMultiply(2)}
                disabled={multiplying}
                className="w-full px-8 py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-vibrant disabled:opacity-50 transition-smooth font-bold text-lg relative overflow-hidden group"
              >
                <span className="relative z-10 flex items-center justify-center gap-3">
                  {multiplying ? (
                    <>
                      <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating Batch 2...
                    </>
                  ) : (
                    <>✨ Generate 5 More Platforms</>
                  )}
                </span>
              </button>
            )}
            
            <div className="p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
              <p className="text-sm font-semibold text-gray-700 text-center mb-2">📋 Platform Batches</p>
              <p className="text-xs text-gray-600 text-center leading-relaxed">
                <span className="font-medium">Batch 1:</span> Twitter, LinkedIn, Facebook, Instagram, YouTube<br />
                <span className="font-medium">Batch 2:</span> Pinterest, TikTok, Email Subject, Blog Intro, Reddit
              </p>
            </div>
          </div>
        </div>

        {results && (results.details || results.multiplied_content) && Object.keys(results.details || results.multiplied_content).length > 0 && (
          <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-green-200 fade-in-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-bold gradient-text flex items-center">
                <span className="mr-3">✅</span> Multiplication Complete!
              </h2>
              <div className="badge-gradient px-4 py-2 text-base font-bold">
                {results.multiplied_count} Versions
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {Object.entries(results.details || results.multiplied_content).map(([platform, data], index) => {
                const platformEmojis = {
                  twitter: '🐦', linkedin: '💼', facebook: '📘', instagram: '📸',
                  youtube: '🎥', pinterest: '📌', tiktok: '🎵', email: '✉️',
                  blog: '📝', reddit: '🔴'
                };
                const emoji = platformEmojis[platform.toLowerCase()] || '📱';
                
                return (
                  <div 
                    key={platform} 
                    onClick={() => setSelectedPlatformContent({ platform, data })}
                    className="card-hover glass-effect border-2 border-purple-200 rounded-xl p-6 hover:shadow-vibrant hover:border-purple-400 transition-smooth cursor-pointer bg-gradient-to-br from-white to-purple-50"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{emoji}</span>
                        <span className="font-bold text-lg text-gray-900 capitalize">{platform.replace('_', ' ')}</span>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-3 py-1.5 rounded-full font-bold border-2 border-green-200">Ready</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-3 line-clamp-3 leading-relaxed">{data.content.substring(0, 120)}...</p>
                    <div className="flex items-center justify-between pt-3 border-t-2 border-gray-100">
                      <p className="text-xs text-gray-600 font-medium">📅 {data.best_posting_time}</p>
                      <p className="text-xs text-purple-600 font-bold">View Full →</p>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
              <p className="text-green-800 font-bold text-lg text-center flex items-center justify-center gap-3">
                <span className="text-3xl">🎉</span>
                Created {results.multiplied_count} platform-optimized versions ready to post!
              </p>
              <div className="flex justify-center mt-4">
                <RiverButton
                  label="Distribute All via Apollo"
                  icon="☀️"
                  onClick={() => startRiver({
                    topic: selectedContent?.substring(0, 80) || 'Multiplied content',
                    content: selectedContent || '',
                    targetTool: 'apollo-chamber',
                    sourceTool: 'multiply',
                  })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Platform Content Modal */}
        {selectedPlatformContent && (
          <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 fade-in">
            <div className="glass-effect bg-white rounded-3xl p-10 max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-vibrant border-2 border-purple-200 animate-scale-in">
              <div className="flex justify-between items-start mb-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    {(() => {
                      const platformEmojis = {
                        twitter: '🐦', linkedin: '💼', facebook: '📘', instagram: '📸',
                        youtube: '🎥', pinterest: '📌', tiktok: '🎵', email: '✉️',
                        blog: '📝', reddit: '🔴'
                      };
                      return <span className="text-4xl">{platformEmojis[selectedPlatformContent.platform.toLowerCase()] || '📱'}</span>;
                    })()}
                    <h3 className="text-3xl font-bold gradient-text capitalize">
                      {selectedPlatformContent.platform.replace('_', ' ')} Version
                    </h3>
                  </div>
                  <p className="text-gray-600 font-medium flex items-center gap-2">
                    <span>📅</span> Best posting time: <span className="text-purple-600 font-bold">{selectedPlatformContent.data.best_posting_time}</span>
                  </p>
                </div>
                <button
                  onClick={() => setSelectedPlatformContent(null)}
                  className="text-gray-400 hover:text-gray-700 text-3xl font-bold transition-smooth hover:rotate-90 transform duration-300"
                >
                  ✕
                </button>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mb-8 border-2 border-purple-200 shadow-md">
                <h4 className="font-bold text-gray-900 mb-4 text-lg flex items-center">
                  <span className="mr-2">✨</span> Platform-Optimized Content:
                </h4>
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap text-gray-800 leading-relaxed text-base">
                    {selectedPlatformContent.data.content}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <button
                  onClick={async () => {
                    const success = await copyToClipboard(selectedPlatformContent.data.content);
                    alert(success ? '✅ Content copied to clipboard!' : '❌ Copy failed');
                  }}
                  className="flex-1 btn-primary-enhanced text-white py-4 rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth"
                >
                  📋 Copy to Clipboard
                </button>
                <button
                  onClick={() => setSelectedPlatformContent(null)}
                  className="flex-1 bg-gray-200 text-gray-700 py-4 rounded-xl font-bold text-lg hover:bg-gray-300 transition-smooth border-2 border-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// SEO Optimizer Component
const SEOOptimizer = () => {
  const { workflow, startRiver } = useWorkflow();
  const [userContent, setUserContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState('');
  const [targetKeyword, setTargetKeyword] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [seoResults, setSeoResults] = useState(null);

  // Pre-fill from River workflow
  useEffect(() => {
    if (workflow.active && workflow.targetTool === 'seo' && workflow.content) {
      setSelectedContent(workflow.content);
      if (workflow.topic) setTargetKeyword(workflow.topic);
    }
  }, [workflow]);

  useEffect(() => {
    fetchUserContent();
  }, []);

  const fetchUserContent = async () => {
    try {
      const response = await axios.get(`${API}/content/history`);
      setUserContent(response.data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedContent) {
      alert('Please select content to analyze');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/content/seo-analyze`, {
        content_id: selectedContent,
        target_keyword: targetKeyword || null
      });
      
      setSeoResults(response.data.seo_analysis);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error analyzing SEO');
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-blue-100';
    if (score >= 40) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        {workflow.active && workflow.targetTool === 'seo' && <RiverBanner />}
        <div className="fade-in-up mb-10">
          <h1 className="text-4xl font-bold gradient-text mb-3 flex items-center">
            <span className="mr-3">🎯</span> SEO Content Optimizer
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed">Real-time SEO analysis that ChatGPT doesn't provide</p>
        </div>

        <div className="glass-effect bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 mb-10 shadow-vibrant border-2 border-green-200 fade-in-up delay-100">
          <h3 className="font-bold text-2xl mb-6 text-gray-900 flex items-center">
            <span className="mr-3">✨</span> Features ChatGPT Lacks:
          </h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="card-hover bg-white rounded-xl p-6 text-center shadow-md border-2 border-gray-100">
              <div className="text-5xl mb-3">💯</div>
              <div className="font-bold text-lg text-gray-900">SEO Score</div>
              <div className="text-sm text-gray-600 mt-2">Instant ranking analysis</div>
            </div>
            <div className="card-hover bg-white rounded-xl p-6 text-center shadow-md border-2 border-gray-100">
              <div className="text-5xl mb-3">🔍</div>
              <div className="font-bold text-lg text-gray-900">Keyword Analysis</div>
              <div className="text-sm text-gray-600 mt-2">Density & optimization</div>
            </div>
            <div className="card-hover bg-white rounded-xl p-6 text-center shadow-md border-2 border-gray-100">
              <div className="text-5xl mb-3">📖</div>
              <div className="font-bold text-lg text-gray-900">Readability Score</div>
              <div className="text-sm text-gray-600 mt-2">Flesch-Kincaid grade</div>
            </div>
            <div className="card-hover bg-white rounded-xl p-6 text-center shadow-md border-2 border-gray-100">
              <div className="text-5xl mb-3">📊</div>
              <div className="font-bold text-lg text-gray-900">Ranking Potential</div>
              <div className="text-sm text-gray-600 mt-2">Competitive insights</div>
            </div>
          </div>
        </div>

        <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 mb-10 border-2 border-gray-200 fade-in-up delay-200">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <label className="block">
              <span className="text-gray-900 font-bold text-lg mb-3 block flex items-center">
                <span className="mr-2">📝</span> Select Content
              </span>
              <select
                value={selectedContent}
                onChange={(e) => setSelectedContent(e.target.value)}
                className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-smooth text-gray-900 font-medium bg-white hover:border-green-400"
              >
                <option value="">Choose content...</option>
                {userContent.map((content) => (
                  <option key={content.id} value={content.id}>
                    {content.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-gray-900 font-bold text-lg mb-3 block flex items-center">
                <span className="mr-2">🔑</span> Target Keyword (Optional)
              </span>
              <input
                type="text"
                value={targetKeyword}
                onChange={(e) => setTargetKeyword(e.target.value)}
                placeholder="e.g., content marketing"
                className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-smooth font-medium"
              />
            </label>
          </div>

          <button
            onClick={handleAnalyze}
            disabled={analyzing || !selectedContent}
            className="w-full px-8 py-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-vibrant disabled:opacity-50 disabled:cursor-not-allowed transition-smooth font-bold text-lg"
          >
            {analyzing ? (
              <span className="flex items-center justify-center gap-3">
                <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Analyzing SEO...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                🎯 Analyze SEO Performance
              </span>
            )}
          </button>
        </div>

        {seoResults && (
          <div className="space-y-8">
            {/* SEO Score */}
            <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-10 border-2 border-gray-200 fade-in-up">
              <h2 className="text-3xl font-bold gradient-text mb-8 flex items-center">
                <span className="mr-3">📊</span> SEO Performance Report
              </h2>
              
              <div className="flex flex-col md:flex-row items-center justify-between mb-10 gap-8">
                <div className="text-center md:text-left">
                  <div className="text-sm text-gray-600 font-semibold mb-2 uppercase tracking-wide">Overall SEO Score</div>
                  <div className={`text-7xl font-bold ${getScoreColor(seoResults.seo_score)} mb-2`}>
                    {seoResults.seo_score}<span className="text-4xl">/100</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 mt-4">
                    <div 
                      className={`h-4 rounded-full transition-all duration-1000 ${
                        seoResults.seo_score >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                        seoResults.seo_score >= 60 ? 'bg-gradient-to-r from-blue-500 to-cyan-600' :
                        seoResults.seo_score >= 40 ? 'bg-gradient-to-r from-yellow-500 to-orange-600' :
                        'bg-gradient-to-r from-red-500 to-pink-600'
                      }`}
                      style={{ width: `${seoResults.seo_score}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className={`text-center px-10 py-6 rounded-2xl shadow-lg border-2 ${
                  seoResults.seo_score >= 80 ? 'bg-green-50 border-green-300' :
                  seoResults.seo_score >= 60 ? 'bg-blue-50 border-blue-300' :
                  seoResults.seo_score >= 40 ? 'bg-yellow-50 border-yellow-300' :
                  'bg-red-50 border-red-300'
                }`}>
                  <div className={`text-3xl font-bold ${getScoreColor(seoResults.seo_score)} mb-2`}>
                    {seoResults.competitive_analysis.potential.toUpperCase()}
                  </div>
                  <div className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Ranking Potential</div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="card-hover glass-effect border-2 border-gray-200 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-gray-700 font-bold uppercase tracking-wide">Word Count</div>
                    <span className="text-3xl">📝</span>
                  </div>
                  <div className="text-4xl font-bold text-blue-600 mb-2">{seoResults.word_count}</div>
                  <div className={`text-sm font-semibold ${seoResults.word_count >= 300 ? 'text-green-600' : 'text-orange-600'}`}>
                    {seoResults.word_count >= 300 ? '✅ Good length' : '⚠️ Too short'}
                  </div>
                </div>
                
                <div className="card-hover glass-effect border-2 border-gray-200 rounded-xl p-6 bg-gradient-to-br from-purple-50 to-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-gray-700 font-bold uppercase tracking-wide">Readability</div>
                    <span className="text-3xl">📖</span>
                  </div>
                  <div className="text-4xl font-bold text-purple-600 mb-2">{seoResults.readability.flesch_score}</div>
                  <div className="text-sm text-gray-600 font-semibold">
                    Grade Level: <span className="text-purple-700">{seoResults.readability.grade_level}</span>
                  </div>
                </div>
                
                <div className="card-hover glass-effect border-2 border-gray-200 rounded-xl p-6 bg-gradient-to-br from-green-50 to-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-gray-700 font-bold uppercase tracking-wide">Keyword Density</div>
                    <span className="text-3xl">🔍</span>
                  </div>
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    {seoResults.keyword_analysis.keyword_density || seoResults.keyword_analysis.primary_keyword_density}%
                  </div>
                  <div className={`text-sm font-semibold ${seoResults.keyword_analysis.optimal_density ? 'text-green-600' : 'text-orange-600'}`}>
                    {seoResults.keyword_analysis.optimal_density ? '✅ Optimal' : '⚠️ Needs adjustment'}
                  </div>
                </div>
              </div>

              {/* Suggestions */}
              <div className="glass-effect bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded-2xl p-8 shadow-md">
                <h3 className="font-bold text-2xl mb-5 text-gray-900 flex items-center">
                  <span className="mr-3">💡</span> Actionable Suggestions:
                </h3>
                <ul className="space-y-3">
                  {seoResults.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-3 text-base text-gray-800 leading-relaxed">
                      <span className="text-blue-600 font-bold flex-shrink-0 mt-1">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Ranking Potential */}
            <div className="glass-effect bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-8 shadow-vibrant border-2 border-purple-300 fade-in-up delay-100">
              <h3 className="text-2xl font-bold mb-4 text-gray-900 flex items-center">
                <span className="mr-3">🚀</span> Ranking Potential
              </h3>
              <p className="text-gray-800 mb-4 text-lg leading-relaxed">{seoResults.competitive_analysis.description}</p>
              <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-xl shadow-md border-2 border-purple-200">
                <span className="text-gray-700 font-semibold">Estimated Search Result Page:</span>
                <span className="text-2xl font-bold text-purple-600">Page {seoResults.competitive_analysis.estimated_page}</span>
              </div>
            </div>

            {/* River Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <RiverButton
                label="Rewrite with SEO Fixes"
                icon="✏️"
                onClick={() => startRiver({
                  topic: targetKeyword || 'SEO-optimized content',
                  content: selectedContent,
                  keywords: seoResults.suggestions?.slice(0, 3).join(', ') || targetKeyword,
                  targetTool: 'generate',
                  sourceTool: 'seo',
                })}
              />
              <RiverButton
                label="Distribute Optimized Content"
                icon="☀️"
                onClick={() => startRiver({
                  topic: targetKeyword,
                  content: selectedContent,
                  targetTool: 'apollo-chamber',
                  sourceTool: 'seo',
                })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Trending Topics Component
const TrendingTopics = () => {
  const { startRiver } = useWorkflow();
  const { storeTrendingTopics, isUncoveredGap, store } = useIntelligence();
  const [industry, setIndustry] = useState('marketing');
  const [loading, setLoading] = useState(false);
  const [trends, setTrends] = useState(null);
  const [enhancedData, setEnhancedData] = useState(null);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/market/trending-topics?industry=${industry}&limit=5`);
      const data = response.data;
      if (data.topics && !data.trending_topics) {
        data.trending_topics = data.topics;
      }
      setTrends(data);
      // Store in intelligence context
      if (data.trending_topics) {
        storeTrendingTopics(data.trending_topics);
      }
      // Enhance with urgency signals and content angles
      if (data.trending_topics?.length > 0) {
        try {
          const enhRes = await axios.post(`${API}/intelligence/enhance-trends`, {
            topics: data.trending_topics,
            check_gaps: !!(store.competitorGaps?.length),
          });
          setEnhancedData(enhRes.data.enhanced_topics || []);
        } catch { /* Enhancement is optional */ }
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrends();
  }, [industry]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="fade-in-up mb-10">
          <h1 className="text-4xl font-bold gradient-text mb-3 flex items-center">
            <span className="mr-3">📈</span> Trending Topics Feed
          </h1>
          <p className="text-gray-600 text-lg leading-relaxed">Real-time trending topics in your industry - ChatGPT can't do this!</p>
        </div>

        <div className="glass-effect bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8 mb-10 shadow-vibrant border-2 border-green-200 fade-in-up delay-100">
          <h3 className="font-bold text-2xl mb-6 text-gray-900 flex items-center">
            <span className="mr-3">🚀</span> Stay Ahead of Trends:
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="card-hover bg-white rounded-xl p-6 text-center shadow-md border-2 border-gray-100">
              <div className="text-5xl mb-3">⏰</div>
              <div className="font-bold text-lg text-gray-900">Real-Time Data</div>
              <div className="text-sm text-gray-600 mt-2">Live trend tracking</div>
            </div>
            <div className="card-hover bg-white rounded-xl p-6 text-center shadow-md border-2 border-gray-100">
              <div className="text-5xl mb-3">📊</div>
              <div className="font-bold text-lg text-gray-900">Search Volume</div>
              <div className="text-sm text-gray-600 mt-2">Popularity metrics</div>
            </div>
            <div className="card-hover bg-white rounded-xl p-6 text-center shadow-md border-2 border-gray-100">
              <div className="text-5xl mb-3">💡</div>
              <div className="font-bold text-lg text-gray-900">Content Ideas</div>
              <div className="text-sm text-gray-600 mt-2">AI-generated topics</div>
            </div>
          </div>
        </div>

        <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 mb-10 border-2 border-gray-200 fade-in-up delay-200">
          <label className="block">
            <span className="text-gray-900 font-bold text-lg mb-4 block flex items-center">
              <span className="mr-2">🏢</span> Select Your Industry
            </span>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-smooth text-gray-900 font-medium bg-white hover:border-green-400"
            >
              <option value="marketing">📢 Marketing</option>
              <option value="technology">💻 Technology</option>
              <option value="business">💼 Business</option>
              <option value="ecommerce">🛒 E-commerce</option>
              <option value="health">🏥 Health & Wellness</option>
            </select>
          </label>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <svg className="animate-spin h-16 w-16 mx-auto text-green-600 mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 font-semibold text-lg">Loading trending topics...</p>
          </div>
        ) : trends && trends.trending_topics && (
          <div className="space-y-6">
            {/* Show live data indicator */}
            {trends.is_live_data && (
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl p-4 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl animate-pulse">🔴</span>
                  <span className="font-bold">LIVE DATA</span>
                  <span className="text-green-100">• Based on {trends.data_sources?.news_headlines_count || ''} real-time news &amp; search data</span>
                </div>
                <span className="text-sm text-green-100">Updated: {new Date(trends.generated_at).toLocaleTimeString()}</span>
              </div>
            )}
            
            {trends.trending_topics.map((topic, index) => {
              const enhanced = enhancedData?.[index];
              const hasGapData = store.competitorGaps?.length > 0;
              const isGap = isUncoveredGap(topic.topic);

              return (
              <div key={index} className="card-hover glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-gray-200 hover:border-purple-300 transition-smooth fade-in-up" style={{ animationDelay: `${index * 100}ms` }}>
                {/* Uncovered Gap Badge */}
                {isGap && (
                  <div className="mb-3 px-3 py-1.5 bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-300 rounded-lg inline-flex items-center gap-2">
                    <span>⭐</span>
                    <span className="text-amber-800 text-sm font-bold">UNCOVERED — None of your tracked competitors have written about this</span>
                  </div>
                )}
                {!hasGapData && index === 0 && (
                  <div className="mb-3 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg inline-flex items-center gap-2">
                    <span className="text-xs text-gray-500">Run Beat Competition to see which trends your competitors are missing</span>
                  </div>
                )}

                <div className="flex items-start justify-between mb-4 gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-4xl font-bold gradient-text flex-shrink-0">#{index + 1}</span>
                      <h3 className="text-2xl font-bold text-gray-900 leading-tight">{topic.topic}</h3>
                    </div>
                    <div className="flex items-center gap-3 text-base flex-wrap">
                      <span className="flex items-center gap-2 font-semibold">
                        <span className="text-2xl">🔥</span>
                        <span className="text-gray-700">Trend Score:</span>
                        <span className="text-orange-600">{topic.trend_score}/100</span>
                      </span>
                      {/* Freshness/Urgency Badge */}
                      {enhanced?.freshness && (
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                          enhanced.freshness.badge === 'act_now' ? 'bg-red-100 text-red-700' :
                          enhanced.freshness.badge === 'gaining' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {enhanced.freshness.emoji} {enhanced.freshness.label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`px-6 py-3 rounded-2xl text-base font-bold shadow-md border-2 flex-shrink-0 ${
                    topic.trend_score >= 90 ? 'bg-red-50 text-red-700 border-red-300' :
                    topic.trend_score >= 80 ? 'bg-orange-50 text-orange-700 border-orange-300' :
                    'bg-yellow-50 text-yellow-700 border-yellow-300'
                  }`}>
                    {topic.trend_score >= 90 ? '🔥 Hot' : topic.trend_score >= 80 ? '📈 Rising' : '💫 Emerging'}
                  </div>
                </div>

                {/* Urgency Signals Row */}
                {enhanced && (
                  <div className="flex flex-wrap gap-3 mb-4">
                    {enhanced.competition && (
                      <span className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                        enhanced.competition.level === 'low' ? 'bg-green-50 text-green-700 border-green-200' :
                        enhanced.competition.level === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                        'bg-red-50 text-red-700 border-red-200'
                      }`}>
                        📰 {enhanced.competition.text}
                      </span>
                    )}
                    {enhanced.freshness?.window && (
                      <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                        ⏰ {enhanced.freshness.window}
                      </span>
                    )}
                  </div>
                )}

                {/* Why it's trending */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 mb-4">
                  <h4 className="font-bold mb-3 text-lg text-gray-900 flex items-center">
                    <span className="mr-2">📰</span> Why It's Trending:
                  </h4>
                  <p className="text-gray-800 leading-relaxed">{topic.why_trending}</p>
                </div>

                {/* Content angle and audience */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border-2 border-purple-200">
                    <h4 className="font-bold mb-2 text-base text-gray-900 flex items-center">
                      <span className="mr-2">💡</span> Content Angle:
                    </h4>
                    <p className="text-gray-800 text-sm leading-relaxed">{topic.content_angle}</p>
                  </div>
                  <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-5 border-2 border-green-200">
                    <h4 className="font-bold mb-2 text-base text-gray-900 flex items-center">
                      <span className="mr-2">🎯</span> Target Audience:
                    </h4>
                    <p className="text-gray-800 text-sm leading-relaxed">{topic.target_audience}</p>
                  </div>
                </div>

                {/* AI-Generated Content Angles (3 specific angles per trend) */}
                {enhanced?.content_angles && (
                  <div className="mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 border-2 border-indigo-200">
                    <h4 className="font-bold mb-3 text-base text-gray-900 flex items-center">
                      <span className="mr-2">✨</span> 3 Content Angles You Could Take:
                    </h4>
                    <div className="space-y-2">
                      {enhanced.content_angles.map((angle, ai) => (
                        <div key={ai} className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-gray-100 group hover:border-purple-300 transition-all">
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-semibold text-gray-900">
                              {angle.emoji} {angle.label}: <span className="font-normal text-gray-700">{angle.title}</span>
                            </span>
                            <span className="text-xs text-gray-500 ml-2">• Best for: {angle.best_for}</span>
                          </div>
                          <RiverButton
                            label="Forge This"
                            icon="→"
                            onClick={() => startRiver({
                              topic: angle.title,
                              keywords: topic.suggested_keywords?.join(', ') || topic.topic,
                              contentType: 'blog_post',
                              targetTool: 'generate',
                              sourceTool: 'trending',
                              competitorGap: angle.angle,
                            })}
                            className="!py-1.5 !px-3 !text-xs ml-3 opacity-70 group-hover:opacity-100"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* River Action Buttons */}
                <div className="flex flex-wrap gap-3 mt-5 pt-4 border-t border-gray-200">
                  <RiverButton
                    label="Forge Content on This"
                    icon="🔥"
                    onClick={() => startRiver({
                      topic: topic.topic,
                      keywords: topic.suggested_keywords?.join(', ') || topic.content_angle || '',
                      contentType: 'blog_post',
                      targetTool: 'generate',
                      sourceTool: 'trending',
                    })}
                  />
                  <RiverButton
                    label="Debate This in War Room"
                    icon="⚔️"
                    onClick={() => startRiver({
                      topic: topic.topic,
                      keywords: topic.content_angle || '',
                      targetTool: 'alliance',
                      sourceTool: 'trending',
                    })}
                  />
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};


// ============================================
// PRO FEATURES - Competitive Advantage Tools
// ============================================

// Content Brief Generator Component
const ContentBriefGenerator = () => {
  const [topic, setTopic] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [goals, setGoals] = useState('');
  const [contentType, setContentType] = useState('blog');
  const [wordCount, setWordCount] = useState('1500');
  const [tone, setTone] = useState('professional');
  const [keywords, setKeywords] = useState('');
  const [loading, setLoading] = useState(false);
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState('');
  const [savedBriefs, setSavedBriefs] = useState([]);
  const [showSaved, setShowSaved] = useState(false);

  useEffect(() => {
    fetchSavedBriefs();
  }, []);

  const fetchSavedBriefs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/briefs/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedBriefs(data.briefs || []);
      }
    } catch (err) {
      console.error('Error fetching saved briefs:', err);
    }
  };

  const generateBrief = async () => {
    if (!topic.trim()) {
      setError('Please enter a topic');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/brief`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          topic,
          target_audience: targetAudience,
          goals,
          content_type: contentType,
          word_count: parseInt(wordCount),
          tone,
          keywords: keywords.split(',').map(k => k.trim()).filter(k => k)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate brief');
      }

      setBrief(data);
      fetchSavedBriefs(); // Refresh saved briefs list
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyBrief = async () => {
    if (brief) {
      const success = await copyToClipboard(brief.content);
      alert(success ? '✅ Content brief copied to clipboard!' : '❌ Copy failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="fade-in-up mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-4xl font-bold gradient-text flex items-center">
            <span className="mr-3">💡</span> AI Content Brief Generator
          </h2>
          <button
            onClick={() => setShowSaved(!showSaved)}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-semibold hover:bg-purple-200 transition-smooth"
          >
            📚 Saved Briefs ({savedBriefs.length})
          </button>
        </div>
        <p className="text-gray-600 text-lg leading-relaxed">
          Generate strategic content briefs with outlines, keywords, competitor insights, and actionable recommendations. Plan your content like a pro!
        </p>
        <div className="badge-gradient mt-4 inline-block px-4 py-2 text-sm font-bold">
          PRO Feature - 5 Credits
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-2">
          <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-gray-200 fade-in-up delay-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">📝</span> Brief Configuration
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🎯</span> Topic / Subject *
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., 'How to start a successful podcast in 2025'"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">📄</span> Content Type
                  </label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  >
                    <option value="blog">📝 Blog Post</option>
                    <option value="article">📰 Article</option>
                    <option value="video">🎥 Video Script</option>
                    <option value="social">📱 Social Media</option>
                    <option value="whitepaper">📊 Whitepaper</option>
                    <option value="email">✉️ Email Campaign</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">📏</span> Target Word Count
                  </label>
                  <select
                    value={wordCount}
                    onChange={(e) => setWordCount(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  >
                    <option value="500">500-800 words (Quick)</option>
                    <option value="1500">1,500-2,000 words (Standard)</option>
                    <option value="3000">3,000+ words (In-depth)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🎭</span> Tone & Style
                </label>
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                >
                  <option value="professional">💼 Professional</option>
                  <option value="casual">😊 Casual & Friendly</option>
                  <option value="authoritative">🎓 Authoritative</option>
                  <option value="conversational">💬 Conversational</option>
                  <option value="technical">🔬 Technical</option>
                  <option value="inspirational">✨ Inspirational</option>
                </select>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">👥</span> Target Audience
                </label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="e.g., 'Aspiring podcasters, content creators, entrepreneurs'"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🔑</span> Primary Keywords (comma-separated)
                </label>
                <input
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="e.g., 'podcast tips, audio content, content creation'"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🎯</span> Content Goals
                </label>
                <textarea
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  placeholder="e.g., 'Educate readers, generate leads, establish authority'"
                  rows="4"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-300 text-red-800 px-6 py-4 rounded-xl font-semibold">
                  ⚠️ {error}
                </div>
              )}

              <button
                onClick={generateBrief}
                disabled={loading || !topic}
                className="btn-primary-enhanced w-full py-5 text-white rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Strategic Brief...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    💡 Generate Content Brief
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar with tips */}
        <div className="lg:col-span-1">
          <div className="glass-effect bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-vibrant p-8 border-2 border-purple-200 sticky top-8 fade-in-up delay-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">💡</span> Pro Tips
            </h3>
            <div className="space-y-6">
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-purple-700 mb-2 flex items-center">
                  <span className="mr-2">🎯</span> Be Specific
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  The more specific your topic, the better the brief. Include target year, industry, or unique angle.
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-blue-700 mb-2 flex items-center">
                  <span className="mr-2">🔑</span> Keywords Matter
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Add 3-5 primary keywords to get SEO-optimized content strategies and keyword clustering.
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-green-700 mb-2 flex items-center">
                  <span className="mr-2">👥</span> Know Your Audience
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Define your audience's pain points, experience level, and interests for targeted briefs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {brief && (
        <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-10 border-2 border-gray-200 fade-in-up delay-300">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <h3 className="text-3xl font-bold gradient-text flex items-center">
              <span className="mr-3">📄</span> Your Content Brief
            </h3>
            <div className="flex gap-3">
              <button
                onClick={copyBrief}
                className="px-6 py-3 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-xl hover:shadow-md font-bold transition-smooth border-2 border-purple-200"
              >
                📋 Copy
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([brief.content], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `content-brief-${topic.substring(0, 30).replace(/[^a-z0-9]/gi, '-')}.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-6 py-3 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-xl hover:shadow-md font-bold transition-smooth border-2 border-green-200"
              >
                💾 Download
              </button>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border-2 border-purple-200 mb-6">
            <div className="prose prose-lg max-w-none">
              <pre className="whitespace-pre-wrap bg-white p-6 rounded-xl text-base text-gray-800 leading-relaxed font-sans shadow-sm border-2 border-gray-200">
{brief.content}
              </pre>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-5 bg-green-50 border-2 border-green-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">✅</span>
                <span className="font-bold text-green-900">Brief Generated!</span>
              </div>
              <p className="text-sm text-green-800 font-medium">
                Successfully created your content strategy
              </p>
            </div>
            <div className="p-5 bg-purple-50 border-2 border-purple-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">💎</span>
                <span className="font-bold text-purple-900">Credits Used</span>
              </div>
              <p className="text-sm text-purple-800 font-medium">
                {brief.credits_used} credits | {brief.credits_remaining} remaining
              </p>
            </div>
            <div className="p-5 bg-blue-50 border-2 border-blue-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">📚</span>
                <span className="font-bold text-blue-900">Auto-Saved</span>
              </div>
              <p className="text-sm text-blue-800 font-medium">
                Access anytime in your saved briefs
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Saved Briefs Modal */}
      {showSaved && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 fade-in">
          <div className="glass-effect bg-white rounded-3xl p-10 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-vibrant border-2 border-purple-200">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-3xl font-bold gradient-text flex items-center">
                <span className="mr-3">📚</span> Saved Briefs
              </h3>
              <button
                onClick={() => setShowSaved(false)}
                className="text-gray-400 hover:text-gray-700 text-3xl font-bold transition-smooth"
              >
                ✕
              </button>
            </div>
            
            {savedBriefs.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-6xl block mb-4">📝</span>
                <p className="text-gray-600 text-lg">No saved briefs yet</p>
                <p className="text-gray-500 text-sm mt-2">Generate your first content brief to see it here!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {savedBriefs.map((savedBrief, idx) => (
                  <div key={idx} className="card-hover p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200 hover:shadow-lg transition-smooth cursor-pointer"
                    onClick={() => {
                      setBrief(savedBrief);
                      setTopic(savedBrief.topic);
                      setShowSaved(false);
                    }}>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">{savedBrief.topic}</h4>
                    <p className="text-sm text-gray-600 mb-3">{savedBrief.target_audience || 'No audience specified'}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>📅 {new Date(savedBrief.created_at).toLocaleDateString()}</span>
                      <span>💎 {savedBrief.credits_used} credits</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Competitor Analyzer Component
const CompetitorAnalyzer = ({ onNavigate }) => {
  const { startRiver } = useWorkflow();
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [yourTopic, setYourTopic] = useState('');
  const [yourUrl, setYourUrl] = useState('');
  const [analysisType, setAnalysisType] = useState('content');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [urlPreview, setUrlPreview] = useState(null);

  useEffect(() => {
    fetchSavedAnalyses();
  }, []);

  // Pre-fill from digest email "Forge this brief →" deep-link (1 hour TTL)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('isupplyai_pending_brief');
      if (!raw) return;
      const pending = JSON.parse(raw);
      const ageMs = Date.now() - (pending.ts || 0);
      if (ageMs > 60 * 60 * 1000) {
        sessionStorage.removeItem('isupplyai_pending_brief');
        return;
      }
      if (pending.topic) setYourTopic(pending.topic);
      sessionStorage.removeItem('isupplyai_pending_brief');
    } catch { /* noop */ }
  }, []);

  const fetchSavedAnalyses = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/competitor-analyses/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedAnalyses(data.analyses || []);
      }
    } catch (err) {
      console.error('Error fetching saved analyses:', err);
    }
  };

  const analyzeCompetitor = async () => {
    if (!competitorUrl.trim()) {
      setError('Please enter a competitor URL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/analyze-competitor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          competitor_urls: [competitorUrl],
          topic: yourTopic || competitorUrl,
          analysis_type: analysisType || 'comprehensive',
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to analyze competitor');
      }

      setAnalysis(data);
      // Credits will refresh on next page load
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyAnalysis = async () => {
    if (analysis) {
      const success = await copyToClipboard(analysis.content);
      alert(success ? '✅ Competitive analysis copied to clipboard!' : '❌ Copy failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="fade-in-up mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-4xl font-bold gradient-text flex items-center">
            <span className="mr-3">🔍</span> Competitive Content Analyzer
          </h2>
          <button
            onClick={() => setShowSaved(!showSaved)}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-semibold hover:bg-purple-200 transition-smooth"
          >
            📚 Saved Analyses ({savedAnalyses.length})
          </button>
        </div>
        <p className="text-gray-600 text-lg leading-relaxed">
          Analyze your competitor's content and discover exactly how to create superior content that outranks them. Find their weaknesses, your opportunities!
        </p>
        <div className="badge-gradient mt-4 inline-block px-4 py-2 text-sm font-bold">
          PRO Feature - 8 Credits
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-2">
          <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-gray-200 fade-in-up delay-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">⚙️</span> Analysis Configuration
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🎯</span> Analysis Type
                </label>
                <select
                  value={analysisType}
                  onChange={(e) => setAnalysisType(e.target.value)}
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                >
                  <option value="content">📝 Content Quality & Structure</option>
                  <option value="seo">🔍 SEO & Keywords</option>
                  <option value="engagement">💬 User Engagement</option>
                  <option value="comprehensive">🎯 Comprehensive (All Above)</option>
                </select>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🔗</span> Competitor URL *
                </label>
                <input
                  type="url"
                  value={competitorUrl}
                  onChange={(e) => setCompetitorUrl(e.target.value)}
                  placeholder="https://competitor.com/their-top-ranking-article"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
                <p className="text-sm text-gray-600 mt-2 font-medium">
                  💡 Paste the URL of the content you want to outrank
                </p>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🏠</span> Your URL (Optional)
                </label>
                <input
                  type="url"
                  value={yourUrl}
                  onChange={(e) => setYourUrl(e.target.value)}
                  placeholder="https://yoursite.com/your-article (for direct comparison)"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
                <p className="text-sm text-gray-600 mt-2 font-medium">
                  💡 Add your content URL for side-by-side comparison
                </p>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">📝</span> Your Target Topic/Keyword
                </label>
                <input
                  type="text"
                  value={yourTopic}
                  onChange={(e) => setYourTopic(e.target.value)}
                  placeholder="e.g., 'Email marketing strategies for SaaS companies'"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
                <p className="text-sm text-gray-600 mt-2 font-medium">
                  💡 The topic/keyword you're targeting - helps generate targeted insights
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-300 text-red-800 px-6 py-4 rounded-xl font-semibold">
                  ⚠️ {error}
                </div>
              )}

              <button
                onClick={analyzeCompetitor}
                disabled={loading || !competitorUrl}
                className="btn-primary-enhanced w-full py-5 text-white rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing Competitor...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    🔍 Analyze & Find Opportunities
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar with benefits */}
        <div className="lg:col-span-1">
          <div className="glass-effect bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-vibrant p-8 border-2 border-purple-200 sticky top-8 fade-in-up delay-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">💡</span> What You Get
            </h3>
            <div className="space-y-5">
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-green-700 mb-2 flex items-center">
                  <span className="mr-2">✅</span> Strengths Analysis
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Learn what makes their content rank and perform well
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-red-700 mb-2 flex items-center">
                  <span className="mr-2">🎯</span> Weakness Detection
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Discover gaps, outdated info, and opportunities to exploit
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-blue-700 mb-2 flex items-center">
                  <span className="mr-2">🚀</span> Action Plan
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Get specific tactics to create superior, outranking content
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-purple-700 mb-2 flex items-center">
                  <span className="mr-2">📊</span> SEO Insights
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Keyword opportunities and optimization strategies
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {analysis && (
        <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-10 border-2 border-gray-200 fade-in-up delay-300">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <h3 className="text-3xl font-bold gradient-text flex items-center">
              <span className="mr-3">📊</span> Competitive Analysis Report
            </h3>
            <div className="flex gap-3">
              <button
                onClick={copyAnalysis}
                className="px-6 py-3 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-xl hover:shadow-md font-bold transition-smooth border-2 border-purple-200"
              >
                📋 Copy
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([analysis.content], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `competitor-analysis-${Date.now()}.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-6 py-3 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-xl hover:shadow-md font-bold transition-smooth border-2 border-green-200"
              >
                💾 Download
              </button>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border-2 border-purple-200 mb-6">
            <div className="prose prose-lg max-w-none">
              <pre className="whitespace-pre-wrap bg-white p-6 rounded-xl text-base text-gray-800 leading-relaxed font-sans shadow-sm border-2 border-gray-200">
{typeof analysis.content === 'object' 
  ? (analysis.content?.text || analysis.content?.content || analysis.content?.message || JSON.stringify(analysis.content, null, 2))
  : analysis.content}
              </pre>
            </div>
          </div>

          {/* ─────────── Part B: Competitor Quality Score ─────────── */}
          {analysis.competitor_quality_score && analysis.competitor_quality_score.overall != null && (() => {
            const q = analysis.competitor_quality_score;
            const overall = Number(q.overall || 0);
            const overallColor = overall >= 70 ? 'from-emerald-500 to-green-600' : overall >= 40 ? 'from-amber-500 to-orange-500' : 'from-red-500 to-rose-600';
            return (
              <div className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-xl p-6 border-2 border-purple-200 mb-6" data-testid="competitor-quality-score-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                    📊 Competitor Content Quality
                  </h3>
                  <div className={`px-4 py-2 rounded-xl bg-gradient-to-r ${overallColor} text-white font-black text-2xl shadow-md`} data-testid="competitor-quality-overall">
                    {overall}<span className="text-sm font-semibold opacity-80">/100</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'SEO', value: q.seo, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
                    { label: 'Content Depth', value: q.content_depth, color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' },
                    { label: 'Freshness', value: q.freshness, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
                  ].map((m) => (
                    <div key={m.label} className={`${m.bg} border-2 rounded-lg p-3 text-center`} data-testid={`quality-${m.label.toLowerCase().replace(' ', '-')}`}>
                      <div className="text-xs font-bold text-gray-600 uppercase tracking-widest">{m.label}</div>
                      <div className={`text-2xl font-black ${m.color} mt-1`}>{Number(m.value || 0)}<span className="text-xs font-semibold opacity-70">/100</span></div>
                    </div>
                  ))}
                </div>
                {q.rationale && <p className="text-sm text-gray-700 mt-3 italic">"{q.rationale}"</p>}
              </div>
            );
          })()}

          {/* ─────────── Part B: Scored Content Gaps ─────────── */}
          {Array.isArray(analysis.content_gaps_scored) && analysis.content_gaps_scored.length > 0 && (
            <div className="bg-white rounded-xl p-6 border-2 border-gray-200 mb-6 shadow-sm" data-testid="scored-content-gaps">
              <h3 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
                🎯 Scored Content Gaps
                <span className="text-xs font-semibold text-gray-500 ml-2">({analysis.content_gaps_scored.length} opportunities)</span>
              </h3>
              <div className="space-y-3">
                {analysis.content_gaps_scored.map((gap, i) => {
                  const score = Number(gap.opportunity_score || 0);
                  const scoreColor = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
                  const diff = gap.difficulty || (score >= 70 ? 'Easy Win' : score >= 40 ? 'Moderate Effort' : 'Hard Battle');
                  const diffColor = diff === 'Easy Win' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                                    diff === 'Moderate Effort' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                                    'bg-red-100 text-red-800 border-red-300';
                  return (
                    <div key={gap.gap_id || i} className="border-2 border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors" data-testid={`gap-card-${i}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-gray-900 mb-1">{gap.title || 'Untitled gap'}</h4>
                          {gap.why_it_matters && <p className="text-sm text-gray-600 mb-2">{gap.why_it_matters}</p>}
                          {gap.score_reasoning && <p className="text-xs text-gray-500 italic">Why this score: {gap.score_reasoning}</p>}
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <div className={`${scoreColor} text-white font-black text-lg px-3 py-1 rounded-lg shadow-sm`} data-testid={`gap-score-${i}`}>
                            {score}<span className="text-xs opacity-80">/100</span>
                          </div>
                          <span className={`text-xs font-bold px-2 py-1 rounded-full border ${diffColor}`} data-testid={`gap-difficulty-${i}`}>
                            {diff}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─────────── Part B: Ready-to-Forge Briefs ─────────── */}
          {Array.isArray(analysis.ready_to_forge_briefs) && analysis.ready_to_forge_briefs.length > 0 && (
            <div className="bg-gradient-to-br from-purple-100 via-blue-50 to-cyan-50 rounded-2xl p-6 border-2 border-purple-300 mb-6 shadow-lg" data-testid="ready-to-forge-briefs">
              <h3 className="text-2xl font-black text-gray-900 mb-2 flex items-center gap-2">
                ⚒️ Ready-to-Forge Briefs
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-600 text-white tracking-wider">NEW</span>
              </h3>
              <p className="text-sm text-gray-700 mb-4">Top 3 opportunities, ready to forge in one click. We'll pre-fill Content Forge in Deep mode.</p>
              <div className="grid md:grid-cols-3 gap-4">
                {analysis.ready_to_forge_briefs.slice(0, 3).map((b, i) => {
                  // Map Athena's LLM angle → Deep Forge dropdown value (defensive)
                  const angleMap = {
                    'thought_leadership': 'thought_leadership',
                    'counter_competitor': 'counter_competitor',
                    'how_to_guide': 'how_to_guide',
                    'how_to': 'how_to_guide',
                    'case_study': 'case_study',
                    'news_reaction': 'news_reaction',
                    'hot_take': 'hot_take',
                    'data_driven': 'thought_leadership',   // data-first maps to TL
                    'story_led': 'case_study',             // story-led maps to case study
                    'comparison': 'counter_competitor',    // comparison maps to counter-competitor
                  };
                  const rawAngle = (b.recommended_angle || 'thought_leadership').toLowerCase();
                  const angle = angleMap[rawAngle] || 'thought_leadership';
                  const handleForge = () => {
                    // Both: River workflow (instant smooth UX) + URL update (shareable / future-proof)
                    const title = b.suggested_title || '';
                    const keyword = b.target_keyword || '';
                    startRiver({
                      topic: title,
                      keywords: keyword,
                      contentAngle: angle,
                      sourceTool: 'competitor-analyzer',
                      targetTool: 'hermes-forge',
                    });
                    // Stash for sessionStorage prefill (matches digest email pattern)
                    try {
                      sessionStorage.setItem('isupplyai_pending_forge_topic', JSON.stringify({
                        topic: title,
                        keyword: keyword,
                        angle: angle,
                        mode: 'deep',
                        ts: Date.now(),
                      }));
                    } catch { /* noop */ }
                    // Update URL for shareability — matches digest email URL pattern
                    try {
                      const url = new URL(window.location.href);
                      url.searchParams.set('river', 'athena');
                      url.searchParams.set('brief', title);
                      if (keyword) url.searchParams.set('keyword', keyword);
                      if (angle) url.searchParams.set('angle', angle);
                      window.history.pushState({}, '', url.toString());
                    } catch { /* noop */ }
                    // Switch tab
                    if (onNavigate) onNavigate('generate');
                  };
                  const angleLabelMap = {
                    'thought_leadership': 'Thought Leadership',
                    'counter_competitor': 'Counter-Competitor',
                    'how_to_guide': 'How-To Guide',
                    'case_study': 'Case Study',
                    'news_reaction': 'News Reaction',
                    'hot_take': 'Hot Take',
                  };
                  const angleDisplay = angleLabelMap[angle] || angle.replace(/_/g, ' ');
                  return (
                    <div key={i} className="bg-white rounded-xl p-5 border-2 border-purple-200 shadow-md hover:shadow-xl hover:border-purple-400 transition-all flex flex-col" data-testid={`forge-brief-${i}`}>
                      <div className="text-xs font-bold text-purple-600 uppercase tracking-widest mb-2">#{b.rank || i + 1} · {angleDisplay}</div>
                      <h4 className="font-black text-gray-900 mb-2 text-base leading-tight">{b.suggested_title || 'Untitled brief'}</h4>
                      {b.target_keyword && (
                        <div className="mb-2">
                          <span className="inline-block px-2 py-1 rounded bg-purple-100 text-purple-700 text-xs font-bold">🎯 {b.target_keyword}</span>
                        </div>
                      )}
                      {b.why_it_matters && <p className="text-sm text-gray-600 mb-3 flex-1">{b.why_it_matters}</p>}
                      {b.estimated_word_count && (
                        <div className="text-xs text-gray-500 mb-3">~{b.estimated_word_count} words</div>
                      )}
                      <button
                        onClick={handleForge}
                        className="mt-auto w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-lg shadow-md transition-all text-sm"
                        data-testid={`forge-brief-btn-${i}`}
                      >
                        Send to Content Forge →
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-5 bg-green-50 border-2 border-green-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">✅</span>
                <span className="font-bold text-green-900">Analysis Complete!</span>
              </div>
              <p className="text-sm text-green-800 font-medium">
                Your competitive insights are ready
              </p>
            </div>
            <div className="p-5 bg-purple-50 border-2 border-purple-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">💎</span>
                <span className="font-bold text-purple-900">Credits Used</span>
              </div>
              <p className="text-sm text-purple-800 font-medium">
                {analysis.credits_used} credits | {analysis.credits_remaining} remaining
              </p>
            </div>
            <div className="p-5 bg-blue-50 border-2 border-blue-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">📚</span>
                <span className="font-bold text-blue-900">Auto-Saved</span>
              </div>
              <p className="text-sm text-blue-800 font-medium">
                Access anytime in your history
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Saved Analyses Modal */}
      {showSaved && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 fade-in">
          <div className="glass-effect bg-white rounded-3xl p-10 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-vibrant border-2 border-purple-200">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-3xl font-bold gradient-text flex items-center">
                <span className="mr-3">📚</span> Saved Analyses
              </h3>
              <button
                onClick={() => setShowSaved(false)}
                className="text-gray-400 hover:text-gray-700 text-3xl font-bold transition-smooth"
              >
                ✕
              </button>
            </div>
            
            {savedAnalyses.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-6xl block mb-4">🔍</span>
                <p className="text-gray-600 text-lg">No saved analyses yet</p>
                <p className="text-gray-500 text-sm mt-2">Analyze your first competitor to see it here!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {savedAnalyses.map((savedAnalysis, idx) => (
                  <div key={idx} className="card-hover p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200 hover:shadow-lg transition-smooth cursor-pointer"
                    onClick={() => {
                      setAnalysis(savedAnalysis);
                      setCompetitorUrl(savedAnalysis.competitor_url);
                      setYourTopic(savedAnalysis.your_topic || '');
                      setShowSaved(false);
                    }}>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">🔗 {savedAnalysis.competitor_url}</h4>
                    <p className="text-sm text-gray-600 mb-3">{savedAnalysis.your_topic || 'No topic specified'}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>📅 {new Date(savedAnalysis.created_at).toLocaleDateString()}</span>
                      <span>💎 {savedAnalysis.credits_used} credits</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Content Calendar Generator Component
const ContentCalendarGenerator = () => {
  const [industry, setIndustry] = useState('');
  const [timeframeDays, setTimeframeDays] = useState(30);
  const [contentTypes, setContentTypes] = useState(['blog', 'social']);
  const [platforms, setPlatforms] = useState(['linkedin', 'twitter']);
  const [goals, setGoals] = useState('traffic');
  const [postingFrequency, setPostingFrequency] = useState('daily');
  const [includeHolidays, setIncludeHolidays] = useState(true);
  const [loading, setLoading] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [error, setError] = useState('');
  const [savedCalendars, setSavedCalendars] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    fetchSavedCalendars();
  }, []);

  const fetchSavedCalendars = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/calendars/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedCalendars(data.calendars || []);
      }
    } catch (err) {
      console.error('Error fetching saved calendars:', err);
    }
  };

  const toggleContentType = (type) => {
    setContentTypes(prev => 
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const togglePlatform = (platform) => {
    setPlatforms(prev => 
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const generateCalendar = async () => {
    if (!industry.trim()) {
      setError('Please enter your industry/niche');
      return;
    }

    if (contentTypes.length === 0) {
      setError('Please select at least one content type');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          industry,
          timeframe_days: timeframeDays,
          content_types: contentTypes,
          platforms: platforms,
          goals: goals,
          posting_frequency: postingFrequency,
          include_holidays: includeHolidays
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate calendar');
      }

      setCalendar(data);
      fetchSavedCalendars(); // Refresh saved calendars
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyCalendar = async () => {
    if (calendar) {
      const success = await copyToClipboard(calendar.content);
      alert(success ? '✅ Content calendar copied to clipboard!' : '❌ Copy failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="fade-in-up mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-4xl font-bold gradient-text flex items-center">
            <span className="mr-3">📅</span> AI Content Calendar Generator
          </h2>
          <button
            onClick={() => setShowSaved(!showSaved)}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-semibold hover:bg-purple-200 transition-smooth"
          >
            📚 Saved Calendars ({savedCalendars.length})
          </button>
        </div>
        <p className="text-gray-600 text-lg leading-relaxed">
          Generate a complete content strategy in seconds! Get 30-90 days of content topics, titles, publishing schedule, and platform recommendations.
        </p>
        <div className="badge-gradient mt-4 inline-block px-4 py-2 text-sm font-bold">
          PRO Feature - 10 Credits
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-2">
          <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-gray-200 fade-in-up delay-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">⚙️</span> Calendar Configuration
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🏢</span> Industry / Niche *
                </label>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g., 'SaaS marketing', 'Fitness coaching', 'E-commerce fashion'"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">📅</span> Timeframe
                </label>
                <div className="flex gap-3">
                  {[30, 60, 90].map(days => (
                    <button
                      key={days}
                      onClick={() => setTimeframeDays(days)}
                      className={`flex-1 px-6 py-4 rounded-xl font-bold text-lg transition-smooth border-2 ${
                        timeframeDays === days
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-purple-600 shadow-md'
                          : 'bg-gray-50 text-gray-700 border-gray-300 hover:border-purple-400'
                      }`}
                    >
                      {days} Days
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">📝</span> Content Types (Select Multiple)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { id: 'blog', label: '📝 Blog Posts', emoji: '📝' },
                    { id: 'social', label: '📱 Social Media', emoji: '📱' },
                    { id: 'video', label: '🎥 Videos', emoji: '🎥' },
                    { id: 'email', label: '✉️ Email', emoji: '✉️' },
                    { id: 'podcast', label: '🎙️ Podcast', emoji: '🎙️' },
                    { id: 'infographic', label: '📊 Infographic', emoji: '📊' }
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => toggleContentType(type.id)}
                      className={`px-4 py-3 rounded-xl font-semibold transition-smooth border-2 ${
                        contentTypes.includes(type.id)
                          ? 'bg-purple-100 text-purple-700 border-purple-400'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {type.emoji} {type.label.split(' ')[1]}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🎯</span> Target Platforms (Select Multiple)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { id: 'linkedin', label: 'LinkedIn', emoji: '💼' },
                    { id: 'twitter', label: 'Twitter', emoji: '🐦' },
                    { id: 'instagram', label: 'Instagram', emoji: '📸' },
                    { id: 'facebook', label: 'Facebook', emoji: '📘' },
                    { id: 'youtube', label: 'YouTube', emoji: '🎥' },
                    { id: 'tiktok', label: 'TikTok', emoji: '🎵' },
                    { id: 'pinterest', label: 'Pinterest', emoji: '📌' },
                    { id: 'blog', label: 'Blog', emoji: '📝' }
                  ].map(platform => (
                    <button
                      key={platform.id}
                      onClick={() => togglePlatform(platform.id)}
                      className={`px-3 py-2 rounded-xl font-semibold text-sm transition-smooth border-2 ${
                        platforms.includes(platform.id)
                          ? 'bg-blue-100 text-blue-700 border-blue-400'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {platform.emoji} {platform.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">🎯</span> Primary Goal
                  </label>
                  <select
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  >
                    <option value="traffic">🚀 Drive Traffic</option>
                    <option value="engagement">💬 Boost Engagement</option>
                    <option value="leads">📈 Generate Leads</option>
                    <option value="brand">🏆 Build Brand</option>
                    <option value="sales">💰 Increase Sales</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">⏰</span> Posting Frequency
                  </label>
                  <select
                    value={postingFrequency}
                    onChange={(e) => setPostingFrequency(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  >
                    <option value="daily">📅 Daily</option>
                    <option value="3x-week">🗓️ 3x per Week</option>
                    <option value="weekly">📆 Weekly</option>
                    <option value="2x-week">📋 2x per Week</option>
                  </select>
                </div>
              </div>

              <div className="p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeHolidays}
                    onChange={(e) => setIncludeHolidays(e.target.checked)}
                    className="w-6 h-6 text-green-600 rounded focus:ring-2 focus:ring-green-500 cursor-pointer"
                  />
                  <span className="ml-4 font-bold text-gray-900 text-lg">
                    🎉 Include Holidays & Special Events
                  </span>
                </label>
                <p className="text-sm text-gray-600 mt-2 ml-10">
                  Automatically include relevant holidays, awareness days, and seasonal events in your calendar
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-300 text-red-800 px-6 py-4 rounded-xl font-semibold">
                  ⚠️ {error}
                </div>
              )}

              <button
                onClick={generateCalendar}
                disabled={loading || !industry || contentTypes.length === 0}
                className="btn-primary-enhanced w-full py-5 text-white rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating Calendar...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    📅 Generate Content Calendar
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar with tips */}
        <div className="lg:col-span-1">
          <div className="glass-effect bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl shadow-vibrant p-8 border-2 border-purple-200 sticky top-8 fade-in-up delay-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">💡</span> What You Get
            </h3>
            <div className="space-y-5">
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-purple-700 mb-2 flex items-center">
                  <span className="mr-2">📝</span> Content Topics
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Ready-to-use content ideas optimized for your industry and goals
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-blue-700 mb-2 flex items-center">
                  <span className="mr-2">⏰</span> Posting Schedule
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Optimal dates and times for maximum engagement on each platform
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-green-700 mb-2 flex items-center">
                  <span className="mr-2">🎯</span> Content Mix
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Balanced strategy with educational, promotional, and engagement content
                </p>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-sm">
                <h4 className="font-bold text-orange-700 mb-2 flex items-center">
                  <span className="mr-2">🎉</span> Holiday Tie-ins
                </h4>
                <p className="text-sm text-gray-700 leading-relaxed">
                  Leverage trending events and holidays for timely, relevant content
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {calendar && (
        <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-10 border-2 border-gray-200 fade-in-up delay-300">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
              <h3 className="text-3xl font-bold gradient-text flex items-center">
                <span className="mr-3">📅</span> Your {calendar.timeframe_days}-Day Content Calendar
              </h3>
              <p className="text-gray-600 mt-2">{calendar.total_posts || 0} content pieces planned</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
                className="px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:shadow-md font-bold transition-smooth border-2 border-gray-300"
              >
                {viewMode === 'list' ? '📊 Grid View' : '📝 List View'}
              </button>
              <button
                onClick={copyCalendar}
                className="px-6 py-3 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 rounded-xl hover:shadow-md font-bold transition-smooth border-2 border-purple-200"
              >
                📋 Copy
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([calendar.content], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `content-calendar-${Date.now()}.txt`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-6 py-3 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-xl hover:shadow-md font-bold transition-smooth border-2 border-green-200"
              >
                💾 Download
              </button>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-8 border-2 border-purple-200 mb-6">
            <div className="prose prose-lg max-w-none">
              <pre className="whitespace-pre-wrap bg-white p-6 rounded-xl text-base text-gray-800 leading-relaxed font-sans shadow-sm border-2 border-gray-200">
{calendar.content}
              </pre>
            </div>
          </div>
          
          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-5 bg-green-50 border-2 border-green-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">✅</span>
                <span className="font-bold text-green-900">Calendar Ready!</span>
              </div>
              <p className="text-sm text-green-800 font-medium">
                {calendar.timeframe_days} days of content planned
              </p>
            </div>
            <div className="p-5 bg-purple-50 border-2 border-purple-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">💎</span>
                <span className="font-bold text-purple-900">Credits Used</span>
              </div>
              <p className="text-sm text-purple-800 font-medium">
                {calendar.credits_used} credits | {calendar.credits_remaining} remaining
              </p>
            </div>
            <div className="p-5 bg-blue-50 border-2 border-blue-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">📚</span>
                <span className="font-bold text-blue-900">Auto-Saved</span>
              </div>
              <p className="text-sm text-blue-800 font-medium">
                Access anytime in your history
              </p>
            </div>
            <div className="p-5 bg-orange-50 border-2 border-orange-300 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">🚀</span>
                <span className="font-bold text-orange-900">Ready to Publish</span>
              </div>
              <p className="text-sm text-orange-800 font-medium">
                Start executing your strategy today
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Saved Calendars Modal */}
      {showSaved && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4 fade-in">
          <div className="glass-effect bg-white rounded-3xl p-10 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-vibrant border-2 border-purple-200">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-3xl font-bold gradient-text flex items-center">
                <span className="mr-3">📚</span> Saved Calendars
              </h3>
              <button
                onClick={() => setShowSaved(false)}
                className="text-gray-400 hover:text-gray-700 text-3xl font-bold transition-smooth"
              >
                ✕
              </button>
            </div>
            
            {savedCalendars.length === 0 ? (
              <div className="text-center py-12">
                <span className="text-6xl block mb-4">📅</span>
                <p className="text-gray-600 text-lg">No saved calendars yet</p>
                <p className="text-gray-500 text-sm mt-2">Generate your first content calendar to see it here!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {savedCalendars.map((savedCal, idx) => (
                  <div key={idx} className="card-hover p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200 hover:shadow-lg transition-smooth cursor-pointer"
                    onClick={() => {
                      setCalendar(savedCal);
                      setIndustry(savedCal.industry);
                      setTimeframeDays(savedCal.timeframe_days);
                      setShowSaved(false);
                    }}>
                    <h4 className="font-bold text-lg text-gray-900 mb-2">🏢 {savedCal.industry}</h4>
                    <p className="text-sm text-gray-600 mb-3">{savedCal.timeframe_days} days | {savedCal.content_types?.join(', ') || 'Multiple types'}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>📅 {new Date(savedCal.created_at).toLocaleDateString()}</span>
                      <span>💎 {savedCal.credits_used} credits</span>
                      <span>🎯 {savedCal.goals || 'Multiple goals'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// CUTTING-EDGE FEATURES - ROI Calculator
// ============================================

// Content ROI Calculator Component
const ContentROICalculator = () => {
  const [contentType, setContentType] = useState('blog_post');
  const [industry, setIndustry] = useState('');
  const [audienceSize, setAudienceSize] = useState(10000);
  const [conversionRate, setConversionRate] = useState(2.5);
  const [dealValue, setDealValue] = useState(997);
  const [contentTitle, setContentTitle] = useState('');
  const [contentDescription, setContentDescription] = useState('');
  const [timeframe, setTimeframe] = useState('12_months');
  const [useIndustryDefaults, setUseIndustryDefaults] = useState(true);
  const [loading, setLoading] = useState(false);
  const [roiData, setRoiData] = useState(null);
  const [error, setError] = useState('');
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [realTimeROI, setRealTimeROI] = useState(null);

  useEffect(() => {
    fetchSavedCalculations();
  }, []);

  useEffect(() => {
    // Real-time ROI estimation
    if (audienceSize && conversionRate && dealValue) {
      const estimatedLeads = (audienceSize * conversionRate / 100);
      const estimatedRevenue = estimatedLeads * dealValue;
      setRealTimeROI({
        leads: Math.round(estimatedLeads),
        revenue: Math.round(estimatedRevenue)
      });
    }
  }, [audienceSize, conversionRate, dealValue]);

  const fetchSavedCalculations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/roi-calculations/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedCalculations(data.calculations || []);
      }
    } catch (err) {
      console.error('Error fetching saved calculations:', err);
    }
  };

  const calculateROI = async () => {
    if (!contentType || !industry || !audienceSize || !contentTitle) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/roi-calculator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          content_type: contentType,
          industry,
          audience_size: parseInt(audienceSize),
          current_conversion_rate: conversionRate ? parseFloat(conversionRate) : null,
          average_deal_value: dealValue ? parseFloat(dealValue) : null,
          content_title: contentTitle,
          content_description: contentDescription
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to calculate ROI');
      }

      setRoiData(data);
      // Credits will refresh on next page load
    } catch (err) {
      setError(err.message || String(err) || 'Failed to calculate ROI');
    } finally {
      setLoading(false);
    }
  };

  const copyROI = async () => {
    if (roiData) {
      const success = await copyToClipboard(roiData.analysis);
      alert(success ? '✅ ROI analysis copied to clipboard!' : '❌ Copy failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="fade-in-up mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-4xl font-bold gradient-text flex items-center">
            <span className="mr-3">💰</span> Content ROI Calculator
          </h2>
          <button
            onClick={() => setShowSaved(!showSaved)}
            className="px-4 py-2 bg-green-100 text-green-700 rounded-xl font-semibold hover:bg-green-200 transition-smooth"
          >
            📚 Saved ({savedCalculations.length})
          </button>
        </div>
        <p className="text-gray-600 text-lg leading-relaxed">
          Predict revenue before you publish! Get data-driven traffic projections, conversion estimates, and revenue forecasts.
        </p>
        <div className="badge-gradient mt-4 inline-block px-4 py-2 text-sm font-bold">
          PRO Feature - 8 Credits
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-2">
          <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-gray-200 fade-in-up delay-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">⚙️</span> ROI Configuration
            </h3>

            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Content Type */}
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">📝</span> Content Type *
                  </label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-smooth font-medium"
                  >
                    <option value="blog_post">📝 Blog Post</option>
                    <option value="social_media">📱 Social Media Post</option>
                    <option value="email">✉️ Email Campaign</option>
                    <option value="video">🎥 Video Content</option>
                    <option value="infographic">📊 Infographic</option>
                    <option value="ebook">📚 E-book / Guide</option>
                    <option value="webinar">🎤 Webinar</option>
                    <option value="podcast">🎙️ Podcast Episode</option>
                  </select>
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">🏢</span> Industry / Niche *
                  </label>
                  <input
                    type="text"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    placeholder="e.g., SaaS, E-commerce, Real Estate"
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-smooth font-medium"
                  />
                </div>
              </div>

              {/* Use Industry Defaults Toggle */}
              <div className="p-5 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useIndustryDefaults}
                    onChange={(e) => setUseIndustryDefaults(e.target.checked)}
                    className="w-6 h-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  />
                  <span className="ml-4 font-bold text-gray-900 text-lg">
                    📊 Use Industry Average Benchmarks
                  </span>
                </label>
                <p className="text-sm text-gray-600 mt-2 ml-10">
                  Automatically apply typical conversion rates and deal values for your industry
                </p>
              </div>

              {!useIndustryDefaults && (
                <>
                  {/* Audience Size Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-lg font-bold text-gray-900">
                        <span className="mr-2">👥</span> Target Audience Size
                      </label>
                      <span className="text-2xl font-bold text-green-600">{audienceSize.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min="1000"
                      max="1000000"
                      step="1000"
                      value={audienceSize}
                      onChange={(e) => setAudienceSize(parseInt(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-green"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>1K</span>
                      <span>500K</span>
                      <span>1M</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      💡 Email list size, social followers, monthly website visitors, etc.
                    </p>
                  </div>

                  {/* Conversion Rate Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-lg font-bold text-gray-900">
                        <span className="mr-2">🎯</span> Conversion Rate
                      </label>
                      <span className="text-2xl font-bold text-blue-600">{conversionRate}%</span>
                    </div>
                    <input
                      type="range"
                      min="0.1"
                      max="15"
                      step="0.1"
                      value={conversionRate}
                      onChange={(e) => setConversionRate(parseFloat(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-blue"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>0.1%</span>
                      <span>7.5%</span>
                      <span>15%</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      💡 Percentage of audience that converts to leads/customers
                    </p>
                  </div>

                  {/* Deal Value Slider */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-lg font-bold text-gray-900">
                        <span className="mr-2">💵</span> Average Deal Value
                      </label>
                      <span className="text-2xl font-bold text-purple-600">${dealValue.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="10000"
                      step="10"
                      value={dealValue}
                      onChange={(e) => setDealValue(parseInt(e.target.value))}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-purple"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span>$10</span>
                      <span>$5K</span>
                      <span>$10K</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">
                      💡 Average revenue per customer or client
                    </p>
                  </div>
                </>
              )}

              {/* Timeframe Selection */}
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">📅</span> Forecast Timeframe
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: '30_days', label: '30 Days' },
                    { value: '90_days', label: '90 Days' },
                    { value: '6_months', label: '6 Months' },
                    { value: '12_months', label: '12 Months' }
                  ].map(tf => (
                    <button
                      key={tf.value}
                      onClick={() => setTimeframe(tf.value)}
                      className={`px-4 py-3 rounded-xl font-semibold transition-smooth border-2 ${
                        timeframe === tf.value
                          ? 'bg-green-100 text-green-700 border-green-400'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {tf.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content Title & Description */}
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">✨</span> Content Title *
                </label>
                <input
                  type="text"
                  value={contentTitle}
                  onChange={(e) => setContentTitle(e.target.value)}
                  placeholder="e.g., '10 Proven Strategies to Double Your Revenue'"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-smooth font-medium"
                />
              </div>

              {/* Content Description */}
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">📄</span> Content Description (Optional)
                </label>
                <textarea
                  value={contentDescription}
                  onChange={(e) => setContentDescription(e.target.value)}
                  placeholder="Brief description of what the content covers..."
                  rows="4"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-smooth font-medium"
                />
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-300 text-red-800 px-6 py-4 rounded-xl font-semibold">
                  ⚠️ {error}
                </div>
              )}

              <button
                onClick={calculateROI}
                disabled={loading || !contentTitle || !industry}
                className="btn-primary-enhanced w-full py-5 text-white rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-green-600 to-emerald-600"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Calculating Your ROI...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    💰 Calculate Detailed ROI Forecast
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Real-Time Preview Sidebar */}
        <div className="lg:col-span-1">
          <div className="glass-effect bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-vibrant p-8 border-2 border-green-300 sticky top-8 fade-in-up delay-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">⚡</span> Live Preview
            </h3>
            
            {realTimeROI ? (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-green-200">
                  <p className="text-sm text-gray-600 font-semibold mb-2">Estimated Leads</p>
                  <p className="text-4xl font-bold text-green-600 mb-1">{realTimeROI.leads.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Based on {conversionRate}% conversion</p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-emerald-200">
                  <p className="text-sm text-gray-600 font-semibold mb-2">Estimated Revenue</p>
                  <p className="text-4xl font-bold text-emerald-600 mb-1">${realTimeROI.revenue.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">At ${dealValue} per customer</p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-5 rounded-xl border-2 border-blue-200">
                  <p className="text-xs text-blue-800 font-bold mb-2">💡 QUICK MATH</p>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p>{audienceSize.toLocaleString()} people × {conversionRate}%</p>
                    <p>= {realTimeROI.leads.toLocaleString()} leads</p>
                    <p>× ${dealValue.toLocaleString()} per sale</p>
                    <p className="pt-2 border-t-2 border-blue-200 font-bold text-emerald-700">
                      = ${realTimeROI.revenue.toLocaleString()} revenue
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 p-4 rounded-xl border-2 border-yellow-200">
                  <p className="text-xs text-yellow-900 font-semibold flex items-start gap-2">
                    <span>⚡</span>
                    <span>Click "Calculate" for comprehensive AI-powered analysis with industry benchmarks and optimization tips!</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <span className="text-6xl block mb-4">📊</span>
                <p className="text-gray-600">Adjust sliders to see<br/>live ROI preview</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {roiData && (
        <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-green-200">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Your ROI Prediction</h3>
              <p className="text-sm text-gray-600 mt-1">Data-driven revenue forecast for your content</p>
            </div>
            <button
              onClick={copyROI}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold shadow-md"
            >
              📋 Copy Full Analysis
            </button>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-blue-50 p-6 rounded-lg mb-6 border border-green-200">
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap bg-white p-6 rounded-lg text-sm text-gray-800 leading-relaxed shadow-inner border border-gray-200">
                {roiData.analysis}
              </pre>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-100 p-4 rounded-lg text-center border border-green-300">
              <p className="text-green-800 font-semibold text-sm">Credits Used</p>
              <p className="text-3xl font-bold text-green-900 mt-2">{roiData.credits_used}</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-lg text-center border border-blue-300">
              <p className="text-blue-800 font-semibold text-sm">Credits Remaining</p>
              <p className="text-3xl font-bold text-blue-900 mt-2">{roiData.credits_remaining}</p>
            </div>
            <div className="bg-purple-100 p-4 rounded-lg text-center border border-purple-300">
              <p className="text-purple-800 font-semibold text-sm">Calculation ID</p>
              <p className="text-xs font-mono text-purple-900 mt-2">{roiData.calculation_id}</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              💡 <strong>Pro Tip:</strong> Use this ROI data to prioritize content creation, justify content budgets, and make strategic marketing decisions. Save this analysis for future reference!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Content Decay Alerter Component
const ContentDecayAlerter = () => {
  const [contentUrl, setContentUrl] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [publishedDate, setPublishedDate] = useState('');
  const [contentType, setContentType] = useState('blog_post');
  const [industry, setIndustry] = useState('');
  const [currentTraffic, setCurrentTraffic] = useState('');
  const [originalTraffic, setOriginalTraffic] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState('');
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [decayScore, setDecayScore] = useState(null);

  useEffect(() => {
    fetchSavedAnalyses();
  }, []);

  useEffect(() => {
    // Calculate real-time decay score
    if (publishedDate && currentTraffic && originalTraffic) {
      const monthsOld = Math.floor((new Date() - new Date(publishedDate)) / (1000 * 60 * 60 * 24 * 30));
      const trafficDrop = ((originalTraffic - currentTraffic) / originalTraffic * 100).toFixed(1);
      const score = Math.max(0, 100 - (trafficDrop * 0.5) - (monthsOld * 2));
      setDecayScore({
        score: Math.round(score),
        monthsOld,
        trafficDrop,
        status: score > 70 ? 'healthy' : score > 40 ? 'warning' : 'critical'
      });
    }
  }, [publishedDate, currentTraffic, originalTraffic]);

  const fetchSavedAnalyses = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/decay-analyses/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedAnalyses(data.analyses || []);
      }
    } catch (err) {
      console.error('Error fetching saved analyses:', err);
    }
  };

  const analyzeDecay = async () => {
    if (!contentUrl || !contentTitle || !publishedDate || !industry) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/decay-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          content_url: contentUrl,
          content_title: contentTitle,
          published_date: publishedDate,
          content_type: contentType,
          industry
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to analyze content decay');
      }

      setAnalysis(data);
      // Credits will refresh on next page load
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyAnalysis = async () => {
    if (analysis) {
      const success = await copyToClipboard(analysis.analysis);
      alert(success ? '✅ Content decay analysis copied to clipboard!' : '❌ Copy failed');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="fade-in-up mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-4xl font-bold gradient-text flex items-center">
            <span className="mr-3">⚠️</span> Content Decay Alerter
          </h2>
          <button
            onClick={() => setShowSaved(!showSaved)}
            className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl font-semibold hover:bg-orange-200 transition-smooth"
          >
            📚 History ({savedAnalyses.length})
          </button>
        </div>
        <p className="text-gray-600 text-lg leading-relaxed">
          Monitor published content for decay. Get decay scores, traffic trends, and AI-powered refresh recommendations to maintain rankings.
        </p>
        <div className="badge-gradient mt-4 inline-block px-4 py-2 text-sm font-bold">
          PRO Feature - 6 Credits
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-2">
          <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-gray-200 fade-in-up delay-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">🔍</span> Content Analysis
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">🔗</span> Content URL *
                </label>
                <input
                  type="url"
                  value={contentUrl}
                  onChange={(e) => setContentUrl(e.target.value)}
                  placeholder="https://yourblog.com/your-article"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-smooth font-medium"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">✍️</span> Content Title *
                  </label>
                  <input
                    type="text"
                    value={contentTitle}
                    onChange={(e) => setContentTitle(e.target.value)}
                    placeholder="e.g., 'Ultimate Guide to Email Marketing'"
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-smooth font-medium"
                  />
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">📅</span> Published Date *
                  </label>
                  <input
                    type="date"
                    value={publishedDate}
                    onChange={(e) => setPublishedDate(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-smooth font-medium"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">📝</span> Content Type
                  </label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-smooth font-medium"
                  >
                    <option value="blog_post">📝 Blog Post</option>
                    <option value="article">📰 Article</option>
                    <option value="guide">📚 Guide / Tutorial</option>
                    <option value="case_study">💼 Case Study</option>
                    <option value="whitepaper">📊 Whitepaper</option>
                    <option value="resource">🔖 Resource Page</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">🏢</span> Industry *
                  </label>
                  <input
                    type="text"
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    placeholder="e.g., 'SaaS', 'E-commerce', 'Marketing'"
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-smooth font-medium"
                  />
                </div>
              </div>

              {/* Traffic Data - Creative Addition */}
              <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📊</span> Traffic Data (Optional - For Better Analysis)
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Original Monthly Traffic
                    </label>
                    <input
                      type="number"
                      value={originalTraffic}
                      onChange={(e) => setOriginalTraffic(e.target.value)}
                      placeholder="e.g., 5000"
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-smooth"
                    />
                    <p className="text-xs text-gray-600 mt-1">Traffic when first published</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Current Monthly Traffic
                    </label>
                    <input
                      type="number"
                      value={currentTraffic}
                      onChange={(e) => setCurrentTraffic(e.target.value)}
                      placeholder="e.g., 2500"
                      className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-smooth"
                    />
                    <p className="text-xs text-gray-600 mt-1">Current monthly traffic</p>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-300 text-red-800 px-6 py-4 rounded-xl font-semibold">
                  ⚠️ {error}
                </div>
              )}

              <button
                onClick={analyzeDecay}
                disabled={loading || !contentUrl || !contentTitle || !publishedDate || !industry}
                className="btn-primary-enhanced w-full py-5 text-white rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-red-600 to-orange-600"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing Content Decay...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    ⚠️ Analyze Content Health & Get Refresh Plan
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Real-Time Decay Preview Sidebar - CREATIVE FEATURE */}
        <div className="lg:col-span-1">
          <div className="glass-effect bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl shadow-vibrant p-8 border-2 border-orange-300 sticky top-8 fade-in-up delay-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">⚡</span> Decay Preview
            </h3>
            
            {decayScore ? (
              <div className="space-y-6">
                {/* Decay Score Meter - CREATIVE VISUAL */}
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-orange-200">
                  <p className="text-sm text-gray-600 font-semibold mb-3 text-center">Content Health Score</p>
                  <div className="relative">
                    {/* Circular Progress */}
                    <div className="relative w-32 h-32 mx-auto">
                      <svg className="transform -rotate-90 w-32 h-32">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="#e5e7eb"
                          strokeWidth="12"
                          fill="none"
                        />
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke={decayScore.status === 'healthy' ? '#10b981' : decayScore.status === 'warning' ? '#f59e0b' : '#ef4444'}
                          strokeWidth="12"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 56}`}
                          strokeDashoffset={`${2 * Math.PI * 56 * (1 - decayScore.score / 100)}`}
                          strokeLinecap="round"
                          className="transition-all duration-1000"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`text-4xl font-bold ${decayScore.status === 'healthy' ? 'text-green-600' : decayScore.status === 'warning' ? 'text-orange-600' : 'text-red-600'}`}>
                          {decayScore.score}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <span className={`inline-block px-4 py-2 rounded-full font-bold text-sm ${
                      decayScore.status === 'healthy' ? 'bg-green-100 text-green-800 border-2 border-green-300' :
                      decayScore.status === 'warning' ? 'bg-orange-100 text-orange-800 border-2 border-orange-300' :
                      'bg-red-100 text-red-800 border-2 border-red-300'
                    }`}>
                      {decayScore.status === 'healthy' ? '✅ Healthy' : decayScore.status === 'warning' ? '⚠️ Needs Attention' : '🚨 Critical'}
                    </span>
                  </div>
                </div>

                {/* Traffic Trend */}
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-red-200">
                  <p className="text-sm text-gray-600 font-semibold mb-3">Traffic Change</p>
                  <p className="text-3xl font-bold text-red-600 mb-2">-{decayScore.trafficDrop}%</p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(decayScore.trafficDrop, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">From {originalTraffic} to {currentTraffic} visitors/month</p>
                </div>

                {/* Age Indicator */}
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-orange-200">
                  <p className="text-sm text-gray-600 font-semibold mb-2">Content Age</p>
                  <p className="text-3xl font-bold text-orange-600">{decayScore.monthsOld}</p>
                  <p className="text-sm text-gray-700 mt-1">months old</p>
                  <div className="mt-3 pt-3 border-t-2 border-gray-100">
                    <p className="text-xs text-gray-600">
                      {decayScore.monthsOld > 18 ? '🚨 Needs major refresh' :
                       decayScore.monthsOld > 12 ? '⚠️ Update recommended' :
                       decayScore.monthsOld > 6 ? '💡 Consider updating' :
                       '✅ Still fresh'}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 p-4 rounded-xl border-2 border-yellow-200">
                  <p className="text-xs text-yellow-900 font-semibold flex items-start gap-2">
                    <span>💡</span>
                    <span>Click "Analyze" for detailed decay analysis, specific refresh recommendations, and SEO recovery tactics!</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <span className="text-6xl block mb-4">📊</span>
                <p className="text-gray-600">Fill in traffic data to see<br/>real-time decay preview</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {analysis && (
        <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-red-200">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Content Health Report</h3>
              <p className="text-sm text-gray-600 mt-1">Decay analysis and refresh recommendations</p>
            </div>
            <button
              onClick={copyAnalysis}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold shadow-md"
            >
              📋 Copy Full Report
            </button>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-orange-50 p-6 rounded-lg mb-6 border border-red-200">
            <div className="prose max-w-none">
              <pre className="whitespace-pre-wrap bg-white p-6 rounded-lg text-sm text-gray-800 leading-relaxed shadow-inner border border-gray-200">
                {analysis.analysis}
              </pre>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-red-100 p-4 rounded-lg text-center border border-red-300">
              <p className="text-red-800 font-semibold text-sm">Credits Used</p>
              <p className="text-3xl font-bold text-red-900 mt-2">{analysis.credits_used}</p>
            </div>
            <div className="bg-orange-100 p-4 rounded-lg text-center border border-orange-300">
              <p className="text-orange-800 font-semibold text-sm">Credits Remaining</p>
              <p className="text-3xl font-bold text-orange-900 mt-2">{analysis.credits_remaining}</p>
            </div>
            <div className="bg-yellow-100 p-4 rounded-lg text-center border border-yellow-300">
              <p className="text-yellow-800 font-semibold text-sm">Analysis ID</p>
              <p className="text-xs font-mono text-yellow-900 mt-2">{analysis.analysis_id}</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              📊 <strong>Next Steps:</strong> Use the decay risk score and refresh recommendations to prioritize content updates. Focus on high-decay content first for maximum impact. Track improvements after implementing changes!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Audience Sentiment Predictor Component
const AudienceSentimentPredictor = () => {
  const [title, setTitle] = useState('');
  const [contentBody, setContentBody] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState('blog');
  const [tone, setTone] = useState('neutral');
  const [contentType, setContentType] = useState('educational');
  const [loading, setLoading] = useState(false);
  const [prediction, setPrediction] = useState(null);
  const [error, setError] = useState('');
  const [savedPredictions, setSavedPredictions] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [quickSentiment, setQuickSentiment] = useState(null);

  useEffect(() => {
    fetchSavedPredictions();
  }, []);

  // Quick sentiment analysis based on word patterns - CREATIVE FEATURE
  useEffect(() => {
    if (contentBody.length > 50) {
      const positiveWords = ['great', 'amazing', 'excellent', 'wonderful', 'love', 'fantastic', 'best', 'awesome', 'perfect', 'brilliant'];
      const negativeWords = ['bad', 'worst', 'terrible', 'hate', 'awful', 'horrible', 'poor', 'disappointing', 'fail', 'disaster'];
      const controversialWords = ['always', 'never', 'everyone', 'nobody', 'impossible', 'stupid', 'dead', 'wrong'];
      
      const text = contentBody.toLowerCase();
      const positiveCount = positiveWords.filter(w => text.includes(w)).length;
      const negativeCount = negativeWords.filter(w => text.includes(w)).length;
      const controversialCount = controversialWords.filter(w => text.includes(w)).length;
      
      const sentiment = positiveCount > negativeCount + 1 ? 'positive' :
                       negativeCount > positiveCount + 1 ? 'negative' :
                       controversialCount > 2 ? 'controversial' : 'neutral';
      
      const riskLevel = controversialCount + negativeCount * 2;
      
      setQuickSentiment({
        sentiment,
        positiveCount,
        negativeCount,
        controversialCount,
        riskLevel: riskLevel > 5 ? 'high' : riskLevel > 2 ? 'medium' : 'low'
      });
    } else {
      setQuickSentiment(null);
    }
  }, [contentBody]);

  const fetchSavedPredictions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/sentiment-predictions/history`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedPredictions(data.predictions || []);
      }
    } catch (err) {
      console.error('Error fetching saved predictions:', err);
    }
  };

  const predictSentiment = async () => {
    if (!title || !contentBody || !targetAudience) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/sentiment-prediction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          content_title: title,
          content_body: contentBody,
          target_audience: targetAudience,
          content_topic: topic,
          platform
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to predict sentiment');
      }

      setPrediction(data);
      // Credits will refresh on next page load
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="fade-in-up mb-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-4xl font-bold gradient-text flex items-center">
            <span className="mr-3">😊😐😡</span> Audience Sentiment Predictor
          </h2>
          <button
            onClick={() => setShowSaved(!showSaved)}
            className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-semibold hover:bg-purple-200 transition-smooth"
          >
            📚 History ({savedPredictions.length})
          </button>
        </div>
        <p className="text-gray-600 text-lg leading-relaxed">
          Predict audience reactions BEFORE publishing. Get sentiment scores, emotion breakdowns, risk alerts, and optimization tips.
        </p>
        <div className="badge-gradient mt-4 inline-block px-4 py-2 text-sm font-bold">
          PRO Feature - 7 Credits
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
        <div className="lg:col-span-2">
          <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-8 border-2 border-gray-200 fade-in-up delay-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">✍️</span> Content Analysis
            </h3>

            <div className="space-y-6">
              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">✍️</span> Content Title *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., 'Why Traditional Marketing is Dead in 2025'"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
              </div>

              <div>
                <label className="block text-lg font-bold text-gray-900 mb-3">
                  <span className="mr-2">📝</span> Content Body * ({contentBody.length} chars)
                </label>
                <textarea
                  value={contentBody}
                  onChange={(e) => setContentBody(e.target.value)}
                  placeholder="Paste your full content here..."
                  rows="8"
                  className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">👥</span> Target Audience *
                  </label>
                  <input
                    type="text"
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    placeholder="e.g., 'SaaS founders, Marketing professionals'"
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  />
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">🏷️</span> Topic
                  </label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., 'Content Marketing'"
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">📱</span> Platform
                  </label>
                  <select
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  >
                    <option value="blog">📝 Blog</option>
                    <option value="linkedin">💼 LinkedIn</option>
                    <option value="twitter">🐦 Twitter</option>
                    <option value="instagram">📸 Instagram</option>
                    <option value="facebook">📘 Facebook</option>
                    <option value="youtube">🎥 YouTube</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">🎭</span> Content Tone
                  </label>
                  <select
                    value={tone}
                    onChange={(e) => setTone(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  >
                    <option value="professional">💼 Professional</option>
                    <option value="casual">😊 Casual</option>
                    <option value="humorous">😄 Humorous</option>
                    <option value="serious">🎓 Serious</option>
                    <option value="controversial">🔥 Controversial</option>
                    <option value="neutral">😐 Neutral</option>
                  </select>
                </div>

                <div>
                  <label className="block text-lg font-bold text-gray-900 mb-3">
                    <span className="mr-2">📊</span> Content Type
                  </label>
                  <select
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
                  >
                    <option value="educational">🎓 Educational</option>
                    <option value="promotional">💰 Promotional</option>
                    <option value="opinion">💭 Opinion/Editorial</option>
                    <option value="news">📰 News</option>
                    <option value="entertainment">🎭 Entertainment</option>
                  </select>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-300 text-red-800 px-6 py-4 rounded-xl font-semibold">
                  ⚠️ {error}
                </div>
              )}

              <button
                onClick={predictSentiment}
                disabled={loading || !title || !contentBody || !targetAudience}
                className="btn-primary-enhanced w-full py-5 text-white rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-purple-600 to-pink-600"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing Sentiment...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    😊😐😡 Predict Full Audience Sentiment Analysis
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Sentiment Preview Sidebar - CREATIVE EMOJI VISUALIZATION */}
        <div className="lg:col-span-1">
          <div className="glass-effect bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-vibrant p-8 border-2 border-purple-300 sticky top-8 fade-in-up delay-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <span className="mr-3">⚡</span> Quick Sentiment
            </h3>
            
            {quickSentiment ? (
              <div className="space-y-6">
                {/* Emoji Sentiment Display - CREATIVE */}
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-purple-200">
                  <p className="text-sm text-gray-600 font-semibold mb-4 text-center">Overall Sentiment</p>
                  <div className="text-center">
                    <div className="text-8xl mb-3 animate-pulse">
                      {quickSentiment.sentiment === 'positive' ? '😊' :
                       quickSentiment.sentiment === 'negative' ? '😡' :
                       quickSentiment.sentiment === 'controversial' ? '🔥' : '😐'}
                    </div>
                    <span className={`inline-block px-6 py-3 rounded-full font-bold text-lg ${
                      quickSentiment.sentiment === 'positive' ? 'bg-green-100 text-green-800 border-2 border-green-300' :
                      quickSentiment.sentiment === 'negative' ? 'bg-red-100 text-red-800 border-2 border-red-300' :
                      quickSentiment.sentiment === 'controversial' ? 'bg-orange-100 text-orange-800 border-2 border-orange-300' :
                      'bg-gray-100 text-gray-800 border-2 border-gray-300'
                    }`}>
                      {quickSentiment.sentiment.toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Word Analysis - CREATIVE BREAKDOWN */}
                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-green-200">
                  <p className="text-sm text-gray-600 font-semibold mb-3">Positive Signals</p>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-4xl">😊</span>
                    <span className="text-3xl font-bold text-green-600">{quickSentiment.positiveCount}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(quickSentiment.positiveCount * 20, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-red-200">
                  <p className="text-sm text-gray-600 font-semibold mb-3">Negative Signals</p>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-4xl">😡</span>
                    <span className="text-3xl font-bold text-red-600">{quickSentiment.negativeCount}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(quickSentiment.negativeCount * 20, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md border-2 border-orange-200">
                  <p className="text-sm text-gray-600 font-semibold mb-3">Controversial Words</p>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-4xl">🔥</span>
                    <span className="text-3xl font-bold text-orange-600">{quickSentiment.controversialCount}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-orange-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(quickSentiment.controversialCount * 15, 100)}%` }}
                    ></div>
                  </div>
                </div>

                {/* Risk Alert */}
                <div className={`p-5 rounded-xl border-2 ${
                  quickSentiment.riskLevel === 'high' ? 'bg-red-50 border-red-300' :
                  quickSentiment.riskLevel === 'medium' ? 'bg-yellow-50 border-yellow-300' :
                  'bg-green-50 border-green-300'
                }`}>
                  <p className="font-bold text-sm mb-2">
                    {quickSentiment.riskLevel === 'high' ? '🚨 HIGH RISK' :
                     quickSentiment.riskLevel === 'medium' ? '⚠️ MEDIUM RISK' :
                     '✅ LOW RISK'}
                  </p>
                  <p className="text-xs text-gray-700">
                    {quickSentiment.riskLevel === 'high' ? 'Consider revising controversial statements' :
                     quickSentiment.riskLevel === 'medium' ? 'Review tone and word choice' :
                     'Content appears well-balanced'}
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
                  <p className="text-xs text-blue-900 font-semibold flex items-start gap-2">
                    <span>💡</span>
                    <span>Click "Predict" for comprehensive AI analysis with demographic breakdowns, engagement predictions, and optimization tips!</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <span className="text-6xl block mb-4">😊😐😡</span>
                <p className="text-gray-600">Type 50+ characters<br/>to see sentiment preview</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {prediction && (
        <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-purple-200">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Sentiment Prediction Report</h3>
            <button
              onClick={async () => {
                const success = await copyToClipboard(prediction.prediction);
                alert(success ? '✅ Report copied!' : '❌ Copy failed');
              }}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold shadow-md"
            >
              📋 Copy Report
            </button>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg">
            <pre className="whitespace-pre-wrap bg-white p-6 rounded-lg text-sm text-gray-800 leading-relaxed">
              {prediction.prediction}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

// Multi-Format Content Explosion Component
const MultiFormatExplosion = () => {
  const [originalContent, setOriginalContent] = useState('');
  const [contentTitle, setContentTitle] = useState('');
  const [contentType, setContentType] = useState('blog_post');
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formats, setFormats] = useState(null);
  const [error, setError] = useState('');

  const platformOptions = ['twitter', 'linkedin', 'instagram', 'youtube', 'tiktok', 'facebook', 'pinterest', 'medium', 'reddit'];

  const togglePlatform = (platform) => {
    setPlatforms(prev => 
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  const explodeContent = async () => {
    if (!originalContent || !contentTitle || platforms.length === 0) {
      setError('Please fill in content, title, and select at least one platform');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/multi-format-explosion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          original_content: originalContent,
          content_title: contentTitle,
          content_type: contentType,
          target_platforms: platforms
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to explode content');
      }

      setFormats(data);
      // Credits will refresh on next page load
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg shadow-lg p-8 mb-6 border-2 border-blue-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">🎨 Multi-Format Content Explosion</h2>
            <p className="text-lg text-blue-700 font-semibold">1 Piece → 17+ Formats Instantly!</p>
          </div>
          <span className="bg-blue-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-md">
            PRO Feature - 12 Credits
          </span>
        </div>
        <p className="text-gray-700 text-base leading-relaxed">
          Transform ONE piece of content into 17+ formats: Twitter threads, LinkedIn carousels, Instagram captions, YouTube scripts, TikTok videos, email newsletters, and MORE! Maximum reach, minimum effort.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Original Content *</label>
            <textarea
              value={originalContent}
              onChange={(e) => setOriginalContent(e.target.value)}
              placeholder="Paste your blog post, article, or content here..."
              rows="6"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Title *</label>
              <input
                type="text"
                value={contentTitle}
                onChange={(e) => setContentTitle(e.target.value)}
                placeholder="e.g., '10 Marketing Strategies That Work'"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content Type</label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="blog_post">Blog Post</option>
                <option value="article">Article</option>
                <option value="guide">Guide</option>
                <option value="case_study">Case Study</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target Platforms * (Select multiple)</label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {platformOptions.map(platform => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  className={`px-3 py-2 rounded-lg font-medium text-sm ${
                    platforms.includes(platform)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
          )}

          <button
            onClick={explodeContent}
            disabled={loading}
            className={`w-full py-4 px-6 rounded-lg font-bold text-white text-lg ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 shadow-lg transform hover:scale-105 transition-all'
            }`}
          >
            {loading ? '🎨 Exploding Into Multiple Formats...' : '🎨 Explode Into 17+ Formats'}
          </button>
        </div>
      </div>

      {formats && (
        <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-blue-200">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{formats.formats_count} Formats Created!</h3>
              <p className="text-sm text-gray-600 mt-1">Ready to distribute across all platforms</p>
            </div>
            <button
              onClick={async () => {
                const success = await copyToClipboard(formats.formats);
                alert(success ? '✅ All formats copied!' : '❌ Copy failed');
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold shadow-md"
            >
              📋 Copy All Formats
            </button>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-lg">
            <pre className="whitespace-pre-wrap bg-white p-6 rounded-lg text-sm text-gray-800 leading-relaxed">
              {formats.formats}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

// Auto-Pilot Content Engine Component

// Quora Goldmine Component
const QuoraGoldmine = () => {
  const { user, fetchUserProfile } = React.useContext(AuthContext);
  const [settings, setSettings] = useState({
    niche_keywords: [],
    company_name: '',
    company_description: '',
    company_url: '',
    user_expertise: ''
  });
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchStats();
    fetchActivity();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/quora/settings`);
      if (response.data.niche_keywords?.length > 0) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching Quora settings:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/quora/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/quora/activity`);
      setActivity(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  const handleAddKeyword = () => {
    if (keywordInput.trim() && !settings.niche_keywords.includes(keywordInput.trim())) {
      setSettings({
        ...settings,
        niche_keywords: [...settings.niche_keywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setSettings({
      ...settings,
      niche_keywords: settings.niche_keywords.filter(k => k !== keyword)
    });
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setMessage('');

    try {
      await axios.post(`${API}/quora/settings`, settings);
      setMessage('Settings saved successfully! Install the Chrome extension to start.');
      fetchStats();
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to save settings');
    }

    setLoading(false);
  };

  const handleDownloadExtension = () => {
    // Download the Chrome extension ZIP file
    const downloadUrl = '/downloads/isupplyai-quora-extension.zip';
    window.open(downloadUrl, '_blank');
    
    // Show installation instructions
    setTimeout(() => {
      alert('📦 Extension Downloaded!\n\nInstallation Steps:\n\n1. Unzip the downloaded file\n2. Open Chrome and go to: chrome://extensions/\n3. Enable "Developer mode" (top right toggle)\n4. Click "Load unpacked"\n5. Select the unzipped folder\n6. Login with your iSupplyAI credentials\n\nDetailed instructions are included in the ZIP file!');
    }, 1000);
  };

  const getDailyLimitInfo = () => {
    if (!stats) return { limit: 0, remaining: 0, tierName: 'Free' };
    
    const tierInfo = {
      'free': { limit: 1, tierName: 'Free' },
      'pro': { limit: 5, tierName: 'Pro' },
      'pro_plus': { limit: 5, tierName: 'Pro' },
      'enterprise': { limit: 10, tierName: 'Enterprise' }
    };

    // Check for standalone subscription
    if (user?.quora_standalone_subscription) {
      if (user.quora_standalone_tier === 'quora_pro') {
        return { limit: 10, remaining: stats.remaining_today, tierName: 'Quora Pro (Standalone)' };
      }
      return { limit: 5, remaining: stats.remaining_today, tierName: 'Quora Basic (Standalone)' };
    }

    const info = tierInfo[user?.subscription_tier] || tierInfo['free'];
    return { ...info, remaining: stats.remaining_today };
  };

  const limitInfo = getDailyLimitInfo();

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-10 fade-in-up">
        <h2 className="text-4xl font-bold gradient-text mb-3 flex items-center">
          <span className="mr-3">🎯</span> Quora Goldmine
        </h2>
        <p className="text-gray-600 text-lg leading-relaxed">
          AI-powered Quora answer automation. Build authority, generate backlinks, and grow your brand organically.
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="card-hover glass-effect bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-300 shadow-vibrant fade-in-up delay-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <p className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-2">Answers Today</p>
                <p className="text-5xl font-bold text-blue-900">{stats.answers_today}</p>
              </div>
              <div className="text-6xl">📝</div>
            </div>
            <div className="mt-6">
              <div className="text-sm text-blue-800 font-semibold mb-3">
                {limitInfo.remaining} remaining today
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden shadow-inner">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-blue-700 h-3 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min((stats.answers_today / limitInfo.limit) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="text-xs text-blue-600 mt-2 font-medium">{limitInfo.tierName}</div>
            </div>
          </div>

          <div className="card-hover glass-effect bg-gradient-to-br from-green-50 to-emerald-100 rounded-2xl p-8 border-2 border-green-300 shadow-vibrant fade-in-up delay-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <p className="text-sm font-bold text-green-700 uppercase tracking-wide mb-2">Total Answers</p>
                <p className="text-5xl font-bold text-green-900">{stats.total_answers}</p>
              </div>
              <div className="text-6xl">🎯</div>
            </div>
            <div className="mt-6 pt-4 border-t-2 border-green-200">
              <p className="text-sm text-green-800 font-semibold">All-time contributions</p>
            </div>
          </div>

          <div className="card-hover glass-effect bg-gradient-to-br from-purple-50 to-pink-100 rounded-2xl p-8 border-2 border-purple-300 shadow-vibrant fade-in-up delay-300">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <p className="text-sm font-bold text-purple-700 uppercase tracking-wide mb-2">Daily Limit</p>
                <p className="text-5xl font-bold text-purple-900">{limitInfo.limit}</p>
              </div>
              <div className="text-6xl">⚡</div>
            </div>
            <div className="mt-6 pt-4 border-t-2 border-purple-200">
              <p className="text-sm text-purple-800 font-semibold">{limitInfo.tierName} tier</p>
            </div>
          </div>
        </div>
      )}

      {/* Setup Instructions */}
      <div className="glass-effect bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 mb-10 border-2 border-indigo-300 shadow-vibrant fade-in-up delay-400">
        <div className="flex items-start gap-6">
          <div className="text-6xl">🚀</div>
          <div className="flex-1">
            <h3 className="text-3xl font-bold text-gray-900 mb-6 gradient-text">Getting Started (5 Minutes Setup)</h3>
            <ol className="space-y-5">
              <li className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md border-2 border-indigo-100 hover:border-indigo-300 transition-smooth">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">1</span>
                <div className="flex-1">
                  <strong className="text-gray-900 text-lg">Configure settings below</strong>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">Add your niche keywords (e.g., "AI marketing", "content generation") and company information. This helps our AI find relevant questions and craft expert answers.</p>
                </div>
              </li>
              <li className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md border-2 border-indigo-100 hover:border-indigo-300 transition-smooth">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">2</span>
                <div className="flex-1">
                  <strong className="text-gray-900 text-lg">Download Chrome Extension</strong>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">Click the button below to get installation instructions. Works only on Chrome/Edge browsers. Takes 2 minutes to install.</p>
                </div>
              </li>
              <li className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md border-2 border-indigo-100 hover:border-indigo-300 transition-smooth">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">3</span>
                <div className="flex-1">
                  <strong className="text-gray-900 text-lg">Find relevant Quora questions</strong>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">Search Quora for questions in your niche. The AI will validate relevance automatically.</p>
                </div>
              </li>
              <li className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-md border-2 border-indigo-100 hover:border-indigo-300 transition-smooth">
                <span className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold flex-shrink-0">4</span>
                <div className="flex-1">
                  <strong className="text-gray-900 text-lg">Click extension → Review AI answer → Post!</strong>
                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">AI generates 300-500 word expert answers. You review, approve, and it auto-posts. Track everything here in your dashboard.</p>
                </div>
              </li>
            </ol>
            <div className="mt-6 p-5 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 rounded-xl shadow-md">
              <p className="text-sm text-yellow-900 font-medium flex items-start gap-2">
                <span className="text-2xl flex-shrink-0">💡</span>
                <span><strong>Pro Tip:</strong> Start with 2-3 broad niche keywords. The AI works best with clear, focused topics like "digital marketing" rather than vague terms.</span>
              </p>
            </div>
            <button
              onClick={handleDownloadExtension}
              className="mt-6 btn-primary-enhanced text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth flex items-center gap-3 w-full justify-center"
            >
              <span className="text-2xl">⬇️</span> Download Chrome Extension & Installation Guide
            </button>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-10 mb-10 border-2 border-gray-200 fade-in-up delay-500">
        <h3 className="text-3xl font-bold gradient-text mb-8 flex items-center">
          <span className="mr-3">⚙️</span> Configuration
        </h3>

        <div className="space-y-8">
          {/* Niche Keywords */}
          <div>
            <label className="block text-lg font-bold text-gray-900 mb-3">
              🎯 Niche Keywords
            </label>
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                placeholder="e.g., AI marketing, content generation"
                className="flex-1 px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
              />
              <button
                onClick={handleAddKeyword}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold hover:shadow-lg transition-smooth"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-3 mb-3">
              {settings.niche_keywords.map((keyword, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 px-4 py-2 rounded-full text-sm font-semibold border-2 border-purple-200 hover:shadow-md transition-smooth"
                >
                  {keyword}
                  <button
                    onClick={() => handleRemoveKeyword(keyword)}
                    className="text-purple-700 hover:text-purple-900 font-bold text-lg transition-smooth"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <p className="text-sm text-gray-600 font-medium">
              💡 Keywords help find relevant Quora questions in your niche
            </p>
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-lg font-bold text-gray-900 mb-3">
              🏢 Company/Brand Name
            </label>
            <input
              type="text"
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              placeholder="iSupplyAI"
              className="w-full px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
            />
          </div>

          {/* Company Description */}
          <div>
            <label className="block text-lg font-bold text-gray-900 mb-3">
              📝 Company Description
            </label>
            <textarea
              value={settings.company_description}
              onChange={(e) => setSettings({ ...settings, company_description: e.target.value })}
              placeholder="AI-powered content generation platform for businesses..."
              rows={4}
              className="w-full px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium leading-relaxed"
            />
          </div>

          {/* Company URL */}
          <div>
            <label className="block text-lg font-bold text-gray-900 mb-3">
              🔗 Company Website URL
            </label>
            <input
              type="url"
              value={settings.company_url}
              onChange={(e) => setSettings({ ...settings, company_url: e.target.value })}
              placeholder="https://isupplyai.com"
              className="w-full px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
            />
            <p className="text-sm text-gray-600 mt-2 font-medium">
              💡 Used for backlinks (90/10 strategy - 1 in 10 answers includes your link)
            </p>
          </div>

          {/* User Expertise */}
          <div>
            <label className="block text-lg font-bold text-gray-900 mb-3">
              ⭐ Your Expertise/Credentials
            </label>
            <input
              type="text"
              value={settings.user_expertise}
              onChange={(e) => setSettings({ ...settings, user_expertise: e.target.value })}
              placeholder="10 years in digital marketing, helped 50+ businesses..."
              className="w-full px-5 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-smooth font-medium"
            />
            <p className="text-sm text-gray-600 mt-2 font-medium">
              💡 Builds authority in your answers
            </p>
          </div>

          <button
            onClick={handleSaveSettings}
            disabled={loading || !settings.company_name}
            className="w-full btn-primary-enhanced text-white py-5 rounded-xl font-bold text-lg hover:shadow-vibrant transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-3">
                <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <span>💾</span> Save Settings
              </span>
            )}
          </button>

          {message && (
            <div className={`p-5 rounded-xl font-semibold border-2 ${message.includes('success') ? 'bg-green-50 text-green-800 border-green-300' : 'bg-red-50 text-red-800 border-red-300'}`}>
              {message}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      {activity.length > 0 && (
        <div className="glass-effect bg-white rounded-2xl shadow-vibrant p-10 border-2 border-gray-200 fade-in-up delay-600">
          <h3 className="text-3xl font-bold gradient-text mb-8 flex items-center">
            <span className="mr-3">📊</span> Recent Activity
          </h3>
          <div className="space-y-5">
            {activity.slice(0, 10).map((item, idx) => (
              <div key={idx} className="card-hover border-l-4 border-purple-500 pl-6 py-5 bg-gradient-to-r from-purple-50 to-white rounded-xl shadow-md hover:shadow-lg transition-smooth border-2 border-gray-100">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 mb-2 text-lg leading-snug">{item.question}</p>
                    <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{item.answer.substring(0, 150)}...</p>
                  </div>
                  <div className="ml-4 text-right flex-shrink-0">
                    <span className={`inline-block px-4 py-2 rounded-xl text-sm font-bold border-2 shadow-sm ${
                      item.has_link 
                        ? 'bg-blue-50 text-blue-800 border-blue-300' 
                        : 'bg-green-50 text-green-800 border-green-300'
                    }`}>
                      {item.has_link ? '🔗 With Link' : '✍️ Pure Value'}
                    </span>
                    <p className="text-xs text-gray-500 mt-2 font-medium">
                      {new Date(item.posted_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


const AutoPilotEngine = () => {
  const { user } = React.useContext(AuthContext);
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [postingFrequency, setPostingFrequency] = useState(3);
  const [platforms, setPlatforms] = useState([]);
  const [contentThemes, setContentThemes] = useState('');
  const [businessGoals, setBusinessGoals] = useState('');
  const [loading, setLoading] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [error, setError] = useState('');

  const platformOptions = ['blog', 'linkedin', 'twitter', 'instagram', 'youtube', 'tiktok', 'facebook'];

  const togglePlatform = (platform) => {
    setPlatforms(prev => 
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  // Check tier restriction
  if (user?.subscription_tier !== 'enterprise') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-lg p-12 border-2 border-indigo-200">
            <div className="text-6xl mb-4">🤖</div>
            <h1 className="text-3xl font-bold mb-4 text-gray-900">Auto-Pilot Engine - Enterprise Only</h1>
            <p className="text-gray-700 mb-6 text-lg">
              Set it, forget it, watch it grow! Complete 90-day automated content strategy.
            </p>
            <div className="bg-white rounded-lg p-6 mb-6">
              <p className="text-gray-600 mb-4">🔒 This feature requires an Enterprise subscription</p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li>✅ 90-day complete strategy</li>
                <li>✅ Weekly automation workflows</li>
                <li>✅ Multi-platform scheduling</li>
                <li>✅ Performance tracking built-in</li>
              </ul>
            </div>
            <button
              onClick={() => window.location.href = '#pricing'}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition duration-300"
            >
              ⬆️ Upgrade to Enterprise
            </button>
          </div>
        </div>
      </div>
    );
  }

  const generateStrategy = async () => {
    if (!industry || !targetAudience || platforms.length === 0) {
      setError('Please fill in industry, audience, and select platforms');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const themes = contentThemes.split(',').map(t => t.trim()).filter(t => t);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/autopilot-strategy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          industry,
          target_audience: targetAudience,
          posting_frequency: postingFrequency,
          platforms,
          content_themes: themes,
          business_goals: businessGoals
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate autopilot strategy');
      }

      setStrategy(data);
      // Credits will refresh on next page load
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg shadow-lg p-8 mb-6 border-2 border-indigo-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">🤖 Auto-Pilot Content Engine</h2>
            <p className="text-lg text-indigo-700 font-semibold">Set It, Forget It, Watch It Grow!</p>
          </div>
          <span className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold px-4 py-2 rounded-full shadow-md">
            ENTERPRISE ONLY - 15 Credits
          </span>
        </div>
        <p className="text-gray-700 text-base leading-relaxed">
          Generate a complete 90-day autopilot content strategy with weekly schedules, automation workflows, engagement tactics, lead generation, and performance tracking. Fully automated marketing that runs itself!
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Industry *</label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., 'SaaS', 'E-commerce'"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience *</label>
              <input
                type="text"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="e.g., 'Small business owners'"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Posting Frequency: {postingFrequency} posts/week</label>
            <input
              type="range"
              min="1"
              max="7"
              value={postingFrequency}
              onChange={(e) => setPostingFrequency(parseInt(e.target.value))}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Platforms *</label>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {platformOptions.map(platform => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  className={`px-3 py-2 rounded-lg font-medium text-sm ${
                    platforms.includes(platform)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content Themes (comma-separated)</label>
            <input
              type="text"
              value={contentThemes}
              onChange={(e) => setContentThemes(e.target.value)}
              placeholder="e.g., 'Tips, Case Studies, Industry News, How-tos'"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Goals</label>
            <textarea
              value={businessGoals}
              onChange={(e) => setBusinessGoals(e.target.value)}
              placeholder="e.g., 'Generate 100 leads per month, establish thought leadership'"
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
          )}

          <button
            onClick={generateStrategy}
            disabled={loading}
            className={`w-full py-4 px-6 rounded-lg font-bold text-white text-lg ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-lg transform hover:scale-105 transition-all'
            }`}
          >
            {loading ? '🤖 Generating 90-Day Strategy...' : '🤖 Generate Auto-Pilot Strategy'}
          </button>
        </div>
      </div>

      {strategy && (
        <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-indigo-200">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">90-Day Autopilot Strategy</h3>
              <p className="text-sm text-gray-600 mt-1">Complete automation blueprint ready to implement</p>
            </div>
            <button
              onClick={async () => {
                const success = await copyToClipboard(strategy.content);
                alert(success ? '✅ Strategy copied!' : '❌ Copy failed');
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              📋 Copy Strategy
            </button>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-lg">
            <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">
              {strategy.content}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

// White-Label Settings Component (Enterprise Only)
const WhiteLabelSettings = () => {
  const { user, fetchUserProfile } = React.useContext(AuthContext);
  const [settings, setSettings] = useState({
    company_name: '',
    logo_url: null,
    primary_color: '#667eea',
    secondary_color: '#764ba2',
    custom_domain: '',
    email_from_name: '',
    support_email: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/white-label/settings`);
      if (response.data.enabled) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setMessage('Please upload an image file (PNG, JPG, SVG)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setMessage('File size must be less than 2MB');
      return;
    }

    setUploading(true);
    setMessage('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/white-label/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSettings({ ...settings, logo_url: response.data.logo_url });
      setMessage('Logo uploaded successfully!');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to upload logo');
    }

    setUploading(false);
  };

  const handleSave = async () => {
    setLoading(true);
    setMessage('');

    try {
      await axios.post(`${API}/white-label/settings`, settings);
      setMessage('White-label settings saved! Your custom branding is now active.');
      fetchUserProfile(); // Refresh to apply branding
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Failed to save settings');
    }

    setLoading(false);
  };

  const handleDisable = async () => {
    if (!window.confirm('Are you sure you want to disable white-label and revert to default iSupplyAI branding?')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/white-label/disable`);
      setMessage('White-label disabled. Reverted to default branding.');
      fetchSettings();
      fetchUserProfile();
    } catch (error) {
      setMessage('Failed to disable white-label');
    }
    setLoading(false);
  };

  if (user?.subscription_tier !== 'enterprise') {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-12 text-center border-2 border-purple-200">
          <div className="text-6xl mb-4">🏢</div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            White-Label Solutions
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Make this platform your own! Remove our branding, add your logo, customize colors, and use your own domain.
          </p>
          <div className="bg-white rounded-xl p-6 mb-6 max-w-md mx-auto">
            <h3 className="font-bold text-gray-900 mb-3">Enterprise Feature Includes:</h3>
            <ul className="text-left space-y-2 text-sm text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Custom logo upload
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Custom color scheme (brand colors)
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Custom domain support
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Remove all "iSupplyAI" branding
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Custom email sender name
              </li>
            </ul>
          </div>
          <button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-semibold hover:opacity-90 transition">
            Upgrade to Enterprise
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">🏢 White-Label Settings</h2>
        <p className="text-gray-600">
          Customize the platform with your own branding. Changes apply immediately across the entire platform.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">Brand Identity</h3>

        <div className="space-y-6">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              value={settings.company_name}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              placeholder="Your Company Inc."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Replaces "iSupplyAI" throughout the platform
            </p>
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Logo
            </label>
            {settings.logo_url && (
              <div className="mb-3 p-4 bg-gray-50 rounded-lg inline-block">
                <img src={settings.logo_url} alt="Logo" className="h-16" />
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              disabled={uploading}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG, or SVG. Max 2MB. Recommended: 200x50px transparent PNG
            </p>
            {uploading && <p className="text-sm text-blue-600 mt-2">Uploading...</p>}
          </div>

          {/* Primary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Brand Color
            </label>
            <div className="flex gap-3">
              <input
                type="color"
                value={settings.primary_color}
                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                className="h-12 w-20 border-2 border-gray-200 rounded-xl cursor-pointer"
              />
              <input
                type="text"
                value={settings.primary_color}
                onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                placeholder="#667eea"
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Used for buttons, links, and primary accents
            </p>
          </div>

          {/* Secondary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Brand Color
            </label>
            <div className="flex gap-3">
              <input
                type="color"
                value={settings.secondary_color}
                onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                className="h-12 w-20 border-2 border-gray-200 rounded-xl cursor-pointer"
              />
              <input
                type="text"
                value={settings.secondary_color}
                onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                placeholder="#764ba2"
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Used for gradients and secondary elements
            </p>
          </div>

          {/* Custom Domain */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Domain (Optional)
            </label>
            <input
              type="text"
              value={settings.custom_domain}
              onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })}
              placeholder="app.yourcompany.com"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Contact support for DNS configuration help
            </p>
          </div>

          {/* Email Settings */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email From Name
              </label>
              <input
                type="text"
                value={settings.email_from_name}
                onChange={(e) => setSettings({ ...settings, email_from_name: e.target.value })}
                placeholder="Your Company"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Support Email
              </label>
              <input
                type="email"
                value={settings.support_email}
                onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                placeholder="support@yourcompany.com"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-8">
          <button
            onClick={handleSave}
            disabled={loading || uploading}
            className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50"
          >
            {loading ? 'Saving...' : '💾 Save White-Label Settings'}
          </button>
          <button
            onClick={handleDisable}
            disabled={loading}
            className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition disabled:opacity-50"
          >
            Disable
          </button>
        </div>

        {message && (
          <div className={`mt-4 p-4 rounded-xl ${message.includes('success') || message.includes('saved') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message}
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Brand Preview</h3>
        <div className="bg-white rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt="Logo" className="h-10" />
            ) : (
              <div className="text-2xl font-bold" style={{ color: settings.primary_color }}>
                {settings.company_name || 'Your Company'}
              </div>
            )}
          </div>
          <button
            style={{
              background: `linear-gradient(135deg, ${settings.primary_color} 0%, ${settings.secondary_color} 100%)`
            }}
            className="px-6 py-3 text-white rounded-lg font-semibold"
          >
            Sample Button
          </button>
          <p className="text-sm text-gray-600">
            This is how your brand colors will appear throughout the platform.
          </p>
        </div>
      </div>
    </div>
  );
};


// Viral Analyzer Component
const ViralAnalyzer = () => {
  const [userContent, setUserContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    fetchUserContent();
  }, []);

  const fetchUserContent = async () => {
    try {
      const response = await axios.get(`${API}/content/history`);
      setUserContent(response.data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedContent) {
      alert('Please select content to analyze');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/market/analyze-viral-potential`, {
        content_id: selectedContent
      });
      
      setAnalysis(response.data.viral_analysis);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error analyzing viral potential');
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Viral Coefficient Meter (0-3 scale visualization)
  const ViralCoefficientMeter = ({ coefficient }) => {
    const percentage = (coefficient / 3) * 100;
    const getColor = () => {
      if (coefficient >= 2) return 'bg-green-500';
      if (coefficient >= 1) return 'bg-yellow-500';
      return 'bg-orange-500';
    };

    return (
      <div className="text-center">
        <div className="text-4xl font-bold text-purple-600 mb-2">{coefficient}</div>
        <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
          <div
            className={`h-4 rounded-full transition-all duration-1000 ${getColor()}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <p className="text-xs text-gray-600">Viral Coefficient (0-3)</p>
        <p className="text-xs text-gray-500">{coefficient >= 1 ? 'Each share generates 1+ new share' : 'Needs improvement'}</p>
      </div>
    );
  };

  // Trigger Radar Chart Visualization
  const TriggerRadar = ({ triggers }) => {
    if (!triggers) return null;
    
    const triggerData = [
      { name: 'Curiosity', score: triggers.curiosity?.score || 0, color: 'purple' },
      { name: 'Emotion', score: triggers.emotion?.score || 0, color: 'red' },
      { name: 'Social Proof', score: triggers.social_proof?.score || 0, color: 'blue' },
      { name: 'Controversy', score: triggers.controversy?.score || 0, color: 'orange' }
    ];

    return (
      <div className="grid grid-cols-2 gap-4">
        {triggerData.map((trigger, idx) => (
          <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold">{trigger.name}</span>
              <span className={`text-lg font-bold ${
                trigger.score >= 70 ? 'text-green-600' :
                trigger.score >= 40 ? 'text-yellow-600' :
                'text-gray-600'
              }`}>
                {trigger.score}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-1000 ${
                  trigger.color === 'purple' ? 'bg-purple-500' :
                  trigger.color === 'red' ? 'bg-red-500' :
                  trigger.color === 'blue' ? 'bg-blue-500' :
                  'bg-orange-500'
                }`}
                style={{ width: `${trigger.score}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">🔥 Viral Content Analyzer</h1>
        <p className="text-gray-600 mb-6">Reverse engineer viral patterns - Know what will go viral!</p>

        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-6 mb-6">
          <h3 className="font-bold text-lg mb-3">⚡ Viral Intelligence:</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">💯</div>
              <div className="font-semibold text-sm">Viral Score</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">🎯</div>
              <div className="font-semibold text-sm">8 Pattern Check</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">📚</div>
              <div className="font-semibold text-sm">Viral Frameworks</div>
            </div>
            <div className="bg-white rounded-lg p-3 text-center">
              <div className="text-2xl mb-1">🚀</div>
              <div className="font-semibold text-sm">Actionable Tips</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <label className="block mb-4">
            <span className="text-gray-700 font-medium mb-2 block">Select Content to Analyze</span>
            <select
              value={selectedContent}
              onChange={(e) => setSelectedContent(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            >
              <option value="">Choose content...</option>
              {userContent.map((content) => (
                <option key={content.id} value={content.id}>
                  {content.title}
                </option>
              ))}
            </select>
          </label>

          <button
            onClick={handleAnalyze}
            disabled={analyzing || !selectedContent}
            className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg hover:shadow-lg disabled:bg-gray-400 transition-all"
          >
            {analyzing ? '🔥 Analyzing Viral Potential...' : '🔥 Analyze Viral Potential'}
          </button>
        </div>

        {analysis && (
          <div className="space-y-6">
            {/* If analysis is just text (not structured), show it in a simple format */}
            {typeof analysis === 'string' || !analysis.viral_score ? (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-4 flex items-center">
                  <span className="mr-2">🔥</span> Viral Analysis Report
                </h2>
                <div className="prose max-w-none">
                  <pre className="whitespace-pre-wrap text-gray-700 font-sans">{typeof analysis === 'string' ? analysis : JSON.stringify(analysis, null, 2)}</pre>
                </div>
              </div>
            ) : (
              <>
            {/* Main Viral Score Display */}
            <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 rounded-xl shadow-lg p-8">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold mb-2">🔥 Viral Analysis Report</h2>
                <p className="text-gray-700 text-lg">{analysis.summary}</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {/* Viral Score */}
                <div className="bg-white rounded-xl p-6 shadow-md text-center">
                  <div className={`text-6xl font-bold mb-2 ${getScoreColor(analysis.viral_score)}`}>
                    {analysis.viral_score}
                  </div>
                  <div className="text-sm text-gray-600 mb-3">Viral Score</div>
                  <div className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                    analysis.viral_potential === 'HIGH' ? 'bg-green-100 text-green-700' :
                    analysis.viral_potential === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {analysis.viral_potential} POTENTIAL
                  </div>
                </div>

                {/* Share Probability */}
                {analysis.detailed_metrics?.share_probability && (
                  <div className="bg-white rounded-xl p-6 shadow-md text-center">
                    <div className="text-6xl font-bold text-blue-600 mb-2">
                      {analysis.detailed_metrics.share_probability}%
                    </div>
                    <div className="text-sm text-gray-600 mb-2">Share Probability</div>
                    <div className="text-xs text-gray-500">
                      {analysis.detailed_metrics.expected_shares_per_100_views} shares per 100 views
                    </div>
                  </div>
                )}

                {/* Viral Coefficient */}
                {analysis.detailed_metrics?.virality_coefficient && (
                  <div className="bg-white rounded-xl p-6 shadow-md">
                    <ViralCoefficientMeter coefficient={analysis.detailed_metrics.virality_coefficient} />
                  </div>
                )}
              </div>

              {/* Detailed Metrics Row */}
              {analysis.detailed_metrics && (
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-purple-600">{analysis.detailed_metrics.emotional_impact_score}</div>
                    <div className="text-sm text-gray-600 mt-1">Emotional Impact</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-orange-600">{analysis.detailed_metrics.hook_effectiveness}</div>
                    <div className="text-sm text-gray-600 mt-1">Hook Effectiveness</div>
                  </div>
                </div>
              )}
            </div>

            {/* Viral Trigger Analysis */}
            {analysis.trigger_analysis && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="text-2xl mr-2">🎯</span>
                  Viral Trigger Analysis
                </h3>
                <TriggerRadar triggers={analysis.trigger_analysis} />
                
                {/* Trigger Details */}
                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  {Object.entries(analysis.trigger_analysis).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-bold text-sm mb-2 capitalize">{key.replace('_', ' ')}</h4>
                      <ul className="space-y-1">
                        {(value.triggers || value.elements)?.map((item, idx) => (
                          <li key={idx} className="text-xs text-gray-700">• {item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Patterns Found vs Missing */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 shadow-md">
                <h3 className="font-bold mb-4 text-green-800 flex items-center text-lg">
                  <span className="text-2xl mr-2">✅</span>
                  Patterns Found
                </h3>
                <ul className="space-y-2">
                  {(analysis.patterns_found || []).map((pattern, idx) => (
                    <li key={idx} className="text-sm text-green-700 bg-white rounded-lg p-2">{pattern}</li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6 shadow-md">
                <h3 className="font-bold mb-4 text-orange-800 flex items-center text-lg">
                  <span className="text-2xl mr-2">⚠️</span>
                  Missing Patterns
                </h3>
                <ul className="space-y-2">
                  {(analysis.patterns_missing || []).map((pattern, idx) => (
                    <li key={idx} className="text-sm text-orange-700 bg-white rounded-lg p-2">{pattern}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Viral Frameworks */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center">
                <span className="text-2xl mr-2">📚</span>
                Proven Viral Frameworks
              </h3>
              <p className="text-sm text-gray-600 mb-4">Use these frameworks to structure your viral content:</p>
              <div className="grid md:grid-cols-2 gap-4">
                {(analysis.viral_frameworks || []).map((framework, idx) => (
                  <div key={idx} className="border-2 border-purple-200 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-pink-50 hover:shadow-lg transition-shadow">
                    <h4 className="font-bold mb-2 text-purple-800">{typeof framework === 'string' ? framework : (framework?.name || 'Framework')}</h4>
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-700">
                        <strong className="text-purple-700">Pattern:</strong> {typeof framework === 'string' ? framework : (framework?.pattern || 'N/A')}
                      </p>
                      <p className="text-green-700 bg-green-50 rounded p-2">
                        <strong>Example:</strong> {typeof framework === 'string' ? framework : (framework?.example || 'N/A')}
                      </p>
                      <p className="text-xs text-gray-600 italic">{typeof framework === 'string' ? '' : (framework?.why_it_works || '')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Viral Examples */}
            {analysis.examples && analysis.examples.length > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl shadow-md p-6 border-2 border-yellow-200">
                <h3 className="text-xl font-bold mb-4 flex items-center">
                  <span className="text-2xl mr-2">💡</span>
                  Viral-Worthy Title Examples
                </h3>
                <div className="space-y-2">
                  {(analysis.examples || []).map((example, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 text-sm font-medium text-gray-800 shadow-sm hover:shadow-md transition-shadow">
                      {idx + 1}. {example}
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Performance Predictor Component
const PerformancePredictor = () => {
  const [userContent, setUserContent] = useState([]);
  const [selectedContent, setSelectedContent] = useState('');
  const [platform, setPlatform] = useState('');
  const [predicting, setPredicting] = useState(false);
  const [prediction, setPrediction] = useState(null);

  useEffect(() => {
    fetchUserContent();
  }, []);

  const fetchUserContent = async () => {
    try {
      const response = await axios.get(`${API}/content/history`);
      setUserContent(response.data || []);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handlePredict = async () => {
    if (!selectedContent) {
      alert('Please select content to analyze');
      return;
    }

    setPredicting(true);
    try {
      const response = await axios.post(`${API}/content/predict-performance`, {
        content_id: selectedContent,
        platform: platform || null
      });
      
      setPrediction(response.data.prediction);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error predicting performance');
    } finally {
      setPredicting(false);
    }
  };

  // Helper to create circular gauge
  const CircularGauge = ({ score, label, color = "purple" }) => {
    const radius = 70;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    
    const colorMap = {
      purple: { stroke: "#9333ea", bg: "#f3e8ff" },
      blue: { stroke: "#3b82f6", bg: "#dbeafe" },
      green: { stroke: "#10b981", bg: "#d1fae5" },
      orange: { stroke: "#f59e0b", bg: "#fed7aa" },
      red: { stroke: "#ef4444", bg: "#fecaca" }
    };
    
    const colors = colorMap[color] || colorMap.purple;
    
    return (
      <div className="flex flex-col items-center">
        <svg width="180" height="180" className="transform -rotate-90">
          <circle
            cx="90"
            cy="90"
            r={radius}
            stroke={colors.bg}
            strokeWidth="14"
            fill="none"
          />
          <circle
            cx="90"
            cy="90"
            r={radius}
            stroke={colors.stroke}
            strokeWidth="14"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
          <text
            x="90"
            y="90"
            textAnchor="middle"
            dy="0.3em"
            className="transform rotate-90 text-3xl font-bold"
            fill={colors.stroke}
            style={{ transformOrigin: 'center' }}
          >
            {Math.round(score)}
          </text>
        </svg>
        <p className="text-sm font-semibold text-gray-700 mt-2">{label}</p>
      </div>
    );
  };

  // Helper for confidence meter
  const ConfidenceMeter = ({ confidence }) => {
    const getConfidenceColor = (conf) => {
      if (conf >= 80) return 'bg-green-500';
      if (conf >= 60) return 'bg-blue-500';
      if (conf >= 40) return 'bg-yellow-500';
      return 'bg-orange-500';
    };

    return (
      <div className="w-full">
        <div className="flex justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Prediction Confidence</span>
          <span className="text-sm font-bold text-purple-600">{confidence}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-1000 ${getConfidenceColor(confidence)}`}
            style={{ width: `${confidence}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">🔮 Content Performance Predictor</h1>
        <p className="text-gray-600 mb-6">Predict content success BEFORE publishing - ChatGPT can't do this!</p>

        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 mb-6">
          <h3 className="font-bold text-lg mb-3">🎯 Predictive Intelligence:</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl mb-2">📈</div>
              <div className="font-semibold mb-1">Engagement Prediction</div>
              <div className="text-sm text-gray-600">Estimated engagement rate and factors</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl mb-2">🔥</div>
              <div className="font-semibold mb-1">Virality Analysis</div>
              <div className="text-sm text-gray-600">Likelihood of going viral</div>
            </div>
            <div className="bg-white rounded-lg p-4">
              <div className="text-2xl mb-2">💡</div>
              <div className="font-semibold mb-1">Improvement Tips</div>
              <div className="text-sm text-gray-600">Actionable recommendations</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <label className="block">
              <span className="text-gray-700 font-medium mb-2 block">Select Content</span>
              <select
                value={selectedContent}
                onChange={(e) => setSelectedContent(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Choose content...</option>
                {userContent.map((content) => (
                  <option key={content.id} value={content.id}>
                    {content.title}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-gray-700 font-medium mb-2 block">Target Platform (Optional)</span>
              <select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">General Analysis</option>
                <option value="twitter">Twitter</option>
                <option value="linkedin">LinkedIn</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="blog">Blog</option>
              </select>
            </label>
          </div>

          <button
            onClick={handlePredict}
            disabled={predicting || !selectedContent}
            className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg disabled:bg-gray-400 transition-all"
          >
            {predicting ? '🔮 Analyzing...' : '🔮 Predict Performance'}
          </button>
        </div>

        {prediction && (
          <div className="space-y-6">
            {/* Overall Score with Circular Gauges */}
            <div className="bg-white rounded-xl shadow-md p-8">
              <h2 className="text-2xl font-bold mb-2 text-center">🎯 Performance Prediction</h2>
              <p className="text-gray-600 text-center mb-6">{prediction.performance_rating}</p>
              
              {/* Confidence Meter */}
              {prediction.confidence && (
                <div className="mb-8">
                  <ConfidenceMeter confidence={prediction.confidence} />
                </div>
              )}

              {/* Three Circular Gauges */}
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <CircularGauge 
                  score={prediction.overall_score} 
                  label="Overall Score" 
                  color="purple" 
                />
                <CircularGauge 
                  score={prediction.engagement_prediction.score} 
                  label="Engagement Score" 
                  color="blue" 
                />
                <CircularGauge 
                  score={prediction.virality_potential.score} 
                  label="Virality Score" 
                  color="orange" 
                />
              </div>

              {/* Detailed Metrics Cards */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600">{prediction.engagement_prediction.expected_likes || 'N/A'}</div>
                  <div className="text-sm text-gray-700 mt-1">Expected Likes</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-green-600">{prediction.engagement_prediction.expected_shares || 'N/A'}</div>
                  <div className="text-sm text-gray-700 mt-1">Expected Shares</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-purple-600">{prediction.engagement_prediction.expected_comments || 'N/A'}</div>
                  <div className="text-sm text-gray-700 mt-1">Expected Comments</div>
                </div>
              </div>

              {/* Engagement & Virality Details */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                  <h3 className="font-bold mb-3 flex items-center text-blue-800">
                    <span className="text-2xl mr-2">📈</span>
                    Engagement Prediction
                  </h3>
                  <div className="mb-3">
                    <span className="text-sm text-gray-600">Estimated Rate:</span>
                    <span className="ml-2 font-bold text-blue-600 text-lg">
                      {prediction.engagement_prediction.estimated_engagement_rate}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700">
                    <strong>Key Factors:</strong>
                    <ul className="mt-2 space-y-1">
                      {prediction.engagement_prediction.factors.map((factor, idx) => (
                        <li key={idx} className="text-xs">✓ {factor}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
                  <h3 className="font-bold mb-3 flex items-center text-orange-800">
                    <span className="text-2xl mr-2">🔥</span>
                    Virality Potential
                  </h3>
                  <div className="mb-3">
                    <span className="text-sm text-gray-600">Likelihood:</span>
                    <span className={`ml-2 font-bold text-lg ${
                      prediction.virality_potential.likelihood === 'high' ? 'text-green-600' :
                      prediction.virality_potential.likelihood === 'medium' ? 'text-yellow-600' :
                      'text-gray-600'
                    }`}>
                      {prediction.virality_potential.likelihood.toUpperCase()}
                    </span>
                  </div>
                  {prediction.virality_potential.share_probability && (
                    <div className="mb-3">
                      <span className="text-sm text-gray-600">Share Probability:</span>
                      <span className="ml-2 font-bold text-orange-600 text-lg">
                        {prediction.virality_potential.share_probability}
                      </span>
                    </div>
                  )}
                  <div className="text-sm text-gray-700">
                    <strong>Viral Elements:</strong>
                    <ul className="mt-2 space-y-1">
                      {prediction.virality_potential.viral_elements.map((element, idx) => (
                        <li key={idx} className="text-xs">✓ {element}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform-Specific Predictions */}
            {prediction.platform_predictions && prediction.platform_predictions.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">📱 Platform-Specific Predictions</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {prediction.platform_predictions.map((plat, idx) => (
                    <div key={idx} className={`border-2 rounded-lg p-4 ${
                      plat.recommendation === 'High' ? 'border-green-300 bg-green-50' :
                      plat.recommendation === 'Medium' ? 'border-yellow-300 bg-yellow-50' :
                      'border-gray-300 bg-gray-50'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <span className="text-3xl mr-2">{plat.icon}</span>
                          <span className="font-bold text-lg">{plat.platform}</span>
                        </div>
                        <span className={`text-2xl font-bold ${
                          plat.predicted_score >= 70 ? 'text-green-600' :
                          plat.predicted_score >= 50 ? 'text-yellow-600' :
                          'text-gray-600'
                        }`}>
                          {plat.predicted_score}
                        </span>
                      </div>
                      <div className="text-sm space-y-1">
                        <p><strong>Est. Reach:</strong> {plat.estimated_reach} people</p>
                        <p><strong>Best Format:</strong> {plat.best_format}</p>
                        <p>
                          <strong>Recommendation:</strong> 
                          <span className={`ml-1 font-bold ${
                            plat.recommendation === 'High' ? 'text-green-600' :
                            plat.recommendation === 'Medium' ? 'text-yellow-600' :
                            'text-gray-600'
                          }`}>
                            {plat.recommendation}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Best Posting Times */}
            {prediction.best_posting_times && prediction.best_posting_times.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-xl font-bold mb-4">⏰ Best Times to Post</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {prediction.best_posting_times.map((time, idx) => (
                    <div key={idx} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4">
                      <div className="font-bold text-purple-700">{time.day}</div>
                      <div className="text-lg font-semibold text-gray-800">{time.time}</div>
                      <div className="text-xs text-gray-600 mt-1">{time.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
              <h3 className="font-bold text-xl mb-4 flex items-center">
                <span className="text-2xl mr-2">💡</span>
                Recommendations to Boost Performance
              </h3>
              <ul className="space-y-2">
                {prediction.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm bg-white rounded-lg p-3 shadow-sm">{rec}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Bulk Content Generator Component (Enterprise Only)
const BulkGenerator = () => {
  const { user } = React.useContext(AuthContext);
  const [topics, setTopics] = useState('');
  const [contentType, setContentType] = useState('blog_post');
  const [tone, setTone] = useState('professional');
  const [wordCount, setWordCount] = useState(500);
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState(null);

  const handleBulkGenerate = async () => {
    const topicList = topics.split('\n').filter(t => t.trim());
    
    if (topicList.length === 0) {
      alert('Please enter at least one topic');
      return;
    }

    if (topicList.length > 10) {
      alert('Maximum 10 topics per batch');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/content/bulk-generate`, {
        topics: topicList,
        content_type: contentType,
        tone: tone,
        word_count: wordCount
      });
      
      setResults(response.data);
      alert(`✅ Successfully generated ${response.data.generated_count} content pieces!`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error generating bulk content');
    } finally {
      setGenerating(false);
    }
  };

  if (user?.subscription_tier !== 'enterprise') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-lg p-12 border-2 border-indigo-200">
            <div className="text-6xl mb-4">⚡</div>
            <h1 className="text-3xl font-bold mb-4 text-gray-900">Bulk Generator - Enterprise Only</h1>
            <p className="text-gray-700 mb-6 text-lg">
              Generate up to 10 content pieces at once. Save hours of time with batch processing.
            </p>
            <div className="bg-white rounded-lg p-6 mb-6">
              <p className="text-gray-600 mb-4">🔒 This feature requires an Enterprise subscription</p>
              <ul className="text-left text-gray-700 space-y-2 mb-6">
                <li>✅ Generate 10 pieces at once</li>
                <li>✅ Batch processing for efficiency</li>
                <li>✅ Perfect for content agencies</li>
                <li>✅ 500 credits per month</li>
              </ul>
            </div>
            <button
              onClick={() => window.location.href = '#pricing'}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition duration-300"
            >
              ⬆️ Upgrade to Enterprise
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">⚡ Bulk Content Generator</h1>
        <p className="text-gray-600 mb-6">Generate content for multiple topics at once</p>

        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <label className="block mb-4">
            <span className="text-gray-700 font-medium mb-2 block">Topics (one per line, max 10)</span>
            <textarea
              value={topics}
              onChange={(e) => setTopics(e.target.value)}
              rows="6"
              placeholder="The Future of AI&#10;Benefits of Remote Work&#10;Sustainable Living Tips&#10;..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </label>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <label className="block">
              <span className="text-gray-700 font-medium mb-2 block">Content Type</span>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="blog_post">Blog Post</option>
                <option value="social_media">Social Media</option>
                <option value="marketing_copy">Marketing Copy</option>
                <option value="email_campaign">Email</option>
              </select>
            </label>

            <label className="block">
              <span className="text-gray-700 font-medium mb-2 block">Tone</span>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="friendly">Friendly</option>
                <option value="persuasive">Persuasive</option>
              </select>
            </label>

            <label className="block">
              <span className="text-gray-700 font-medium mb-2 block">Words per Topic</span>
              <input
                type="number"
                value={wordCount}
                onChange={(e) => setWordCount(e.target.value)}
                min="100"
                max="2000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </label>
          </div>

          <button
            onClick={handleBulkGenerate}
            disabled={generating}
            className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
          >
            {generating ? '⏳ Generating...' : '⚡ Generate All Content'}
          </button>
        </div>

        {results && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Generation Results</h2>
            <div className="space-y-3">
              {results.contents.map((content, index) => (
                <div key={index} className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h3 className="font-semibold text-green-800">{content.title}</h3>
                  <p className="text-sm text-green-600">
                    {content.word_count} words • {content.credits_used} credits used
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-gray-600">
              Total: {results.generated_count} pieces generated • Credits remaining: {results.remaining_credits}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Exit Intent Popup
const ExitIntentPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full relative animate-slideUp shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
        >
          ×
        </button>
        
        <div className="text-center">
          <div className="text-6xl mb-4">🔥</div>
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Wait! Special Founder Pricing!</h2>
          <p className="text-xl text-gray-600 mb-6">
            Lock in <span className="text-orange-600 font-bold">$50/month FOREVER</span> + 
            <span className="text-green-600 font-bold"> 20 BONUS CREDITS</span>!
          </p>
          
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700 mb-2">🎯 This exclusive offer expires in:</p>
            <div className="text-3xl font-bold text-purple-600">4:59</div>
            <p className="text-xs text-gray-500 mt-2">One-time offer for new users only</p>
          </div>

          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 rounded-lg text-lg font-bold hover:shadow-xl transition duration-300 mb-3"
          >
            🚀 Claim My 50% Discount Now
          </button>
          
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            No thanks, I'll pay full price
          </button>
        </div>
      </div>
    </div>
  );
};


// ==================== ADVANCED ANALYTICS DASHBOARD ====================
const AdvancedAnalyticsDashboard = () => {
  const { user } = React.useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30); // Days
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [animatedMetrics, setAnimatedMetrics] = useState({
    content: 0,
    words: 0,
    credits: 0,
    value: 0
  });

  const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1'];

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  // Animate metrics when data loads
  useEffect(() => {
    if (dashboardData) {
      const duration = 1500; // 1.5 seconds
      const steps = 60;
      const interval = duration / steps;

      const targetMetrics = {
        content: dashboardData.content?.total_content || 0,
        words: dashboardData.content?.total_words || 0,
        credits: dashboardData.credits?.total_credits_used || 0,
        value: dashboardData.roi?.content_value || 0
      };

      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;
        
        setAnimatedMetrics({
          content: Math.floor(targetMetrics.content * progress),
          words: Math.floor(targetMetrics.words * progress),
          credits: Math.floor(targetMetrics.credits * progress),
          value: Math.floor(targetMetrics.value * progress)
        });

        if (currentStep >= steps) {
          clearInterval(timer);
          setAnimatedMetrics(targetMetrics);
        }
      }, interval);

      return () => clearInterval(timer);
    }
  }, [dashboardData]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/dashboard?days=${timeRange}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 403) {
        setError('Analytics dashboard is available for Pro and Enterprise tiers only. Please upgrade your plan.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const result = await response.json();
      setDashboardData(result.data);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'json') => {
    if (user?.subscription_tier !== 'enterprise') {
      alert('Export feature is available for Enterprise tier only');
      return;
    }

    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/analytics/export?days=${timeRange}&format=${format}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export analytics');
      }

      const result = await response.json();
      
      if (format === 'csv') {
        // Convert to CSV
        const csvContent = convertToCSV(result.data);
        const dataBlob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics-export-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // JSON format
        const dataStr = JSON.stringify(result.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analytics-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      
      alert('✅ Analytics exported successfully!');
    } catch (err) {
      alert('Failed to export analytics: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const convertToCSV = (data) => {
    // Simple CSV conversion for analytics data
    let csv = 'Metric,Value\n';
    csv += `Total Content,${data.content?.total_content || 0}\n`;
    csv += `Total Words,${data.content?.total_words || 0}\n`;
    csv += `Credits Used,${data.credits?.total_credits_used || 0}\n`;
    csv += `Content Value,$${data.roi?.content_value || 0}\n`;
    csv += `Hours Saved,${data.roi?.hours_saved || 0}\n`;
    return csv;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-4"></div>
          <div className="text-gray-600 font-semibold">Loading analytics dashboard...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass-effect bg-red-50 p-8 rounded-2xl border-2 border-red-200 shadow-vibrant fade-in-up">
          <div className="flex items-start">
            <span className="text-4xl mr-4">⚠️</span>
            <div className="flex-1">
              <p className="text-xl font-bold text-red-900 mb-2">Access Restricted</p>
              <p className="text-red-800 mb-4">{error}</p>
              {user?.subscription_tier === 'free' && (
                <button
                  onClick={() => window.location.href = '/#pricing'}
                  className="btn-primary-enhanced text-white px-6 py-3 rounded-xl font-bold"
                >
                  ⬆️ Upgrade to Pro
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-20">
          <span className="text-6xl mb-4 block">📊</span>
          <p className="text-gray-600 text-lg">No analytics data available yet</p>
          <p className="text-gray-500 text-sm mt-2">Start creating content to see your analytics</p>
        </div>
      </div>
    );
  }

  const { overview, content, features, credits, timeline, social_media, roi, quality, peak_usage } = dashboardData;

  // Prepare data for charts
  const contentByTypeData = Object.entries(content.by_type || {}).map(([type, count]) => ({
    name: type.replace('_', ' ').toUpperCase(),
    value: count
  }));

  const featureUsageData = Object.entries(features.by_feature || {}).slice(0, 8).map(([feature, count]) => ({
    name: feature.replace('_', ' '),
    uses: count
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between fade-in-up">
        <div className="mb-4 md:mb-0">
          <h1 className="text-4xl font-bold gradient-text mb-2">📊 Advanced Analytics Dashboard</h1>
          <p className="text-gray-600 text-lg">
            Comprehensive insights into your content performance
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          {/* Time Range Selector */}
          <div className="relative">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="appearance-none glass-effect pl-4 pr-10 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent font-semibold text-gray-700 cursor-pointer transition-smooth hover:border-purple-400"
            >
              <option value={7}>📅 Last 7 Days</option>
              <option value={14}>📅 Last 14 Days</option>
              <option value={30}>📅 Last 30 Days</option>
              {user?.subscription_tier === 'enterprise' && (
                <>
                  <option value={60}>📅 Last 60 Days</option>
                  <option value={90}>📅 Last 90 Days</option>
                  <option value={180}>📅 Last 6 Months</option>
                  <option value={365}>📅 Last Year</option>
                </>
              )}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          {/* Export Button - Enterprise Only */}
          {user?.subscription_tier === 'enterprise' && (
            <div className="relative group">
              <button
                onClick={() => handleExport('json')}
                disabled={exporting}
                className="btn-primary-enhanced text-white px-6 py-3 rounded-xl font-bold disabled:opacity-50 flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    📥 Export Data
                  </>
                )}
              </button>
              
              {/* Export format dropdown */}
              {!exporting && (
                <div className="absolute right-0 mt-2 w-48 glass-effect rounded-xl shadow-vibrant border-2 border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-smooth z-10">
                  <button
                    onClick={() => handleExport('json')}
                    className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-smooth rounded-t-xl font-semibold text-gray-700"
                  >
                    📄 Export as JSON
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-smooth rounded-b-xl font-semibold text-gray-700 border-t border-gray-200"
                  >
                    📊 Export as CSV
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tier Indicator */}
      <div className="mb-8 glass-effect p-6 rounded-2xl shadow-vibrant animated-gradient relative overflow-hidden fade-in-up delay-100">
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-white text-lg font-semibold drop-shadow-lg">📊 Viewing Analytics:</span>
            <span className="badge-gradient text-base px-5 py-2">
              {overview.subscription_tier.toUpperCase()} PLAN
            </span>
          </div>
          <div className="text-white font-semibold text-sm drop-shadow-lg">
            {user?.subscription_tier === 'pro' && '30-day history limit'}
            {user?.subscription_tier === 'enterprise' && '✨ Unlimited history + Export enabled'}
          </div>
        </div>
      </div>

      {/* Key Metrics Cards with Animation */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card-hover glass-effect p-6 rounded-2xl shadow-vibrant border-2 border-purple-200 fade-in-up delay-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Total Content</p>
            <span className="text-4xl">📝</span>
          </div>
          <p className="text-5xl font-bold gradient-text mb-1">
            {animatedMetrics.content.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">Pieces generated</p>
        </div>

        <div className="card-hover glass-effect p-6 rounded-2xl shadow-vibrant border-2 border-pink-200 fade-in-up delay-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Total Words</p>
            <span className="text-4xl">✍️</span>
          </div>
          <p className="text-5xl font-bold gradient-text mb-1">
            {animatedMetrics.words.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">Words written</p>
        </div>

        <div className="card-hover glass-effect p-6 rounded-2xl shadow-vibrant border-2 border-orange-200 fade-in-up delay-300">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Credits Used</p>
            <span className="text-4xl">💎</span>
          </div>
          <p className="text-5xl font-bold gradient-text mb-1">
            {animatedMetrics.credits.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">From your balance</p>
        </div>

        <div className="card-hover glass-effect p-6 rounded-2xl shadow-vibrant border-2 border-green-200 fade-in-up delay-400">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Content Value</p>
            <span className="text-4xl">💰</span>
          </div>
          <p className="text-5xl font-bold text-green-600 mb-1">
            ${animatedMetrics.value.toLocaleString()}
          </p>
          <p className="text-sm text-gray-500">Estimated worth</p>
        </div>
      </div>

      {/* Timeline Chart */}
      <div className="card-hover glass-effect p-8 rounded-2xl shadow-vibrant mb-8 fade-in-up delay-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-3">📈</span> Activity Timeline
        </h2>
        {timeline && timeline.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={timeline}>
              <defs>
                <linearGradient id="colorContent" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorCredits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ec4899" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#ec4899" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                  border: '2px solid #8b5cf6',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="content_generated" 
                stroke="#8b5cf6" 
                strokeWidth={3} 
                name="Content Generated"
                dot={{ fill: '#8b5cf6', r: 5 }}
                activeDot={{ r: 8 }}
              />
              <Line 
                type="monotone" 
                dataKey="credits_used" 
                stroke="#ec4899" 
                strokeWidth={3} 
                name="Credits Used"
                dot={{ fill: '#ec4899', r: 5 }}
                activeDot={{ r: 8 }}
              />
              <Line 
                type="monotone" 
                dataKey="feature_uses" 
                stroke="#10b981" 
                strokeWidth={3} 
                name="Feature Uses"
                dot={{ fill: '#10b981', r: 5 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-16">
            <span className="text-5xl block mb-4">📊</span>
            <p className="text-gray-500 text-lg">No timeline data available for this period</p>
          </div>
        )}
      </div>

      {/* Two Column Layout - Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Content by Type - Pie Chart */}
        <div className="card-hover glass-effect p-8 rounded-2xl shadow-vibrant fade-in-up delay-300">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">📊</span> Content by Type
          </h2>
          {contentByTypeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={contentByTypeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {contentByTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                    border: '2px solid #8b5cf6',
                    borderRadius: '12px'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-16">
              <span className="text-5xl block mb-4">📝</span>
              <p className="text-gray-500">No content data available</p>
            </div>
          )}
        </div>

        {/* Feature Usage - Bar Chart */}
        <div className="card-hover glass-effect p-8 rounded-2xl shadow-vibrant fade-in-up delay-400">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">🎯</span> Feature Usage
          </h2>
          {featureUsageData.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={featureUsageData}>
                <defs>
                  <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={1}/>
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0.8}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={120} stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                    border: '2px solid #8b5cf6',
                    borderRadius: '12px'
                  }} 
                />
                <Bar dataKey="uses" fill="url(#colorBar)" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-16">
              <span className="text-5xl block mb-4">🎯</span>
              <p className="text-gray-500">No feature usage data available</p>
            </div>
          )}
        </div>
      </div>

      {/* ROI Insights */}
      <div className="card-hover glass-effect p-8 rounded-2xl shadow-vibrant bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 mb-8 fade-in-up delay-100">
        <h2 className="text-2xl font-bold text-green-900 mb-6 flex items-center">
          <span className="mr-3">💰</span> ROI Insights
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-sm text-gray-600 font-semibold mb-2">Content Value</p>
            <p className="text-4xl font-bold text-green-600 mb-1">${roi?.content_value || 0}</p>
            <p className="text-xs text-gray-500">Based on $0.10/word</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-sm text-gray-600 font-semibold mb-2">Hours Saved</p>
            <p className="text-4xl font-bold text-blue-600 mb-1">{roi?.hours_saved || 0}h</p>
            <p className="text-xs text-gray-500">At 50 words/hour</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-sm text-gray-600 font-semibold mb-2">Cost per Content</p>
            <p className="text-4xl font-bold text-purple-600 mb-1">${roi?.cost_per_content || 0}</p>
            <p className="text-xs text-gray-500">Based on subscription</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-sm text-gray-600 font-semibold mb-2">ROI Multiplier</p>
            <p className="text-4xl font-bold gradient-text mb-1">{roi?.roi_multiplier || 0}x</p>
            <p className="text-xs text-gray-500">Value vs cost</p>
          </div>
        </div>
      </div>

      {/* Quality Metrics & Social Media */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Quality Metrics */}
        <div className="card-hover glass-effect p-8 rounded-2xl shadow-vibrant fade-in-up delay-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">⭐</span> Quality Metrics
          </h2>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-700">Average SEO Score</span>
                <span className="text-2xl font-bold text-purple-600">{quality?.average_seo_score || 0}/100</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-1000"
                  style={{ width: `${quality?.average_seo_score || 0}%` }}
                ></div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-gray-700">Avg Engagement Prediction</span>
                <span className="text-2xl font-bold text-pink-600">{quality?.average_engagement_prediction || 0}/100</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-pink-500 to-rose-500 h-3 rounded-full transition-all duration-1000"
                  style={{ width: `${quality?.average_engagement_prediction || 0}%` }}
                ></div>
              </div>
            </div>
            
            <div className="pt-6 border-t-2 border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Optimizations</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">{quality?.total_optimizations || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Total Predictions</p>
                  <p className="text-2xl font-bold text-pink-600 mt-1">{quality?.total_predictions || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Stats */}
        <div className="card-hover glass-effect p-8 rounded-2xl shadow-vibrant fade-in-up delay-300">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">🚀</span> Social Media Automation
          </h2>
          <div className="space-y-6">
            <div className="p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border-2 border-orange-200 hover:shadow-lg transition-smooth">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">🟠</span>
                <h3 className="text-xl font-bold text-gray-900">Quora</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Answers</p>
                  <p className="text-3xl font-bold text-orange-600">{social_media?.quora?.total_answers || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avg per Day</p>
                  <p className="text-3xl font-bold text-orange-600">{social_media?.quora?.average_per_day || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200 hover:shadow-lg transition-smooth">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">🐦</span>
                <h3 className="text-xl font-bold text-gray-900">Twitter</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Posts</p>
                  <p className="text-3xl font-bold text-blue-600">{social_media?.twitter?.total_posts || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Avg per Day</p>
                  <p className="text-3xl font-bold text-blue-600">{social_media?.twitter?.average_per_day || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Peak Usage Insights */}
      <div className="card-hover glass-effect p-8 rounded-2xl shadow-vibrant bg-gradient-to-r from-purple-50 via-pink-50 to-rose-50 border-2 border-purple-200 fade-in-up delay-400">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="mr-3">📅</span> Peak Usage Insights
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-smooth">
            <p className="text-sm text-gray-600 mb-3 font-semibold uppercase tracking-wide">Most Active Day</p>
            <p className="text-3xl font-bold gradient-text">{peak_usage?.most_active_day || 'N/A'}</p>
            <p className="text-sm text-gray-500 mt-2">Your productivity peak</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-smooth">
            <p className="text-sm text-gray-600 mb-3 font-semibold uppercase tracking-wide">Highest Credits Usage</p>
            <p className="text-3xl font-bold gradient-text">{peak_usage?.highest_credits_day || 'N/A'}</p>
            <p className="text-sm text-gray-500 mt-2">Maximum engagement day</p>
          </div>
        </div>
      </div>
    </div>
  );
};



// ==================== AD PERFORMANCE DASHBOARD ====================
const AdPerformanceDashboard = () => {
  const { user } = React.useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [dashboardData, setDashboardData] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [connections, setConnections] = useState([]);
  const [error, setError] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);

  const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1'];

  useEffect(() => {
    fetchDashboardData();
    fetchConnections();
  }, [timeRange, selectedPlatform]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const platformParam = selectedPlatform !== 'all' ? `&platform=${selectedPlatform}` : '';
      
      const response = await fetch(
        `${BACKEND_URL}/api/ads/dashboard?days=${timeRange}${platformParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.status === 403) {
        setError('Ad Performance Dashboard is available for Enterprise tier only. Please upgrade your plan.');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch ad performance data');
      }

      const result = await response.json();
      setDashboardData(result.data);
      
      // Fetch recommendations
      await fetchRecommendations();
    } catch (err) {
      setError(err.message || 'Failed to load ad performance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const token = localStorage.getItem('token');
      const platformParam = selectedPlatform !== 'all' ? `?platform=${selectedPlatform}` : '';
      
      const response = await fetch(
        `${BACKEND_URL}/api/ads/recommendations${platformParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setRecommendations(result.recommendations);
      }
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
    }
  };

  const fetchConnections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/ads/connections`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        setConnections(result.connections || []);
      }
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading ad performance data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 p-6 rounded-lg">
          <p className="text-red-800">{error}</p>
          {user?.subscription_tier !== 'enterprise' && (
            <button
              onClick={() => window.location.href = '/#pricing'}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Upgrade to Enterprise
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!dashboardData || dashboardData.has_connections === false) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📢</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ad Performance Dashboard</h2>
          <p className="text-gray-600 mb-2">Connect your ad platforms to track performance across Google, Facebook, LinkedIn, and Twitter ads.</p>
          <p className="text-gray-500 text-sm mb-6">Direct platform integrations coming soon. For now, use Zapier to connect your ad data.</p>
          <button
            onClick={() => setShowConnectModal(true)}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Connect Ad Account
          </button>
        </div>
      </div>
    );
  }

  const { overview, aggregate_metrics, platform_metrics, top_campaigns, timeline } = dashboardData;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📢 Ad Performance Dashboard</h1>
          <p className="mt-2 text-gray-600">
            Track and optimize your Google Ads and Facebook Ads campaigns with AI-powered insights
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Platform Filter */}
          <select
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Platforms</option>
            <option value="google_ads">Google Ads</option>
            <option value="facebook_ads">Facebook Ads</option>
          </select>

          {/* Time Range Selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value={7}>Last 7 Days</option>
            <option value={14}>Last 14 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={60}>Last 60 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>

          {/* Connect Ad Account Button */}
          <button
            onClick={() => setShowConnectModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            + Connect Account
          </button>
        </div>
      </div>

      {/* Connected Accounts Banner */}
      {connections.length > 0 && (
        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <span className="text-green-600 font-semibold">✓ Connected Accounts:</span>
            {connections.map((conn, idx) => (
              <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                {conn.platform === 'google_ads' ? '🔍 Google Ads' : '📘 Facebook Ads'} - {conn.account_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Spend</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">${(aggregate_metrics?.total_spend || 0).toFixed(2)}</p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Impressions</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{(aggregate_metrics?.total_impressions || 0).toLocaleString()}</p>
            </div>
            <div className="text-4xl">👁️</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average CTR</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{(aggregate_metrics?.average_ctr || 0).toFixed(2)}%</p>
            </div>
            <div className="text-4xl">📊</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">ROAS</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">{(aggregate_metrics?.roas || 0).toFixed(2)}x</p>
            </div>
            <div className="text-4xl">🎯</div>
          </div>
        </div>
      </div>

      {/* Ad Spend Timeline */}
      {timeline && timeline.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📈 Ad Spend & Performance Timeline</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timeline}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="spend" stroke="#8b5cf6" strokeWidth={2} name="Spend ($)" />
              <Line type="monotone" dataKey="clicks" stroke="#ec4899" strokeWidth={2} name="Clicks" />
              <Line type="monotone" dataKey="conversions" stroke="#10b981" strokeWidth={2} name="Conversions" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Platform Comparison */}
      {platform_metrics && Object.keys(platform_metrics).length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🔍 Platform Performance Comparison</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Object.entries(platform_metrics).map(([platform, metrics]) => (
              <div key={platform} className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-lg mb-3">
                  {platform === 'google_ads' ? '🔍 Google Ads' : '📘 Facebook Ads'}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Spend:</span>
                    <span className="font-semibold">${metrics.spend?.toFixed(2) || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">CTR:</span>
                    <span className="font-semibold">{metrics.ctr?.toFixed(2) || 0}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">CPC:</span>
                    <span className="font-semibold">${metrics.cpc?.toFixed(2) || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Conversions:</span>
                    <span className="font-semibold">{metrics.conversions || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Recommendations */}
      {recommendations && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg shadow-md border border-purple-200 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🤖 AI-Powered Optimization Recommendations</h2>
          <div className="space-y-4">
            {recommendations.budget_optimization?.length > 0 && (
              <div>
                <h3 className="font-semibold text-purple-800 mb-2">💰 Budget Optimization</h3>
                {recommendations.budget_optimization.map((rec, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg mb-2">
                    <div className="flex items-start gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {rec.priority.toUpperCase()}
                      </span>
                      <div>
                        <p className="text-sm text-gray-700">{rec.recommendation}</p>
                        <p className="text-xs text-green-600 mt-1">💡 {rec.impact}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {recommendations.ad_copy_improvements?.length > 0 && (
              <div>
                <h3 className="font-semibold text-purple-800 mb-2">✍️ Ad Copy Improvements</h3>
                {recommendations.ad_copy_improvements.map((rec, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg mb-2">
                    <div className="flex items-start gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${rec.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {rec.priority.toUpperCase()}
                      </span>
                      <div>
                        <p className="text-sm text-gray-700">{rec.recommendation}</p>
                        <p className="text-xs text-green-600 mt-1">💡 {rec.impact}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top & Low Performing Campaigns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Top Campaigns */}
        {top_campaigns && top_campaigns.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">🏆 Top Performing Campaigns</h2>
            <div className="space-y-3">
              {top_campaigns.map((campaign, idx) => (
                <div key={idx} className="p-3 bg-green-50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-sm">{campaign.campaign_name}</span>
                    <span className="px-2 py-1 bg-green-600 text-white rounded text-xs">ROAS: {campaign.roas}x</span>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div>Platform: {campaign.platform === 'google_ads' ? 'Google Ads' : 'Facebook Ads'}</div>
                    <div>Spend: ${campaign.spend?.toFixed(2)} | Revenue: ${campaign.revenue?.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Note about data source */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-sm text-blue-800">
        <p>💡 <strong>Note:</strong> Connect your Google Ads and Facebook Ads accounts to see real-time campaign data. Currently showing sample/uploaded data.</p>
      </div>
    </div>
  );
};


// ==================== FREE DOWNLOADS PAGE ====================
const FreeDownloadsPage = () => {
  const { user, isAuthenticated } = React.useContext(AuthContext);
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [downloadForm, setDownloadForm] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/free-downloads/resources`);
      const data = await response.json();
      setResources(data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadClick = async (resource) => {
    setSelectedResource(resource);
    
    // Check if user is logged in via AuthContext
    if (isAuthenticated && user?.email) {
      await captureAndDownload(resource, user.name || user.email.split('@')[0], user.email);
      return;
    }
    
    // Check if user info is already saved in localStorage (for non-logged-in users)
    const savedUser = localStorage.getItem('freeDownloadUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        if (userData.email) {
          await captureAndDownload(resource, userData.name, userData.email);
          return;
        }
      } catch (e) {}
    }
    
    // Show form for first time visitors
    setDownloadForm({ name: '', email: '' });
    setShowDownloadModal(true);
    setDownloadSuccess(false);
  };

  const captureAndDownload = async (resource, name, email) => {
    try {
      setSubmitting(true);
      
      // Capture lead
      await fetch(`${BACKEND_URL}/api/free-downloads/capture?email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}&resource_type=${resource.id}`, {
        method: 'POST'
      });
      
      // Trigger download
      const downloadUrl = `${BACKEND_URL}${resource.download_url}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = resource.name + '.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    
    localStorage.setItem('freeDownloadUser', JSON.stringify({
      name: downloadForm.name,
      email: downloadForm.email
    }));
    
    // Also save to regular storage for pre-filling signup
    localStorage.setItem('user_email', downloadForm.email);
    localStorage.setItem('user_name', downloadForm.name);
    
    setDownloadSuccess(true);
    await captureAndDownload(selectedResource, downloadForm.name, downloadForm.email);
    
    setTimeout(() => {
      setShowDownloadModal(false);
      setDownloadSuccess(false);
    }, 1500);
  };

  const categoryColors = {
    'Strategy': 'from-purple-500 to-indigo-500',
    'SEO': 'from-emerald-500 to-teal-500',
    'Productivity': 'from-orange-500 to-amber-500',
    'Planning': 'from-blue-500 to-cyan-500',
    'Engagement': 'from-pink-500 to-rose-500',
    'Branding': 'from-violet-500 to-purple-500'
  };

  const categoryIcons = {
    'Strategy': '🎯',
    'SEO': '🔍',
    'Productivity': '⚡',
    'Planning': '📅',
    'Engagement': '🔥',
    'Branding': '✨'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-2 border-purple-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030305] relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] bg-purple-600/15 rounded-full blur-[180px]" />
        <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] bg-cyan-600/15 rounded-full blur-[180px]" />
      </div>
      
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI" className="h-10 w-10" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">iSupplyAI</span>
            </Link>
            <Link to="/" className="px-5 py-2 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-lg font-semibold text-white hover:shadow-lg hover:shadow-purple-500/25 transition">
              Get Started Free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-20 px-6 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 mb-6">
              <span className="text-purple-400">🎁</span>
              <span className="text-purple-400 text-sm font-bold">100% FREE RESOURCES</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-white">Level Up Your </span>
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Content Game</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Download our premium guides and start creating better content today. No credit card required.
            </p>
          </div>
        </section>

        {/* Resources Grid */}
        <section className="pb-20 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources?.resources?.map((resource, index) => (
              <div 
                key={resource.id}
                className="group bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-800 p-6 hover:border-purple-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/10"
              >
                {/* Category Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${categoryColors[resource.category] || 'from-gray-600 to-gray-500'}`}>
                    <span>{categoryIcons[resource.category] || '📄'}</span>
                    {resource.category}
                  </span>
                  <span className="text-xs text-gray-500">{resource.pages} pages</span>
                </div>
                
                {/* Title & Description */}
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-400 transition-colors">
                  {resource.name}
                </h3>
                <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                  {resource.description}
                </p>
                
                {/* Download Button */}
                <button
                  onClick={() => handleDownloadClick(resource)}
                  disabled={submitting}
                  className="w-full py-3 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl font-semibold text-white hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting && selectedResource?.id === resource.id ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Downloading...
                    </>
                  ) : (
                    <>
                      <span>📥</span>
                      Download Free PDF
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-6 border-t border-gray-800">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready for Even More?</h2>
            <p className="text-gray-400 mb-8">
              These guides are just the beginning. Create unlimited AI-powered content with iSupplyAI.
            </p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl font-bold text-lg text-white hover:shadow-2xl hover:shadow-purple-500/30 transition-all"
            >
              🚀 Start Creating Free
            </Link>
          </div>
        </section>
      </div>

      {/* Download Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8 max-w-md w-full relative">
            <button 
              onClick={() => setShowDownloadModal(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-white"
            >
              ✕
            </button>
            
            {downloadSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✅</span>
                </div>
                <h3 className="text-xl font-bold text-white">Download Started!</h3>
                <p className="text-gray-400 mt-2">Check your downloads folder.</p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-white mb-2">Get Your Free Download</h3>
                <p className="text-gray-400 mb-6">{selectedResource?.name}</p>
                
                <form onSubmit={handleDownloadSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Name</label>
                    <input
                      type="text"
                      required
                      value={downloadForm.name}
                      onChange={(e) => setDownloadForm({...downloadForm, name: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Email</label>
                    <input
                      type="email"
                      required
                      value={downloadForm.email}
                      onChange={(e) => setDownloadForm({...downloadForm, email: e.target.value})}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-cyan-500 rounded-xl font-semibold text-white hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {submitting ? 'Processing...' : '📥 Download Now'}
                  </button>
                </form>
                
                <p className="text-xs text-gray-500 text-center mt-4">
                  We respect your privacy. No spam, ever.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
const MainApp = () => {
  const { isAuthenticated, loading, user, fetchUserProfile, isDemoMode, demoLogin, exitDemo } = React.useContext(AuthContext);
  const { workflow, startRiver, clearRiver, registerNavigator } = useWorkflow();
  const [showLanding, setShowLanding] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [exitIntentTriggered, setExitIntentTriggered] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(null); // null = loading, true/false
  const [reRunOnboarding, setReRunOnboarding] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  
  // Agent system state
  const [agentView, setAgentView] = useState('marketplace');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [showSmartDiscovery, setShowSmartDiscovery] = useState(false);
  const [hiredAgents, setHiredAgents] = useState([]);
  const [hiredAgentsLoading, setHiredAgentsLoading] = useState(true);
  const [sidebarMobileOpen, setSidebarMobileOpen] = useState(false);

  // Register River navigation so any component can trigger tab changes via WorkflowContext
  useEffect(() => {
    registerNavigator((riverData) => {
      const target = riverData.targetTool;
      if (['hermes-forge', 'apollo-chamber', 'athena', 'dionysus', 'alliance'].includes(target)) {
        setActiveTab('agents');
        setAgentView(target);
      } else if (['generate', 'multiply', 'seo', 'trending', 'predictor', 'artemis', 'referrals', 'dashboard', 'digest-settings', 'competitor-analyzer'].includes(target)) {
        setActiveTab(target);
      }
    });
  }, [registerNavigator]);

  // Deep-link from email "Manage preferences" → /?settings=digest
  // Plus "Forge this brief" / "Forge content on this" links from digest emails:
  //   /?river=athena&brief=<topic>&keyword=<kw>     → Beat Competition (sessionStorage prefill set at module load)
  //   /?river=trending&topic=<topic>                → Content Forge (sessionStorage prefill set at module load)
  //   /?auth=login                                  → forces auth modal in login mode
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('settings') === 'digest') {
      setActiveTab('digest-settings');
      setShowLanding(false);
    }
    const river = params.get('river');
    if (river === 'athena') {
      setActiveTab('competitor-analyzer');
      setShowLanding(false);
    } else if (river === 'trending') {
      setActiveTab('generate');
      setShowLanding(false);
    }
    if (params.get('auth') === 'login') {
      setShowLanding(true);
      try { sessionStorage.setItem('isupplyai_show_auth', 'login'); } catch { /* noop */ }
    }
  }, []);
  
  // Check onboarding status when authenticated
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!isAuthenticated) {
        setShowOnboarding(null);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/user/onboarding-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const incomplete = !res.data.onboarding_completed;
        setShowOnboarding(incomplete);
        // Spec §Navigation point 1: auto-trigger the Awakening Ritual for NEW users on first login
        if (incomplete && !isDemoMode) {
          setShowOnboardingOverlay(true);
        }
      } catch {
        setShowOnboarding(false); // Don't block on error
      }
    };
    checkOnboarding();
    // eslint-disable-next-line
  }, [isAuthenticated]);
  
  // Expose a global re-run trigger so any Settings UI can call it (spec §Navigation point 3)
  useEffect(() => {
    window.iSupplyAIReRunAwakening = () => {
      setReRunOnboarding(true);
    };
    return () => { try { delete window.iSupplyAIReRunAwakening; } catch { /* noop */ } };
  }, []);

  const [showOnboardingOverlay, setShowOnboardingOverlay] = useState(false);

  // Auto-trigger Stripe checkout if user selected a plan on the landing page
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const pendingPlan = sessionStorage.getItem('pending_plan');
    if (!pendingPlan) return;
    sessionStorage.removeItem('pending_plan');
    // Don't auto-checkout for free plan or if user already has a paid plan
    if (pendingPlan === 'free') return;
    if (user.subscription_tier === 'pro' || user.subscription_tier === 'enterprise') return;
    // Parse plan type and billing period (e.g., "pro_annual" → pro + annual)
    const isAnnual = pendingPlan.endsWith('_annual');
    const planType = isAnnual ? pendingPlan.replace('_annual', '') : pendingPlan;
    const billingPeriod = isAnnual ? 'annual' : 'monthly';
    // Trigger Stripe checkout
    const triggerCheckout = async () => {
      try {
        const response = await axios.post(`${API}/payment/create-checkout-session`, {
          plan_type: planType,
          billing_period: billingPeriod,
          success_url: `${window.location.origin}/dashboard?payment=success&plan=${planType}`,
          cancel_url: `${window.location.origin}/dashboard?payment=cancelled`
        });
        if (response.data?.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } catch (err) {
        console.error('Auto-checkout failed:', err);
        // Fall through to dashboard — user can upgrade manually
      }
    };
    // Small delay to let dashboard render first
    const timer = setTimeout(triggerCheckout, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user]);

  const handleOnboardingComplete = (handoff) => {
    setShowOnboarding(false);
    setShowOnboardingOverlay(false);
    setReRunOnboarding(false);
    fetchUserProfile();
    // After onboarding, route based on the Step 4 CTA the user clicked.
    // handoff: { target?: 'competitor-analyzer'|'generate'|'living-war-room'|undefined, payload?: any }
    const target = handoff?.target;
    if (target === 'competitor-analyzer') {
      setActiveTab('competitor-analyzer');
    } else if (target === 'generate') {
      setActiveTab('generate');
      // If they chose a topic, stash for Quick Generate / Deep Forge to pick up
      try {
        const t = handoff?.payload?.title;
        if (t) sessionStorage.setItem('isupplyai_pending_forge_topic', JSON.stringify({ topic: t, mode: 'deep', ts: Date.now() }));
      } catch { /* noop */ }
    } else if (target === 'living-war-room') {
      setActiveTab('living-war-room');
    } else {
      // Default → main Dashboard (where the awakening-findings-card lives)
      setActiveTab('dashboard');
    }
  };

  const handlePantheonClick = () => {
    if (showOnboarding && !isDemoMode) {
      setShowOnboardingOverlay(true);
    } else {
      setAgentView('alliance');
      setSelectedAgent(null);
    }
  };

  // Fetch hired agents when user is authenticated
  useEffect(() => {
    const fetchHiredAgents = async () => {
      if (!isAuthenticated) {
        setHiredAgents([]);
        setHiredAgentsLoading(false);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/agents`);
        if (response.data?.agents) {
          const agentTypes = response.data.agents.map(agent => agent.agent_type);
          setHiredAgents(agentTypes);
        }
      } catch (error) {
        console.error('Error fetching hired agents:', error);
        setHiredAgents([]);
      } finally {
        setHiredAgentsLoading(false);
      }
    };
    
    fetchHiredAgents();
  }, [isAuthenticated]);
  
  // Helper function to check if user has hired a specific agent type
  const hasHiredAgent = (agentType) => {
    return hiredAgents.includes(agentType);
  };
  
  // Check if user has Pro+ subscription (can access agent rooms)
  const canAccessAgentRooms = user?.subscription_tier === 'pro' || user?.subscription_tier === 'enterprise';

  // Email capture modal state
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [emailCaptureFeature, setEmailCaptureFeature] = useState('');

  // Handle upgrade click - show email capture first
  const handleUpgradeClick = (featureName) => {
    setEmailCaptureFeature(featureName);
    setShowEmailCapture(true);
  };

  // Handle email capture submission
  const handleEmailCaptureSubmit = async (email, featureName) => {
    try {
      await axios.post(`${API}/leads/upgrade`, {
        email,
        feature_interest: featureName,
        source: 'locked_feature_preview'
      });
      setShowEmailCapture(false);
    } catch (error) {
      console.error('Failed to capture lead:', error);
      // Still redirect to pricing even if capture fails
      setShowEmailCapture(false);
    }
  };

  // Check for password reset routes
  const hash = window.location.hash;
  const searchParams = new URLSearchParams(window.location.search);
  
  // Quora standalone pricing page
  if (hash === '#quora-pricing' || window.location.pathname === '/quora') {
    return <QuoraStandalonePricing onGetStarted={() => setShowLanding(false)} />;
  }
  
  if (hash === '#forgot-password') {
    return <ForgotPassword />;
  }
  
  if (searchParams.get('token') && window.location.pathname === '/reset-password') {
    return <ResetPassword />;
  }

  // Exit Intent Detection
  useEffect(() => {
    if (!isAuthenticated && showLanding && !exitIntentTriggered) {
      const handleMouseLeave = (e) => {
        if (e.clientY <= 0) {
          setShowExitPopup(true);
          setExitIntentTriggered(true);
        }
      };

      document.addEventListener('mouseleave', handleMouseLeave);
      
      return () => {
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [isAuthenticated, showLanding, exitIntentTriggered]);

  // Check for payment success/cancellation in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      // Show success message
      alert('🎉 Payment successful! Your subscription has been activated.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentStatus === 'cancelled') {
      // Show cancellation message
      alert('Payment was cancelled. You can try again anytime.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const [startWithLogin, setStartWithLogin] = useState(false);

  const handleGetStarted = (selectedPlanType) => {
    if (window.gtag) {
      window.gtag('event', 'landing_conversion', {
        event_category: 'engagement',
        plan_selected: selectedPlanType || 'none'
      });
    }
    if (selectedPlanType) {
      sessionStorage.setItem('pending_plan', selectedPlanType);
    }
    setStartWithLogin(false);
    setShowLanding(false);
  };

  const handleLoginClick = () => {
    setStartWithLogin(true);
    setShowLanding(false);
  };

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    await demoLogin();
    setDemoLoading(false);
  };

  const handleDemoExpired = () => {
    exitDemo();
    setShowLanding(true);
  };

  const handleDemoSignUp = () => {
    exitDemo();
    setShowLanding(false); // Go to auth page
  };

  // Track feature exploration in demo mode
  useEffect(() => {
    if (isDemoMode && window.__demoTrackFeature) {
      window.__demoTrackFeature(activeTab);
    }
  }, [activeTab, isDemoMode]);

  // Auto-trigger demo mode from UTM links (?demo=true)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('demo') === 'true' && !isAuthenticated && !isDemoMode && !loading) {
      handleDemoLogin();
    }
  }, [isAuthenticated, isDemoMode, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030305] flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-96 h-96 bg-purple-600/20 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cyan-600/20 rounded-full blur-[150px]" />
        </div>
        <div className="text-center relative">
          <div className="animate-spin rounded-full h-20 w-20 border-2 border-purple-500 border-t-transparent mx-auto"></div>
          <p className="mt-6 text-gray-400 font-medium">Loading your content creation platform...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && showLanding) {
    return (
      <>
        <PremiumLandingPage onGetStarted={handleGetStarted} onLogin={handleLoginClick} onDemoLogin={handleDemoLogin} demoLoading={demoLoading} />
        <ExitIntentPopup isOpen={showExitPopup} onClose={() => setShowExitPopup(false)} />
      </>
    );
  }

  if (!isAuthenticated) {
    return <AuthPage defaultLogin={startWithLogin} />;
  }

  return (
    <div className="min-h-screen bg-[#030305] relative">
      {/* Onboarding overlay — triggers from The Pantheon, or auto-trigger for new users, or via Settings re-run */}
      {(showOnboardingOverlay || reRunOnboarding) && !isDemoMode && (
        <div className="fixed inset-0 z-[100] bg-[#030305]">
          <Onboarding onComplete={handleOnboardingComplete} isReRun={reRunOnboarding} />
        </div>
      )}
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <DemoBanner onExpired={handleDemoExpired} onSignUp={handleDemoSignUp} />
      )}
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 -left-32 w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[180px]" />
        <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] bg-cyan-600/10 rounded-full blur-[180px]" />
      </div>
      
      <div className="relative z-10 flex h-screen overflow-hidden">
        {/* Sidebar */}
        <AppSidebar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setAgentView={setAgentView}
          userTier={user?.subscription_tier || 'free'}
          isMobileOpen={sidebarMobileOpen}
          setMobileOpen={setSidebarMobileOpen}
        />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Top Header Bar */}
          <header className="bg-gray-900/80 backdrop-blur-xl border-b border-gray-800 px-4 py-3 flex items-center justify-between z-20 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarMobileOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-800 text-gray-400"
                data-testid="mobile-menu-toggle"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
              <Link to="/" className="flex items-center gap-2">
                <img src="https://customer-assets.emergentagent.com/job_procontentai/artifacts/8fc8dhxn_ISupplyAILogo%238.png" alt="iSupplyAI" className="w-8 h-8 rounded-lg" />
                <span className="text-lg font-bold text-white hidden sm:inline">iSupplyAI</span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800/50 rounded-lg border border-gray-700/50">
                <span className="text-purple-400">💎</span>
                <span className="text-sm font-bold text-white">{user?.credits || 0}</span>
                <span className="text-xs text-gray-500">credits</span>
              </div>
              {user?.subscription_tier && user.subscription_tier !== 'free' && (
                <span className="px-2.5 py-1 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-full text-xs font-bold text-emerald-400 uppercase">
                  {user.subscription_tier}
                </span>
              )}
              <button
                onClick={() => { localStorage.removeItem('token'); window.location.reload(); }}
                className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                data-testid="logout-btn"
              >
                Logout
              </button>
            </div>
          </header>

          {/* Content Area */}
          <main className="flex-1 overflow-y-auto py-6 px-2 sm:px-4">
        {activeTab === 'dashboard' && <Dashboard onNavigate={setActiveTab} />}
        {activeTab === 'agents' && (
          <div className={agentView === 'athena' || agentView === 'pipelines' || agentView === 'hermes-forge' || agentView === 'apollo-chamber' || agentView === 'alliance' ? '' : 'container mx-auto px-4'}>
            {/* Agent Navigation - ALWAYS show except in config/runner/output views */}
            {agentView !== 'config' && agentView !== 'runner' && agentView !== 'output' && (
              <div className={`flex flex-wrap gap-2 mb-6 ${agentView === 'athena' || agentView === 'pipelines' || agentView === 'hermes-forge' || agentView === 'apollo-chamber' || agentView === 'alliance' ? 'px-6 pt-4' : ''}`}>
                <button
                  onClick={() => { setAgentView('marketplace'); setSelectedAgent(null); }}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    agentView === 'marketplace'
                      ? 'bg-purple-600 text-white'
                      : agentView === 'athena' || agentView === 'pipelines' || agentView === 'hermes-forge' || agentView === 'apollo-chamber' || agentView === 'alliance'
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🛒 Marketplace
                </button>
                <button
                  onClick={() => { setAgentView('dashboard'); setSelectedAgent(null); }}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    agentView === 'dashboard'
                      ? 'bg-purple-600 text-white'
                      : agentView === 'athena' || agentView === 'pipelines' || agentView === 'hermes-forge' || agentView === 'apollo-chamber' || agentView === 'alliance'
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  📊 My Agents
                </button>
                {/* Athena's War Room - requires competitor_spy agent */}
                {(hasHiredAgent('competitor_spy') || canAccessAgentRooms) && (
                  <button
                    onClick={() => { setAgentView('athena'); setSelectedAgent(null); }}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      agentView === 'athena'
                        ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid="agent-athena-btn"
                  >
                    🦉 Athena's War Room
                  </button>
                )}
                {/* Hermes' Forge - requires content_engine agent */}
                {(hasHiredAgent('content_engine') || canAccessAgentRooms) && (
                  <button
                    onClick={() => { setAgentView('hermes-forge'); setSelectedAgent(null); }}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      agentView === 'hermes-forge'
                        ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid="agent-hermes-btn"
                  >
                    👟 Hermes' Forge
                  </button>
                )}
                {/* Apollo's Chamber - requires distribution_machine agent */}
                {(hasHiredAgent('distribution_machine') || canAccessAgentRooms) && (
                  <button
                    onClick={() => { setAgentView('apollo-chamber'); setSelectedAgent(null); }}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      agentView === 'apollo-chamber'
                        ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid="agent-apollo-btn"
                  >
                    ☀️ Apollo's Chamber
                  </button>
                )}
                {/* The Pantheon - requires any hired agent or Pro+ */}
                {(hiredAgents.length > 0 || canAccessAgentRooms) && (
                  <button
                    onClick={handlePantheonClick}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      agentView === 'alliance'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid="agent-pantheon-btn"
                  >
                    ⚡ The Pantheon
                  </button>
                )}
                {/* Dionysus - Pro+ feature only, no hiring needed */}
                {canAccessAgentRooms && (
                  <button
                    onClick={() => { setAgentView('dionysus'); setSelectedAgent(null); }}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      agentView === 'dionysus'
                        ? 'bg-gradient-to-r from-purple-600 to-red-600 text-white'
                        : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                    data-testid="agent-dionysus-btn"
                  >
                    🍷 Dionysus
                  </button>
                )}
                <button
                  onClick={() => { setAgentView('outputs'); setSelectedAgent(null); }}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    agentView === 'outputs'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  📦 Content Packages
                </button>
              </div>
            )}

            {/* Marketplace View */}
            {agentView === 'marketplace' && (
              <AgentMarketplace
                API={API}
                user={user}
                onHireAgent={(agent) => {
                  setSelectedAgent(agent);
                  setAgentView('config');
                }}
                onViewAgent={(agent) => {
                  setSelectedAgent(agent);
                  setAgentView('config');
                }}
                onProFeaturePreview={(featureView) => {
                  // Navigate to the locked feature preview
                  setAgentView(featureView);
                }}
              />
            )}

            {/* My Agents Dashboard */}
            {agentView === 'dashboard' && (
              <AgentDashboard
                API={API}
                onRunAgent={(agent) => {
                  setSelectedAgent(agent);
                  setAgentView('runner');
                }}
                onViewOutputs={(agent) => {
                  setSelectedAgent(agent);
                  setAgentView('outputs');
                }}
                onConfigureAgent={(agent) => {
                  setSelectedAgent(agent);
                  setAgentView('config');
                }}
              />
            )}

            {/* Configuration Wizard */}
            {agentView === 'config' && selectedAgent && (
              <AgentConfigWizard
                agent={selectedAgent}
                API={API}
                onComplete={(newAgent) => {
                  setSelectedAgent(newAgent);
                  setAgentView('dashboard');
                  // Refresh hired agents list when a new agent is configured
                  const fetchHiredAgents = async () => {
                    try {
                      const response = await axios.get(`${API}/agents`);
                      if (response.data?.agents) {
                        const agentTypes = response.data.agents.map(agent => agent.agent_type);
                        setHiredAgents(agentTypes);
                      }
                    } catch (error) {
                      console.error('Error refreshing hired agents:', error);
                    }
                  };
                  fetchHiredAgents();
                }}
                onCancel={() => {
                  setSelectedAgent(null);
                  setAgentView('marketplace');
                }}
              />
            )}

            {/* Agent Runner */}
            {agentView === 'runner' && selectedAgent && (
              <AgentRunner
                agent={selectedAgent}
                API={API}
                onComplete={(result) => {
                  setSelectedOutput(result);
                  setAgentView('output');
                }}
                onCancel={() => {
                  setSelectedAgent(null);
                  setAgentView('dashboard');
                }}
              />
            )}

            {/* Output Viewer */}
            {agentView === 'output' && (
              <AgentOutputViewer
                API={API}
                packageData={selectedOutput?.package}
                reportData={selectedOutput?.report}
                agentType={selectedAgent?.agent_type}
                onClose={() => {
                  setSelectedOutput(null);
                  setAgentView('dashboard');
                }}
              />
            )}

            {/* All Outputs */}
            {agentView === 'outputs' && (
              <AgentOutputViewer
                API={API}
                onClose={() => setAgentView('marketplace')}
              />
            )}

            {/* Athena's War Room - Competitive Intelligence - requires competitor_spy or Pro+ */}
            {agentView === 'athena' && (
              (hasHiredAgent('competitor_spy') || canAccessAgentRooms) ? (
                <AthenaWarRoom
                  API={API}
                  user={user}
                  onRunAgent={(agentType, spyConfig) => {
                    setSelectedAgent({ 
                      agent_type: agentType || 'competitor_spy',
                      spy_config: spyConfig || {
                        industry: 'technology',
                        focus_areas: ['content marketing', 'SEO'],
                        your_strengths: '',
                        analysis_depth: 'standard'
                      }
                    });
                    setAgentView('runner');
                  }}
                  onBack={() => setAgentView('marketplace')}
                />
              ) : (
                <LockedFeaturePreview
                  featureName="Athena's War Room"
                  featureIcon="🦉"
                  featureDescription="Uncover competitor strategies, find content gaps, and get AI-powered intelligence to dominate your market."
                  previewContent={<AthenaPreviewContent />}
                  unlockMethod="hire"
                  agentName="Athena"
                  onHire={() => setAgentView('marketplace')}
                  onGoToMarketplace={() => setAgentView('marketplace')}
                />
              )
            )}

            {/* Hermes' Content Forge - requires content_engine or Pro+ */}
            {agentView === 'hermes-forge' && (
              (hasHiredAgent('content_engine') || canAccessAgentRooms) ? (
                <UnifiedContentForge
                  API={API}
                  user={user}
                  onRunAgent={(agentType) => {
                    setSelectedAgent({ agent_type: agentType || 'content_engine' });
                    setAgentView('runner');
                  }}
                  onBack={() => setAgentView('marketplace')}
                />
              ) : (
                <LockedFeaturePreview
                  featureName="Hermes' Forge"
                  featureIcon="👟"
                  featureDescription="Create viral content at scale with AI. Blog posts, social media, images - all tailored to your brand voice."
                  previewContent={<HermesPreviewContent />}
                  unlockMethod="hire"
                  agentName="Hermes"
                  onHire={() => setAgentView('marketplace')}
                  onGoToMarketplace={() => setAgentView('marketplace')}
                />
              )
            )}

            {/* Apollo's Sun Chamber - requires distribution_machine or Pro+ */}
            {agentView === 'apollo-chamber' && (
              (hasHiredAgent('distribution_machine') || canAccessAgentRooms) ? (
                <ApolloSunChamber
                  API={API}
                  user={user}
                  onRunAgent={(agentType) => {
                    setSelectedAgent({ agent_type: agentType || 'distribution_machine' });
                    setAgentView('runner');
                  }}
                  onBack={() => setAgentView('marketplace')}
                />
              ) : (
                <LockedFeaturePreview
                  featureName="Apollo's Chamber"
                  featureIcon="☀️"
                  featureDescription="Distribute your content across LinkedIn, Twitter, Facebook, Instagram & Medium with one click."
                  previewContent={<ApolloPreviewContent />}
                  unlockMethod="hire"
                  agentName="Apollo"
                  onHire={() => setAgentView('marketplace')}
                  onGoToMarketplace={() => setAgentView('marketplace')}
                />
              )
            )}

            {/* The Pantheon - requires any hired agent or Pro+ */}
            {agentView === 'alliance' && (
              (hiredAgents.length > 0 || canAccessAgentRooms) ? (
                <AllianceWarRoom
                  API={API}
                  user={user}
                  onBack={() => setAgentView('marketplace')}
                />
              ) : (
                <LockedFeaturePreview
                  featureName="The Pantheon"
                  featureIcon="⚡"
                  featureDescription="Command all your AI agents from one divine control center. Launch Divine Strike for automated content workflows."
                  previewContent={<PantheonPreviewContent />}
                  unlockMethod="hire"
                  agentName="any agent"
                  onHire={() => setAgentView('marketplace')}
                  onGoToMarketplace={() => setAgentView('marketplace')}
                />
              )
            )}

            {/* Dionysus - requires Pro+ */}
            {agentView === 'dionysus' && (
              canAccessAgentRooms ? (
                <DionysusPanel />
              ) : (
                <LockedFeaturePreview
                  featureName="Dionysus"
                  featureIcon="🍷"
                  featureDescription="The Reddit Growth Engine. Find high-intent threads, craft authentic replies, and grow your brand organically."
                  previewContent={<DionysusPreviewContent />}
                  unlockMethod="upgrade"
                  onUpgrade={() => handleUpgradeClick('Dionysus')}
                  onGoToMarketplace={() => setAgentView('marketplace')}
                />
              )
            )}

            {/* Pipeline Builder */}
            {agentView === 'pipelines' && (
              <PipelineBuilder
                API={API}
                onBack={() => setAgentView('marketplace')}
              />
            )}

            {/* Smart Discovery Modal */}
            {showSmartDiscovery && (
              <SmartDiscovery
                API={API}
                onComplete={(results) => {
                  setShowSmartDiscovery(false);
                  // Store results and switch to Athena view
                  setAgentView('athena');
                }}
                onCancel={() => setShowSmartDiscovery(false)}
              />
            )}
          </div>
        )}
        {activeTab === 'dashboard' && <PageSEO page="dashboard" />}
        {activeTab === 'agents' && <PageSEO page="agents" />}
        {activeTab === 'generate' && <><PageSEO page="generate" /><UnifiedContentForge API={API} user={user} onRunAgent={(agentType) => { setSelectedAgent({ agent_type: agentType || 'content_engine' }); setAgentView('runner'); }} /></>}
        {activeTab === 'artemis' && <><PageSEO page="artemis" /><ArtemisWrapper /></>}
        {activeTab === 'multiply' && <><PageSEO page="multiply" /><ContentMultiplier /></>}
        {activeTab === 'seo' && <><PageSEO page="seo" /><SEOOptimizer /></>}
        {activeTab === 'predictor' && <><PageSEO page="performance" /><PerformancePredictor /></>}
        {activeTab === 'trending' && <><PageSEO page="trending" /><TrendingTopics /></>}
        {activeTab === 'viral' && <><PageSEO page="viral" /><ViralAnalyzer /></>}
        {activeTab === 'website-analyzer' && <WebsiteAnalyzer />}
        {activeTab === 'analytics' && <AdvancedAnalyticsDashboard />}
        {activeTab === 'ads-dashboard' && <AdPerformanceDashboard />}
        {activeTab === 'content-brief' && <ContentBriefGenerator />}
        {activeTab === 'competitor-analyzer' && <CompetitorAnalyzer onNavigate={setActiveTab} />}
        {activeTab === 'content-calendar' && <ContentCalendarGenerator />}
        {activeTab === 'roi-calculator' && <ContentROICalculator />}
        {activeTab === 'content-decay' && <ContentDecayAlerter />}
        {activeTab === 'sentiment-predictor' && <AudienceSentimentPredictor />}
        {activeTab === 'content-explosion' && <MultiFormatExplosion />}
        {activeTab === 'quora' && <QuoraGoldmine />}
        {activeTab === 'autopilot' && <AutoPilotEngine />}
        {activeTab === 'referrals' && <><PageSEO page="referrals" /><ReferralProgram /></>}
        {activeTab === 'bulk' && <BulkGenerator />}
        {activeTab === 'api-keys' && <APIKeysManagement />}
        {activeTab === 'zapier' && <ZapierIntegration />}
        {activeTab === 'demo-analytics' && <DemoAnalyticsDashboard />}
        {activeTab === 'digest-settings' && <DigestSettings onNavigate={setActiveTab} />}
      </main>
      
      {/* Upgrade Email Modal */}
      <UpgradeEmailModal
        isOpen={showEmailCapture}
        onClose={() => setShowEmailCapture(false)}
        onSubmit={handleEmailCaptureSubmit}
        featureName={emailCaptureFeature}
      />
      </div>
      </div>
    </div>
  );
};

// Artemis Wrapper - passes user tier to ArtemisHunter
const ArtemisWrapper = () => {
  const { user } = React.useContext(AuthContext);
  const userTier = user?.subscription_tier || 'free';
  return <ArtemisHunter userTier={userTier} />;
};

function App() {
  return (
    <div id="main-content">
    <AuthProvider>
    <WorkflowProvider>
    <IntelligenceProvider>
      <BrowserRouter>
        <RouteHead />
        <HreflangTags />
        <Routes>
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin/blog" element={<BlogAdmin />} />
          <Route path="/compare" element={<ComparisonIndex />} />
          <Route path="/compare/:slug" element={<ComparisonPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
          <Route path="/topics" element={<TopicHub />} />
          <Route path="/free-downloads" element={<FreeDownloadsPage />} />
          <Route path="/hermes-forge" element={<HermesContentForge />} />
          <Route path="/alliance-war-room" element={<AllianceWarRoom />} />
          <Route path="/dionysus" element={<DionysusPanel />} />
          <Route path="/artemis" element={<ArtemisWrapper />} />
          <Route path="/roast" element={<RoastPage />} />
          <Route path="/roast/:roastId" element={<RoastSharePage />} />
          <Route path="/warroom/:shareId" element={<WarRoomSharePage />} />
          <Route path="/living-room/:shareId" element={<LivingRoomReplayPage />} />
          <Route path="/auto-debate/:autoDebateId" element={<AutoDebateReplayPage />} />
          <Route path="/free-debate" element={<FreeTasteWarRoom />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/battle/:battleId" element={<BattlePage />} />
          <Route path="/strategy-score" element={<StrategyScorePage />} />
          <Route path="/score/:scoreId" element={<ScoreReportPage />} />
          <Route path="/replays" element={<WarRoomReplaysPage />} />
          <Route path="/tools/living-war-room" element={<FeaturePageWarRoom />} />
          <Route path="/tools/strategy-score" element={<FeaturePageStrategyScore />} />
          <Route path="/resources" element={<ResourcesPage />} />
          {/* Digest routes — handled by DigestRouter for reliable @username path matching */}
          <Route path="/digest/*" element={<DigestRouter />} />
          <Route path="*" element={<MainApp />} />
        </Routes>
      </BrowserRouter>
    </IntelligenceProvider>
    </WorkflowProvider>
    </AuthProvider>
    </div>
  );
}

export default App;